import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    ArrowLeft,
    Loader2,
    AlertTriangle,
    CheckCircle2,
    Lock,
    Ban,
    ExternalLink,
    ShieldCheck,
    FileCode,
    PencilLine,
    RefreshCw,
    Download,
    FolderOpen,
    FileText,
    MinusCircle,
    Undo2,
} from 'lucide-react';
import {
    Customer,
    DOCUMENT_TYPE_LABEL,
    Invoice,
    InvoiceConfig,
    InvoiceCostCenter,
    InvoiceLine,
    InvoiceSection,
    QuoteConfig,
    UserProfile,
} from '../../types';
import { Button } from '../Button';
import {
    approveInvoice,
    buildProjectServiceLines,
    cancelInvoice,
    correctInvoice,
    createCommercialCredit,
    deleteInvoice,
    fetchCompetingDrafts,
    fetchInvoice,
    getInvoicePdfUrl,
    issueInvoice,
    rejectInvoice,
    listPerformanceProofServices,
    renderPerformanceProofForInvoice,
    submitForReview,
    uploadCustomPerformanceProof,
    updateInvoice,
    verifySnapshotHash,
    InvoiceValidationError,
    IssueContext,
    PerformanceProofServiceRef,
} from '../../services/invoiceService';
import { torSperrAnzeige, type TorSperrAnzeige } from '../../services/allocationGate';
import {
    CONTRIBUTES_TO_NET,
    canExcludeLine,
    fallbackInvoiceTitle,
    formatProjectRef,
    formatProjectRefNameOnly,
    FLAT_SUMMARY_TITLE,
    hasBlockingErrors,
    invoiceProjectRefs,
    isEditable,
    applyInvoiceSections,
    keepManualLineEdits,
    lineDiscountCents,
    nextInvoicePosNumbers,
    printedServicePeriod,
    hasCustomProofRange,
    proofExcludedServiceIds,
    proofFilterRange,
    performancePeriod,
    performedDetails,
    manualCumulativeStart,
    withManualStart,
    quoteHeadlineText,
    effectiveRetentionPercent,
    invoiceAmountCents,
    retentionBasisNote,
    retentionNote,
    servicePeriodDriftNote,
    servicePeriodFromSelection,
    servicePeriodLimitOffer,
    servicePeriodIssue,
    skontoPaymentTermsText,
    validateInvoice,
    wantsStickyCustomerOverride,
    FlatCheckContext,
    ValidationMessage,
} from '../../services/invoiceLogic';
import { fetchQuotesForCustomer } from '../../services/crmService';
import { pickMainQuote } from '../../services/quoteSupplementLogic';
import {
    documentCustomerName,
    documentCustomerState,
    resolveDocumentCustomer,
} from '../../services/customerQuickEdit';
import { NEGATIVE_DOCUMENT_TYPES } from '../../services/creditNoteLogic';
import { useRechte } from '../../hooks/useRechte';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import { rechnungAktionen } from './rechnungRechte';
import { gespeicherteWahlOhneDatum, neuaufbauSchluessel } from './rebuildLogic';
import { CustomerQuickEditModal } from '../crm/CustomerQuickEditModal';
import {
    applyCustomerOverride,
    applyCustomerOverrideOptional,
    customerOverrideApplies,
    customerOverrideSummary,
    hasCustomerOverride,
} from '../../services/customerOverride';
import { billedPeriodForDisplay, billingPeriodOf } from '../../services/allocationLogic';
import { loadProjectById } from '../../services/cloudService';
import type { ListProject } from '../../services/listProject';
import { altBookedServiceIds as altBookedServiceIdsOf } from '../../services/externalAdvances';
import { getNormalizedServices } from '../../services/projectLv';
import {
    centsFromEuroInput,
    formatCents,
    formatCentsPlain,
    formatTaxRateBp,
    parseDecimalInput,
} from '../../services/money';
import {
    advanceDeductionHeadingLabel,
    advanceDeductionLineLabel,
    advanceDeductionRangeLabel,
    advanceDeductionSpanLabel,
    advanceNumberingPlan,
    payableBreakdown,
    priorRetentionTotal,
    wantsRetentionRelease,
} from '../../services/advanceInvoiceLogic';
import { RetentionBackfillModal } from '../project-hub/RetentionBackfillModal';
import { formatDate } from '../../services/pdf/pdfUtils';
import { buildInvoiceCompanyForPdf } from '../../services/pdf/companyForPdf';
import { generateInvoicePDF, renderInvoicePdfBytes } from '../../services/pdf/generatorInvoice';
import { appendPdf } from '../../services/pdf/mergePdf';
import { pdfBytesToDataUri } from '../../services/pdf/tenderAttach';
import {
    checkInvoiceProjects,
    mergeCheckResults,
    setProjectReportExemption,
    setProjectRetentionPercent as saveProjectRetentionPercent,
} from '../../services/projectCheckService';
import { ProjectCheckResult } from '../../types';
import { fetchInvoiceCostCenters } from '../../services/invoiceCostCenters';
import {
    addHeaderTemplate,
    fetchHeaderTemplates,
    headerTemplateOptions,
    normalizeHeaderTemplates,
    removeHeaderTemplate,
    saveHeaderTemplates,
} from '../../services/invoiceHeaderTemplates';
import { describeError } from '../../services/errorMessage';
import { CostCenterPicker } from './CostCenterPicker';
import { HeaderTextPicker } from './HeaderTextPicker';
import { InvoiceStatusBadge } from './InvoiceStatusBadge';
import { BillingModeChip } from './BillingModeChip';
import { getServiceLog } from '../../services/serviceJournal';
import { PaymentsPanel } from './PaymentsPanel';
import { ProjectCheckPanel } from './ProjectCheckPanel';
import { fetchArchivedPdfBytes } from '../../services/gobdArchive';
import { buildUblInvoice } from '../../services/einvoice/ublInvoice';
import { validateEn16931 } from '../../services/einvoice/en16931Rules';
import { validateCancellation } from '../../services/creditNoteLogic';
import { CorrectionDialog } from './CorrectionDialog';
import { istNativeApp } from '../../services/nativePlatform';
import { ausgabeFehlerMelden, dateiAusgeben } from '../../services/dateiAusgabe';
import { blobFuerUrl } from '../../services/nativeShims';
import { NativePdfSeiten } from '../NativePdfSeiten';

/**
 * Rechnungseditor.
 *
 * Bewusst zweigeteilt: solange der Beleg aenderbar ist, steht die Bearbeitung
 * im Vordergrund; ab der Ausstellung ist alles gesperrt und die Ansicht dient
 * nur noch der Einsicht, dem Versand und dem Storno. Die Sperre kommt nicht aus
 * dieser Komponente, sondern aus dem Datenbank-Trigger — die UI bildet sie nur
 * ab, statt sie zu simulieren.
 */

/** yyyy-mm-dd als deutsches Datum; leer bleibt ein Gedankenstrich. */
const de = (iso?: string): string => (iso ? iso.split('-').reverse().join('.') : '—');

/**
 * Was die OBEREN Datumsfelder tun — und was sie nicht tun.
 *
 * Sie steuern die Auswahl der Positionen; der Kasten darunter steuert nur den
 * Andruck. Zwei Datumspaare untereinander sahen fuer den Bearbeiter gleich aus,
 * und wer bloss den ausgewiesenen Zeitraum aendern wollte, verstellte damit,
 * was der Beleg ueberhaupt abrechnet.
 */
const SELECTION_FIELD_HINT = 'Bestimmt, welche Leistungen abgerechnet werden.';

/**
 * Fingerabdruck der aus dem Leistungsverzeichnis stammenden Zeilen.
 *
 * Herkunft, Menge und Einzelpreis — mehr braucht es nicht, um zu erkennen, ob
 * jemand die Positionen von Hand angefasst hat. Titel und Reihenfolge bleiben
 * bewusst draussen: ein umbenannter Titel ist keine Aenderung, die ein
 * Neuaufbau verlieren koennte, denn er kommt ohnehin aus dem Projekt.
 */
const lvFingerprint = (lines: InvoiceLine[]): string =>
    lines
        .filter((l) => l.kind === 'SERVICE' && l.projectId)
        .map((l) => `${l.serviceId}:${l.quantity}:${l.unitPriceCents}`)
        .sort()
        .join('|');

/**
 * Ist das ein zu Ende eingegebenes Datum?
 *
 * Ein Datumsfeld meldet jede Zwischenstufe: "0002-05-01" ist keine Eingabe,
 * sondern der Weg dorthin. Jeder Effekt, der auf ein Datum hin ueber das Netz
 * laeuft, laeuft ohne diese Bremse je Tastendruck einmal — und zwar fuer
 * Zeitraeume, die niemand gemeint hat.
 *
 * Leer ist erlaubt und bedeutet "nicht gesetzt"; abgewiesen wird nur das
 * ANGEFANGENE Datum. Eine Stelle fuer alle Aufrufer: zwei Fassungen derselben
 * Regel liefen frueher oder spaeter auseinander, und dann braemste die eine,
 * waehrend die andere durchliesse.
 */
const datumVollstaendig = (d?: string): boolean =>
    !d || (/^\d{4}-\d{2}-\d{2}$/.test(d) && d >= '2000-01-01');

/** Wie lange nach der letzten Datumsaenderung gewartet wird, bevor ein Lauf startet. */
const DATE_DEBOUNCE_MS = 1000;

/**
 * Warnungen, die die Maske trotz der Ausblendung weiterhin zeigt.
 *
 * Angezeigt wird sonst nur, was das Ausstellen verhindert. Diese Warnungen sind
 * die Ausnahme, weil ihre Folge Geld kostet:
 *
 *  - prior_billing_coverage_unknown: bei Entwuerfen aus der Zeit vor dieser
 *    Pruefung laesst sich nicht feststellen, ob die bereits abgerechnete Menge
 *    durch Vorrechnungen gedeckt ist. Wird das uebersehen, fordert der Beleg
 *    dieselbe Leistung ein zweites Mal — genau dieser Fall ist bei Projekt 6172
 *    eingetreten und hat den Kunden doppelt belastet.
 *  - document_discount_without_base: der zugesagte Nachlass steht am Beleg, hat
 *    aber keine Grundlage gefunden und wirkt deshalb nicht. Das PDF fordert den
 *    vollen Betrag, obwohl der Kunde weniger vereinbart hat.
 *  - line_zero_price: eine Position mit Einzelpreis 0,00 € wird beim Ausstellen
 *    trotzdem als abgerechnet fortgeschrieben (planWriteBack bucht mengen-, nicht
 *    preisbezogen). Die Menge gilt danach dauerhaft als zu null Euro abgerechnet
 *    und ist nur ueber Storno zurueckzuholen; der Projektcheck faengt das nicht
 *    ab, weil zero_unit_price den Preis der LV-Position prueft, nicht den der
 *    Belegzeile.
 *  - final_invoice_leaves_open: eine Schluss- oder Restrechnung, deren
 *    Stichtag vor der letzten erbrachten Leistung liegt. Der Beleg fordert zu
 *    wenig, und die liegengebliebene Menge braucht nach dem Ausstellen einen
 *    eigenen Beleg (Vorfall 5888, 20.10.2026). Steht zusaetzlich in der
 *    Rueckfrage vor dem Ausstellen.
 *
 * Diese Faelle sind selten, deshalb kosten die Ausnahmen im Alltag nichts.
 *
 * Die Liste bleibt kurz und benannt: jede weitere Aufnahme braucht denselben
 * Grund, naemlich eine falsche Geldforderung als Folge.
 */
const ALWAYS_SHOWN_WARNINGS = new Set([
    'prior_billing_coverage_unknown',
    'document_discount_without_base',
    'line_zero_price',
    'final_invoice_leaves_open',
]);

interface Props {
    invoice: Invoice;
    customer?: Customer;
    /**
     * Die bereits geladenen Projektzeilen der Liste.
     *
     * Nur als Rueckfallquelle fuer Nummer und Name: massgeblich ist der
     * Projektvermerk am Beleg, denn nur der steht auch im PDF. Belege aus der
     * Zeit davor kaemen sonst ohne Projektangabe in die Maske.
     */
    projects?: Array<{ id: string; project_number?: string; name?: string }>;
    invoiceConfig: InvoiceConfig;
    /**
     * Der Kundenstamm wurde aus dem Beleg heraus geaendert.
     *
     * Bewusst ein eigener Kanal neben onChange: der Beleg selbst aendert sich
     * dabei NICHT. Liefe die Meldung ueber onChange, setzte die Liste `editing`
     * neu, die invoice-Prop bekaeme eine neue Identitaet — und der Effekt, der
     * den Entwurf daran nachzieht, verwuerfe alles, was noch im Debounce haengt.
     */
    onCustomerChange?: (customer: Customer) => void;
    // senderLine gehoert dazu: App.tsx liefert sie und der Briefkopf setzt sie
    // (buildInvoiceCompanyForPdf → CompanyBase). Ohne sie im Typ floss sie nur
    // zur Laufzeit durch — jeder Zugriff hier waere ein Typfehler gewesen.
    company: { name?: string; address?: string; logo?: string; footerText?: string; senderLine?: string };
    quoteConfig?: unknown;
    userProfile: UserProfile | null;
    today: string;
    onChange: (invoice: Invoice) => void;
    /**
     * Zurueck zur Liste. Eine mitgegebene Erfolgsmeldung zeigt die Liste an —
     * hier im Editor gesetzt wuerde sie nie rendern, weil onBack sofort
     * unmountet, und der Nutzer haelt den Vorgang fuer gescheitert.
     */
    onBack: (notice?: string) => void;
    /**
     * Sprung ins Projekt. Der Editor reicht das frisch geladene Projekt
     * (loadProjectById) — typisiert als Listenform, weil der Empfaenger in der
     * App auch Listeneintraege bekommt und daraus nie speichern darf.
     */
    onOpenProject?: (project: ListProject) => void;
}

export const InvoiceEditor: React.FC<Props> = ({
    invoice,
    customer,
    projects,
    invoiceConfig,
    onCustomerChange,
    company,
    quoteConfig,
    userProfile,
    today,
    onChange,
    onBack,
    onOpenProject,
}) => {
    const [draft, setDraft] = useState<Invoice>(invoice);
    const [busy, setBusy] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    /**
     * Die Sperre am Rechnungstor (S5a Stufe 3b) in ihrer Form — Satz,
     * Positionen, Rat. Sie steht IM Ausstellen-Dialog: der Fehlerbalken der
     * Maske liegt hinter dessen Abdunklung und waere dort nicht zu lesen.
     */
    const [torSperre, setTorSperre] = useState<TorSperrAnzeige | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [previewUri, setPreviewUri] = useState<string | null>(null);
    /**
     * Das archivierte PDF eines ausgestellten Belegs, als Objekt-URL zum
     * Einbetten.
     *
     * Bewusst ein eigener Zustand neben previewUri und nicht derselbe: der
     * Live-Render zeigt den HEUTIGEN Zustand (globale Ueberschrift,
     * Briefkopf-Einstellungen, Firmendaten), das Archiv das rechtlich
     * massgebliche Dokument. Teilten sich beide einen Zustand, stuende waehrend
     * des Ladens noch die alte Vorschau unter der Beschriftung
     * "Archivdokument" — genau die Abweichung, die hier ausgeschlossen wird.
     */
    const [archiveUri, setArchiveUri] = useState<string | null>(null);
    // Ein Fehlschlag beim Laden des Archivs faellt NICHT auf den Live-Render
    // zurueck, sondern wird angezeigt: der Rueckfall zeigte ein anderes
    // Dokument als das archivierte und behauptete damit etwas Falsches.
    const [archiveError, setArchiveError] = useState<string | null>(null);
    const [cancelReason, setCancelReason] = useState('');
    const [cancelOpen, setCancelOpen] = useState(false);
    const [correctionOpen, setCorrectionOpen] = useState(false);
    const [check, setCheck] = useState<ProjectCheckResult | null>(null);
    const [checkBusy, setCheckBusy] = useState(false);
    const [checkError, setCheckError] = useState<string | null>(null);
    const [acknowledgement, setAcknowledgement] = useState('');
    // Ausstellen ist unumkehrbar (endgueltige Nummer, Rueckschreibung ins
    // Projekt, Archiv) und braucht deshalb dieselbe Rueckfrage wie Storno.
    const [issueOpen, setIssueOpen] = useState(false);
    /**
     * Offene Belege, die dieselben LV-Positionen beanspruchen wie der eben
     * ausgestellte. Sie sind ab der Ausstellung wertlos (der Projektcheck
     * sperrt sie mit "Menge doppelt beansprucht"), blieben aber kommentarlos
     * in der Liste liegen — hier wird das Aufraeumen angeboten, nie automatisch
     * geloescht.
     */
    const [competingDrafts, setCompetingDrafts] = useState<Invoice[]>([]);
    // Beim Neuaufbau auch Mengen ohne Protokolleintrag uebernehmen. Vorbelegt
    // mit der Wahl, mit der der Beleg zuletzt gebaut wurde
    // (details.includeUndated) — sonst baute der naechste Aufbau einen mit
    // den Mengen ohne Datum angelegten Entwurf still ohne sie neu. Ohne
    // gespeicherte Wahl bewusst abgewaehlt: sie einem Zeitraum zuzuschlagen
    // ist eine Aussage ueber die Leistung, die nur der Bearbeiter treffen kann.
    // Nur beim Oeffnen und beim Belegwechsel vorbelegt (Effekt auf invoice.id
    // unten), nicht nach jedem Speichern — eine Umstellung des Hakens im
    // selben Beleg bleibt stehen.
    const [rebuildUndated, setRebuildUndated] = useState(() => gespeicherteWahlOhneDatum(invoice.details));
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    /**
     * Kundenstamm-Schnellbearbeitung offen.
     *
     * Rein lokaler Zustand: der Dialog haengt neben dem Beleg, nicht in ihm.
     * Oeffnen und Schliessen fassen `draft` nicht an — ungespeicherte Eingaben
     * am Beleg ueberleben den Vorgang.
     */
    const [customerEditOpen, setCustomerEditOpen] = useState(false);
    /**
     * Gespeicherte Kostenstellen als Tipphilfe fuer den Belegkopf.
     *
     * Rein optional: fehlt die Tabelle (Migration Nr. 26 noch nicht gefahren),
     * liefert der Service eine leere Liste und das Aufklappmenue bleibt leer —
     * die Kostenstelle selbst laesst sich immer von Hand eintragen, sie liegt
     * im jsonb des Belegs.
     */
    const [costCenters, setCostCenters] = useState<InvoiceCostCenter[]>([]);
    /**
     * Das Laden der Vorlagen ist fehlgeschlagen — und zwar NICHT, weil die
     * Migration fehlt (der Fall bleibt still). Der Picker sagt es dezent an,
     * damit ein Rechtefehler nicht als "es gibt eben keine Vorlagen" durchgeht.
     */
    const [costCentersUnavailable, setCostCentersUnavailable] = useState(false);
    /**
     * Eigene Vorlagen fuer die Belegueberschrift.
     *
     * Aus dem Profil des angemeldeten Benutzers, das die Liste als schlichte
     * Wortliste in settings fuehrt. Erster Stand aus der Prop (sofort da, kein
     * Flackern), gleich danach der frische aus der Datenbank — die Prop ist ein
     * Abzug vom Anmeldezeitpunkt, und eine eben gespeicherte Vorlage darf beim
     * naechsten Oeffnen nicht wieder fehlen.
     */
    const [headerTemplates, setHeaderTemplates] = useState<string[]>(() =>
        normalizeHeaderTemplates(userProfile?.settings?.invoiceHeaderTemplates),
    );
    const [headerTemplateBusy, setHeaderTemplateBusy] = useState(false);
    /**
     * Vorhergesagter Abrechnungszeitraum eines kumulativen Entwurfs.
     *
     * Der Entwurf traegt noch keine Zuordnung — die entsteht erst beim
     * Ausstellen. Der Aufbau kennt sie trotzdem: er hat Protokoll und Vorbelege
     * geladen und laesst dieselben puren Bausteine darueber laufen. Deshalb
     * kommt der Wert von dort und wird nicht hier nachgerechnet.
     *
     * Bewusst nur im Arbeitsspeicher und nicht am Beleg: eine gespeicherte
     * Vorhersage waere eine zweite Quelle neben der eingefrorenen Zuordnung und
     * koennte ihr widersprechen. Ohne Aufbau bleibt die Angabe leer, statt einen
     * Zeitraum zu behaupten.
     */
    const [predictedBillingPeriod, setPredictedBillingPeriod] = useState<
        { from: string; to: string } | undefined
    >(undefined);
    const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Nach dem Unmount darf kein setState mehr laufen — sonst meldet ein
    // fehlgeschlagenes Speichern auf einer nicht mehr eingehaengten Komponente.
    const mounted = useRef(true);
    // Ausstehende Aenderungen werden verschmolzen, nicht ersetzt: sonst geht bei
    // zwei Eingaben innerhalb der Debounce-Zeit die erste verloren und springt
    // beim naechsten Serverstand sichtbar zurueck.
    const pendingUpdates = useRef<Partial<Invoice> | null>(null);
    // Speicherungen laufen nacheinander. Parallel gestartete Requests koennen in
    // umgekehrter Reihenfolge antworten und die neuere Eingabe mit dem aelteren
    // Stand ueberschreiben.
    const saveChain = useRef<Promise<void>>(Promise.resolve());

    useEffect(() => setDraft(invoice), [invoice]);

    // Einmal beim Oeffnen. fetchInvoiceCostCenters wirft nicht — eine fehlende
    // Tabelle darf den Editor nicht aufreissen.
    useEffect(() => {
        let alive = true;
        void fetchInvoiceCostCenters().then((res) => {
            if (!alive) return;
            setCostCenters(res.templates);
            setCostCentersUnavailable(res.unavailable);
        });
        return () => {
            alive = false;
        };
    }, []);

    // Ebenso die Vorlagen der Belegueberschrift. Wirft ebenfalls nicht.
    //
    // `null` heisst "nicht ermittelbar" und wird NICHT uebernommen: die Liste
    // ist zugleich die Grundlage des naechsten Schreibvorgangs, und eine aus
    // einem Ladefehler entstandene leere Liste loeschte beim naechsten
    // "Als Vorlage speichern" alle gespeicherten Vorlagen.
    useEffect(() => {
        const userId = userProfile?.id;
        if (!userId) return;
        let alive = true;
        void fetchHeaderTemplates(userId).then((list) => {
            if (alive && list) setHeaderTemplates(list);
        });
        return () => {
            alive = false;
        };
    }, [userProfile?.id]);

    // Rollensystem: Knoepfe nach Rechten (rechnungRechte). Admins haben jedes
    // Recht — fuer sie bleibt alles wie bisher.
    const rechte = useRechte(userProfile?.role);
    const darf = useMemo(() => rechnungAktionen(rechte), [rechte]);
    /**
     * Offen dem STATUS nach (Entwurf bis Freigabe). `editable` heisst
     * zusaetzlich: diese Person darf ihn aendern. Ohne Recht „Rechnungen
     * bearbeiten" bleibt auch ein Entwurf schreibgeschuetzt — Felder, Autosave
     * und die Vorbelegungs-Effekte liefen sonst in die Ablehnung der
     * Datenbank. Was allein am Status haengt (welcher Kundenstand gedruckt
     * wird, Zahlungsbereich, Vorhersage des Zeitraums), fragt `statusOffen`.
     */
    const statusOffen = isEditable(draft.status);
    const editable = statusOffen && darf.bearbeiten.erlaubt;

    /**
     * Wer die Kundendaten aus dem Beleg heraus aendern darf.
     *
     * Den Kundenstamm nur mit dem Recht „Kunden verwalten" — dieselbe Grenze
     * wie die RLS auf `customers` (Nr. 152). Ohne das Recht bleibt im offenen
     * Entwurf die belegbezogene Abweichung (details.customerOverride, ueber
     * patch): der Dialog oeffnet dann nur mit dieser Reichweite
     * (`stammAenderbar`).
     */
    const canEditCustomer = Boolean(draft.customerId) && (darf.kundenstamm.erlaubt || editable);

    // Vorschau und Ausstellung teilen sich diesen Kopf — sonst zeigte die
    // Vorschau ein anderes Dokument als das, was archiviert wird.
    const companyForPdf = useMemo(
        () => buildInvoiceCompanyForPdf(company, quoteConfig as QuoteConfig | undefined, invoiceConfig),
        [company, quoteConfig, invoiceConfig],
    );

    /**
     * Dateiname der Vorschau — klassisch die Belegnummer.
     *
     * Ein Entwurf hat noch keine; die entsteht erst beim Ausstellen. Ohne
     * Ersatz hiesse jede Entwurfsdatei gleich, deshalb das Belegdatum.
     */
    const previewFileName = draft.invoiceNumber || `Entwurf-${draft.invoiceDate}`;

    /**
     * Liegt zu diesem Beleg ein archiviertes PDF?
     *
     * Der Pfad wird beim Ausstellen gesetzt und danach nie wieder geaendert —
     * er ist damit die verlaessliche Grenze zwischen "es gibt nur einen
     * Entwurf, den man rendern muss" und "es gibt das eine Dokument, das
     * gilt". Bewusst nicht am Status festgemacht: der Status kann sich noch
     * bewegen (versendet, bezahlt, storniert), das Archivdokument nicht.
     */
    const hasArchivedPdf = Boolean(draft.pdfUrl);

    /**
     * Der Beleg hat eine eingefrorene Anschrift.
     *
     * ALLEIN der customerSnapshot, ausdruecklich NICHT zusaetzlich das
     * Archivdokument: der Zustand "PDF im Archiv, aber kein Snapshot" kann es
     * nicht geben. `pdf_url` und `customer_snapshot` sind Spalten desselben
     * create table (supabase/invoices.sql:248/252) — es gab nie einen
     * Schemastand, in dem die eine ohne die andere existierte —, und gesetzt
     * werden beide in EINEM update beim Ausstellen (invoiceService.ts:2383 und
     * :2391); danach sperrt der Festschreibungs-Trigger beide
     * (supabase/invoices_v2.sql:79/95). Eine zweite Quelle taeuschte hier also
     * nur Sicherheit vor — und zwar eine, der die E-Rechnung (downloadXml)
     * nicht folgt: die liest den Snapshot.
     */
    const customerFrozen = Boolean(draft.customerSnapshot);

    /** offen / festgeschrieben / abgeschlossen-ohne-Festschreibung. */
    const customerEditState = documentCustomerState(statusOffen, customerFrozen);

    /**
     * Abweichende Kundenangaben NUR fuer diesen Beleg.
     *
     * Der Bearbeiten-Dialog schreibt wahlweise in den Kundenstamm oder hierher
     * (details.customerOverride). Ab dem Ausstellen ist die Abweichung in den
     * customerSnapshot eingerechnet und spielt keine eigene Rolle mehr — und
     * genau deshalb faellt der Hinweis am Beleg dort weg.
     */
    const customerOverride = draft.details?.customerOverride;

    /**
     * Dieselbe Abweichung, aber nur solange sie fuer diesen Beleg ueberhaupt
     * noch gilt (customerOverride: customerOverrideApplies).
     *
     * Frueher stand hier `!customerFrozen`. Ein abgeschlossener Beleg OHNE
     * Snapshot (Altbestand aus der Zeit vor customer_snapshot) ueberlagerte
     * damit weiter, waehrend der Bearbeiten-Dialog die Abweichung ausblendete
     * und zusagte, die Stammdatenaenderung schlage hier durch. Beides zugleich
     * ging nicht — und speichern liesse sich eine Ruecknahme dort ohnehin nicht
     * mehr (patch schreibt nur im Entwurf).
     */
    const effectiveOverride = customerOverrideApplies(customerEditState)
        ? customerOverride
        : undefined;
    const overrideActive = hasCustomerOverride(effectiveOverride);

    /** Der lebende Kunde, ueberlagert mit der Abweichung dieses Belegs. */
    const customerWithOverride = applyCustomerOverrideOptional(customer, effectiveOverride);

    /**
     * Der Kundenstand, den dieser Beleg ausgibt: Kopfzeile, Pruefung und
     * E-Rechnung lesen ihn hier, damit die Zusage des Bearbeiten-Dialogs
     * ("bleibt unveraendert") nicht an einer Ausgabe vorbeigeht. Die Vorschau
     * weiter unten bildet denselben Wert — sie steht in einem Effekt mit
     * eigener Abhaengigkeitsliste und bleibt deshalb bei ihrem Ausdruck.
     */
    const customerForDocument = resolveDocumentCustomer(
        customerWithOverride,
        draft.customerSnapshot,
        customerFrozen,
    );

    /**
     * Uebernimmt der naechste Entwurf desselben Projekts die Abweichung?
     *
     * Dieselbe Regel wie bei Kostenstelle und Projektname
     * (invoiceLogic: wantsStickyCustomerOverride) — ohne Belegangabe gefragt,
     * damit allein die BELEGART entscheidet. Der Dialog sagt es zu, und eine
     * Zusage, die fuer Storno oder Gutschrift gar nicht gilt, waere falsch.
     */
    const overrideProjectSticky =
        Boolean(draft.primaryProjectId) && wantsStickyCustomerOverride(draft.documentType, undefined);

    /**
     * Was der Vorschau-Bereich zeigt: beim ausgestellten Beleg ausschliesslich
     * das Archivdokument, sonst den Live-Render. Nie ersatzweise das jeweils
     * andere — beim Archivdokument waere das genau die Abweichung, die es zu
     * vermeiden gilt.
     */
    const documentUri = hasArchivedPdf ? archiveUri : previewUri;
    /** NUR App: Teilen-Dialog der Vorschau laeuft — Knopf gesperrt (Muster PdfPreviewModal). */
    const [teilenLaeuft, setTeilenLaeuft] = useState(false);

    const pdfUserName = useMemo(() => {
        const p = userProfile as unknown as { first_name?: string; last_name?: string } | null;
        return [p?.first_name, p?.last_name].filter(Boolean).join(' ') || undefined;
    }, [userProfile]);

    /**
     * Projektstand fuer die Riegel, die den Beleg gegen das PROJEKT halten:
     * die Monatspauschale (invoiceLogic: flatRateIssues), die erbrachte,
     * aber nicht im Leistungsstand stehende Menge (missingUsedIssues) und die
     * Paketposition mit offenen Bestandteilen (componentLineIssues,
     * `component_line_incomplete`). Fuer die letzte braucht es keinen
     * eigenen Ladevorgang: loadProjectById liest `project_services` mit
     * `select *`, die Positionen tragen `bestandteile` und das von der
     * Datenbank gepflegte `bestandteileOffen` schon (bestandteilFelderAusZeile).
     * Der Befund ist ein Fehler und steht damit von selbst in der Leiste.
     *
     * Frueher wurde der Stand nur gesetzt, wenn das Projekt eine
     * Pauschalposition trug. Das Projekt wurde dafuer ohnehin geladen — die
     * Bedingung sparte also nichts und kostete den zweiten Riegel: 6106 hat
     * keine Pauschale, und genau dort fehlte die Begehung. Die Pauschalregeln
     * filtern selbst auf Projekte mit Pauschalposition.
     *
     * Bindend prueft der Ausstellpfad (invoiceService: planProjectIssue) aus
     * GENAU dem Schnappschuss, aus dem auch zugeordnet und gebucht wird; die
     * Maske zeigt den Befund hier nur frueher an.
     */
    const [flatContext, setFlatContext] = useState<FlatCheckContext | undefined>(undefined);
    /**
     * Der im PROJEKT vereinbarte Sicherheitseinbehalt — Vorbelegung fuer neue
     * Entwuerfe, siehe den Effekt weiter unten. Er reist auf demselben Ladevorgang
     * mit wie der Pauschalkontext: ein zweiter Zugriff auf dasselbe Projekt waere
     * eine Abfrage fuer eine einzige Zahl.
     */
    const [projectRetentionPercent, setProjectRetentionPercent] = useState<number | undefined>(
        undefined,
    );
    useEffect(() => {
        const projectId = draft.primaryProjectId;
        if (!projectId) {
            setFlatContext(undefined);
            setProjectRetentionPercent(undefined);
            return;
        }
        let cancelled = false;
        loadProjectById(projectId)
            .then((project) => {
                if (cancelled || !project) return;
                const gd = (project as any)?.data?.metadata?.globalData;
                // Vor der Leistungspruefung: der Einbehalt haengt nicht an den
                // Positionen und ginge sonst bei jedem Projekt ohne
                // Leistungsverzeichnis verloren.
                setProjectRetentionPercent(
                    typeof gd?.retention?.percent === 'number' ? gd.retention.percent : undefined,
                );
                const services = gd?.services || getNormalizedServices(project);
                if (!Array.isArray(services)) {
                    setFlatContext(undefined);
                    return;
                }
                setFlatContext({
                    [projectId]: {
                        services,
                        serviceLog: gd?.serviceLog ?? [],
                        startDate: gd?.startDate,
                        endDate: gd?.endDate,
                    },
                });
            })
            // Kein Abbruch der Maske: das Ausstellen prueft mit frischem
            // Kontext ein zweites Mal und bindend.
            .catch((e) => console.warn('Pauschal-Kontext nicht ladbar', e));
        return () => {
            cancelled = true;
        };
    }, [draft.primaryProjectId]);

    const issues: ValidationMessage[] = useMemo(
        // Gegen den ANGEDRUCKTEN Kunden: fehlt dem Beleg die Anschrift, ist die
        // abweichende gemeint und nicht die des Stamms. Dieselbe Quelle, die
        // auch das Ausstellen prueft (invoiceService: documentCustomer) — sonst
        // meldete die Maske gruen, was der Ausstellversuch danach ablehnt.
        () => validateInvoice(draft, customerForDocument ?? customer, invoiceConfig, flatContext),
        [draft, customer, customerForDocument, invoiceConfig, flatContext],
    );
    const blocked = hasBlockingErrors(issues);

    /**
     * Was die Maske anzeigt: die Pflichtverstoesse und die namentlich
     * ausgenommenen Warnungen (siehe ALWAYS_SHOWN_WARNINGS).
     *
     * Alle uebrigen Meldungen vom Rang "warning" bleiben in `issues` — sie
     * werden weiterhin geprueft und stehen jedem anderen Aufrufer zur
     * Verfuegung —, sie stehen nur nicht mehr als Leiste ueber dem Beleg. Ueber
     * sie entscheidet der Bearbeiter. Gefiltert wird hier und NICHT in
     * validateInvoice: die Regelmenge bleibt unveraendert, sonst verloere der
     * Ausstellpfad seine eigene Pruefung.
     */
    const shownIssues = useMemo(
        () => issues.filter((m) => m.level === 'error' || ALWAYS_SHOWN_WARNINGS.has(m.code)),
        [issues],
    );

    /**
     * Leistung nach dem Stichtag, die diese Schluss- oder Restrechnung nicht
     * fordert. Steht in der Leiste UND in der Rueckfrage vor dem Ausstellen:
     * bei 5888 lagen zwischen Anlegen und Ausstellen zwei Minuten, die Leiste
     * allein reicht dafuer nicht.
     */
    const offenNachStichtag = useMemo(
        () => issues.filter((m) => m.code === 'final_invoice_leaves_open'),
        [issues],
    );

    /**
     * Der Nebenkostensatz, der fuer diesen Beleg gilt.
     *
     * Fehlt er am Beleg, wurde er nie festgelegt — dann greift der Standard aus
     * den Einstellungen. Eine ausdrueckliche Null steht am Beleg und schlaegt
     * ihn: sie heisst "dieser Beleg traegt keine Nebenkosten" und darf nicht
     * durch einen spaeter hinterlegten Standard ueberschrieben werden.
     */
    const effectiveAncillaryPercent =
        draft.details?.ancillaryPercent ?? invoiceConfig.defaultAncillaryPercent;

    /**
     * Was gedruckt wird, wenn das Feld "Überschrift" leer bleibt: die globale
     * Einstellung, sonst der Standardtitel der Belegart. Dieselbe Funktion, die
     * der Generator benutzt — ein Platzhalter, der etwas anderes verspricht als
     * das PDF zeigt, waere schlimmer als gar keiner.
     */
    const headingFallback = fallbackInvoiceTitle(draft, invoiceConfig.defaultHeaderText);

    /**
     * Auswahlliste der Belegueberschrift: die eingebauten Vorschlaege und die
     * eigenen. Was ohne Auswahl gilt, steht davon unberuehrt im Platzhalter —
     * der Standardtitel der Belegart bzw. die globale Einstellung.
     */
    const headerOptions = useMemo(() => headerTemplateOptions(headerTemplates), [headerTemplates]);

    /**
     * Schreibt die Vorlagenliste ins Profil und uebernimmt sie ERST DANACH in
     * die Anzeige: eine Liste, die eine gescheiterte Speicherung schon zeigt,
     * behauptet einen Stand, den beim naechsten Oeffnen niemand findet.
     *
     * Anders als das Laden der Kostenstellen-Vorlagen bleibt ein Fehlschlag
     * hier sichtbar — wer auf "speichern" drueckt, muss erfahren, wenn nichts
     * gespeichert wurde.
     */
    const persistHeaderTemplates = useCallback(
        async (next: string[]) => {
            if (!userProfile?.id) {
                setError('Vorlage konnte nicht gespeichert werden: kein angemeldetes Profil.');
                return;
            }
            setHeaderTemplateBusy(true);
            try {
                await saveHeaderTemplates(userProfile.id, next);
                if (mounted.current) setHeaderTemplates(next);
            } catch (e) {
                setError('Vorlage konnte nicht gespeichert werden: ' + describeError(e));
            } finally {
                if (mounted.current) setHeaderTemplateBusy(false);
            }
        },
        [userProfile?.id],
    );

    /**
     * Vorschlag fuer die Zahlungsbedingungen — nur bei vollstaendigem Skonto.
     * Eine halbe Angabe (nur Prozent oder nur Frist) ergaebe einen Satz, der
     * eine Frist verspricht, die es nicht gibt.
     */
    const skonto = draft.details?.skonto;
    const skontoText =
        skonto && skonto.percent > 0 && skonto.days > 0
            ? skontoPaymentTermsText(draft.invoiceDate, draft.paymentTermsDays, skonto)
            : '';

    /**
     * Beleg frisch aus der Datenbank holen.
     *
     * Nach einer Zahlung reicht ein lokales Update nicht: paid_cents und der
     * Status werden vom Datenbank-Trigger gesetzt, nicht vom Browser. Wer hier
     * lokal weiterrechnet, zeigt einen Status an, den der Server nicht hat.
     */
    /** Projektcheck fuer die Anzeige. Das Tor beim Ausstellen laeuft eigenstaendig. */
    const runCheck = useCallback(async () => {
        setCheckBusy(true);
        setCheckError(null);
        try {
            const results = await checkInvoiceProjects(draft, invoiceConfig.projectCheck, today);
            setCheck(results.length ? mergeCheckResults(results) : null);
        } catch (e) {
            setCheckError(
                e instanceof Error ? e.message : 'Der Projektcheck konnte nicht ausgeführt werden.',
            );
        } finally {
            setCheckBusy(false);
        }
    }, [draft, invoiceConfig.projectCheck, today]);

    // Nur beim Wechsel des Belegs pruefen, nicht bei jeder Eingabe: der Check
    // laedt Projekt, Berichtsarchiv und Reservierungen und hat mit dem
    // Rechnungstext nichts zu tun.
    useEffect(() => {
        if (isEditable(invoice.status)) {
            void runCheck();
        } else {
            // Festgeschriebene Belege tragen den Befund von damals in details.
            const frozen = (invoice.details as { projectCheck?: ProjectCheckResult & {
                acknowledgementNote?: string;
            } }).projectCheck;
            if (frozen) {
                setCheck({
                    traffic: frozen.traffic,
                    findings: frozen.findings || [],
                    canCreateDraft: true,
                    canIssue: frozen.traffic !== 'red',
                    requiresAcknowledgement: frozen.traffic === 'yellow',
                });
                setAcknowledgement(frozen.acknowledgementNote ?? '');
            }
        }
        // Absichtlich nur an der Beleg-ID: runCheck haengt an draft und wuerde
        // sonst bei jedem Tastendruck einen neuen Lauf ausloesen.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoice.id, invoice.status]);

    const reloadInvoice = useCallback(async () => {
        const fresh = await fetchInvoice(invoice.id);
        // Kein stilles Aufgeben: fetchInvoice liefert null nur noch, wenn es die
        // Rechnung nicht gibt. Wer nach einer Buchung nicht nachladen kann,
        // sieht sonst weiter den alten Status und haelt ihn fuer aktuell.
        if (!fresh) throw new Error('Die Rechnung ist nicht mehr auffindbar.');
        onChange(fresh);
    }, [invoice.id, onChange]);

    /**
     * Die Zuordnung dieses Belegs — was er NEU abrechnet.
     *
     * Sie speist zweierlei: die Vorhersage fuer den Leistungszeitraum in der
     * Vorschau (billingPeriodFallback) und das Angebot fuer alte kumulative
     * Entwuerfe weiter unten. Die Vorhersage greift nur, solange der Entwurf
     * noch keine eigene Zuordnung traegt.
     *
     * Steht bewusst VOR der Vorschau: die reicht den Wert weiter, und eine
     * Deklaration darunter waere zur Zeit ihrer Abhaengigkeitsliste noch nicht
     * initialisiert.
     */
    const billingPeriod = useMemo(
        () => billedPeriodForDisplay(draft) ?? predictedBillingPeriod,
        [draft, predictedBillingPeriod],
    );

    /**
     * Der ABRECHNUNGSZEITRAUM des Belegs — worueber abgerechnet wird. Dieselbe
     * Regel wie im Andruck und in der E-Rechnung (invoiceLogic:
     * printedServicePeriod), damit die beiden Eingabefelder zeigen, was
     * tatsaechlich gedruckt wird.
     *
     * Die Felder sind damit zugleich vorbelegt: ohne eigene Angabe steht dort
     * die Auswahl aus dem Anlagedialog bzw. das Leistungsdatum des Belegs.
     * Geleert faellt der Wert genau dorthin zurueck.
     */
    const printedPeriod = useMemo(() => printedServicePeriod(draft), [draft]);
    const servicePeriodError = servicePeriodIssue(draft);

    /**
     * Der LEISTUNGSZEITRAUM — erste bis letzte abgerechnete Leistung, genau wie
     * ihn der Andruck ausweist. Aus derselben Funktion wie das PDF: eine zweite
     * Fassung hiesse, dass der Bearbeiter etwas anderes sieht als der Kunde.
     */
    const performedPeriod = useMemo(
        () => performancePeriod(draft, predictedBillingPeriod),
        [draft, predictedBillingPeriod],
    );

    /** Zieht der Beleg Leistungen ausserhalb seines Abrechnungszeitraums heran? */
    const servicePeriodDrift = useMemo(() => servicePeriodDriftNote(draft), [draft]);

    /**
     * Der Stichtag, auf den sich die Abrechnung begrenzen laesst — nur wenn der
     * ausgewiesene Zeitraum FRUEHER endet. Siehe servicePeriodLimitOffer.
     */
    const servicePeriodLimit = useMemo(() => servicePeriodLimitOffer(draft), [draft]);

    /**
     * Betreff des Angebots hinter diesem Beleg — Vorlage fuer die eigene
     * Ueberschrift.
     *
     * Nur fuer Entwuerfe geladen: ein ausgestellter Beleg ist eingefroren, dort
     * gaebe es nichts zu uebernehmen. Der Ladeweg ist der vorhandene
     * fetchQuotesForCustomer; einen eigenen Einzelabruf dafuer anzulegen waere
     * ein zweiter Pfad zu denselben Daten. Gesucht wird zuerst das verknuepfte
     * Angebot, hilfsweise eines zum Projekt des Belegs.
     *
     * Leer heisst: kein Angebot auffindbar. Die Schaltflaeche erscheint dann
     * gar nicht, statt beim Klick nichts zu tun.
     */
    const [quoteHeadline, setQuoteHeadline] = useState('');

    useEffect(() => {
        if (!editable || !draft.customerId) return;
        let cancelled = false;
        fetchQuotesForCustomer(draft.customerId)
            .then((quotes) => {
                if (cancelled) return;
                const byId = draft.quoteId ? quotes.find((q) => q.id === draft.quoteId) : undefined;
                // Hauptangebot des Projekts, nicht ein Nachtrag zum selben Projekt.
                const byProject = draft.primaryProjectId
                    ? pickMainQuote(quotes, draft.primaryProjectId)
                    : undefined;
                setQuoteHeadline(quoteHeadlineText(byId ?? byProject));
            })
            // Nur eine Bequemlichkeit — ein Fehlschlag darf die Maske nicht mit
            // einem Banner behelligen, das Eingabe blockiert wirken laesst.
            .catch((e) => console.warn('Angebot zur Rechnung nicht ladbar', e));
        return () => {
            cancelled = true;
        };
    }, [editable, draft.customerId, draft.quoteId, draft.primaryProjectId]);

    // Live-Vorschau, entkoppelt vom Tippen. Ohne Verzoegerung wuerde bei jedem
    // Zeichen ein komplettes PDF gerendert.
    //
    // Laeuft NICHT, sobald ein Archivdokument existiert: der Render nimmt die
    // HEUTIGEN Einstellungen (globale Ueberschrift, Briefkopf, Firmendaten) und
    // setzte einen ausgestellten Beleg damit neu — nach jeder Einstellungs-
    // aenderung sichtbar anders als das, was im Archiv liegt. Massgeblich ist
    // das archivierte PDF; das laedt der Effekt darunter.
    /**
     * Nachweis-Bytes fuer die Live-Vorschau, gecacht ueber die Eingaben, von
     * denen die Anlage tatsaechlich abhaengt. Der Render laedt Projekt und
     * Berichtsarchiv aus Supabase — zu teuer, um ihn bei jedem Tastendruck im
     * Beleg zu wiederholen; Betrags- und Textaenderungen verwenden die Bytes
     * wieder. Auch ein Fehlschlag wird unter seinem Schluessel gemerkt (bytes
     * null), sonst hammert jede weitere Vorschau dieselbe scheiternde Abfrage.
     */
    const proofAnnexCacheRef = useRef<{ key: string; bytes: Uint8Array | null } | null>(null);

    useEffect(() => {
        if (hasArchivedPdf) return;
        // Der Anlagen-Teil ist async (Supabase + pdf-lib): active verhindert,
        // dass eine langsame alte Vorschau eine neuere ueberschreibt.
        let active = true;
        const t = setTimeout(() => {
            if (!customer) return;
            void (async () => {
                try {
                    const doc = generateInvoicePDF(
                        {
                            invoice: draft,
                            // Dieselbe Quelle wie Kopfzeile und E-Rechnung:
                            // festgeschrieben der Snapshot, sonst der lebende
                            // Kunde MIT der Abweichung dieses Belegs. Ohne sie
                            // zeigte die Vorschau eine andere Anschrift als das
                            // PDF, das beim Ausstellen entsteht.
                            //
                            // Hier neu ueberlagert statt customerWithOverride zu
                            // nehmen: das waere bei jedem Rendern ein neues
                            // Objekt, muesste in die Abhaengigkeitsliste und
                            // setzte den 600-ms-Debounce endlos zurueck — die
                            // Vorschau baute nie fertig. draft und customer
                            // stehen ohnehin darin.
                            //
                            // Die Abweichung nur, solange sie fuer den Beleg
                            // gilt — dieselbe Regel wie oben bei
                            // effectiveOverride. isEditable(draft.status) statt
                            // der fertigen Variablen, damit die
                            // Abhaengigkeitsliste unveraendert bleibt: draft
                            // steht ohnehin darin.
                            customer:
                                draft.customerSnapshot ??
                                applyCustomerOverride(
                                    customer,
                                    isEditable(draft.status)
                                        ? draft.details?.customerOverride
                                        : undefined,
                                ),
                            company: companyForPdf,
                            config: invoiceConfig,
                            userName: pdfUserName,
                        },
                        // Die Vorschau druckt denselben Abrechnungszeitraum, den die
                        // Maske zeigt. Beim kumulativen Entwurf gaebe der Beleg
                        // allein noch keinen her — die Zuordnung entsteht erst beim
                        // Ausstellen —, und eine Vorschau ohne die Angabe, die
                        // daneben steht, liest sich wie ein Fehler des Belegs. Der
                        // Wert greift nur als Rueckfall; ab dem Ausstellen gewinnt
                        // die echte Zuordnung.
                        { billingPeriodFallback: billingPeriod },
                    );
                    let uri = doc.output('datauristring');
                    // Anlage schon im Entwurf zeigen: die Vorschau soll das
                    // Dokument zeigen, das beim Ausstellen entsteht — samt
                    // Leistungsnachweis. Ein Fehlschlag der Anlage laesst die
                    // Belegvorschau stehen statt sie mitzureissen.
                    if (draft.details?.attachPerformanceProof && draft.primaryProjectId) {
                        const key = JSON.stringify([
                            draft.primaryProjectId,
                            draft.deliveryDateFrom ?? null,
                            draft.deliveryDateTo ?? null,
                            draft.details?.projectNameOverride ?? '',
                            draft.details?.projects ?? null,
                            // Die Kostenstelle steht seit Neuem in der Infobox
                            // des Nachweises — ihre Aenderung muss den Cache
                            // verwerfen. Ebenso der hochgeladene eigene
                            // Nachweis, der die generierte Fassung ersetzt.
                            draft.details?.costCenter ?? '',
                            draft.details?.costCenterLabel ?? '',
                            draft.details?.customProofPath ?? '',
                            // Der ausgewiesene Leistungszeitraum steht in der
                            // Kopfzeile des Nachweises — dieselbe Regel: was
                            // die Anlage veraendert, gehoert in den Schluessel.
                            draft.details?.servicePeriodFrom ?? '',
                            draft.details?.servicePeriodTo ?? '',
                            // Zeitraum und Abwahl des Nachweises: beide
                            // entscheiden, WAS die Anlage auflistet — ohne sie
                            // im Schluessel bliebe die alte Anlage stehen und
                            // die Vorschau widerspraeche der Einstellung
                            // daneben.
                            draft.details?.proofRangeFrom ?? '',
                            draft.details?.proofRangeTo ?? '',
                            draft.details?.proofExcludedServiceIds ?? null,
                            (draft.lines ?? []).map((l) => `${l.projectId ?? ''}|${l.projectName ?? ''}`),
                        ]);
                        try {
                            if (proofAnnexCacheRef.current?.key !== key) {
                                proofAnnexCacheRef.current = {
                                    key,
                                    bytes: await renderPerformanceProofForInvoice(draft, userProfile),
                                };
                            }
                            const annex = proofAnnexCacheRef.current.bytes;
                            if (annex?.length) {
                                const merged = await appendPdf(
                                    new Uint8Array(doc.output('arraybuffer')),
                                    annex,
                                );
                                uri = pdfBytesToDataUri(merged);
                            }
                        } catch (e) {
                            proofAnnexCacheRef.current = { key, bytes: null };
                            console.warn('Leistungsnachweis-Vorschau fehlgeschlagen', e);
                        }
                    }
                    if (!active) return;
                    setPreviewUri(uri);
                } catch (e) {
                    console.warn('Rechnungsvorschau fehlgeschlagen', e);
                }
            })();
        }, 600);
        return () => {
            active = false;
            clearTimeout(t);
        };
    }, [hasArchivedPdf, draft, customer, companyForPdf, invoiceConfig, pdfUserName, billingPeriod, userProfile]);

    /**
     * Der aktuelle Beleg fuer den Archiv-Ladeeffekt, ueber eine Ref.
     *
     * Der Ladeeffekt darf allein am Archivpfad haengen: der ist eingefroren,
     * der Beleg daneben nicht (Zahlungen, Versand, Serverstand). Stuende der
     * Beleg in der Abhaengigkeitsliste, laedt jede solche Aenderung dasselbe
     * PDF erneut herunter und die Ansicht flackert.
     */
    const draftRef = useRef(draft);
    useEffect(() => {
        draftRef.current = draft;
    }, [draft]);

    /**
     * Das Archivdokument einmalig laden und einbetten.
     *
     * Derselbe Ladeweg wie der Knopf "PDF öffnen": signierte URL auf den
     * Storage-Pfad, dahinter die Bytes. Genommen wird die vorhandene
     * fetchArchivedPdfBytes — einen zweiten Abrufpfad zu denselben Bytes
     * anzulegen hiesse, zwei Stellen gegen dieselbe Ablage zu pflegen. Die
     * Bytes werden hier zusaetzlich zu einer Objekt-URL gemacht, weil das
     * Einbett-Element eine Quelle braucht, die es ohne erneute Anfrage
     * darstellen kann.
     */
    useEffect(() => {
        const path = draft.pdfUrl;
        if (!path) {
            setArchiveUri(null);
            setArchiveError(null);
            return;
        }
        let cancelled = false;
        let objectUrl: string | null = null;
        setArchiveError(null);
        fetchArchivedPdfBytes(draftRef.current)
            .then((bytes) => {
                if (cancelled) return;
                objectUrl = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
                setArchiveUri(objectUrl);
            })
            .catch((e) => {
                if (cancelled) return;
                console.warn('Archivdokument nicht ladbar', e);
                setArchiveError('Archivdokument konnte nicht geladen werden.');
            });
        return () => {
            cancelled = true;
            // Ohne Freigabe haelt der Browser die PDF-Bytes bis zum Neuladen der
            // Seite fest — bei jedem geoeffneten Beleg ein weiteres Mal.
            if (objectUrl) URL.revokeObjectURL(objectUrl);
            setArchiveUri(null);
        };
    }, [draft.pdfUrl]);

    /**
     * Ausstehende Aenderungen wegschreiben.
     *
     * Haengt sich an die laufende Speicherung an, statt daneben zu starten: zwei
     * gleichzeitige Requests koennen in umgekehrter Reihenfolge antworten, und
     * die aeltere Antwort wuerde die neuere Eingabe ueberschreiben.
     */
    const flushSave = useCallback(() => {
        const updates = pendingUpdates.current;
        if (!updates) return;
        pendingUpdates.current = null;
        saveChain.current = saveChain.current.then(async () => {
            try {
                const saved = await updateInvoice(invoice.id, updates);
                // Wartet schon wieder etwas, ist der Serverstand ueberholt —
                // ihn jetzt zu uebernehmen setzt das gerade Getippte zurueck.
                if (!mounted.current || pendingUpdates.current || saveTimer.current) return;
                setDraft(saved);
                onChange(saved);
            } catch (e: any) {
                const message = rechtFehlerOder(
                    e,
                    () => e?.message || 'Die Änderung konnte nicht gespeichert werden.',
                );
                // Nach dem Verlassen gibt es kein Banner mehr, das den Fehlschlag
                // zeigen koennte — er darf trotzdem nicht spurlos verschwinden.
                if (mounted.current) setError(message);
                else console.error('Autosave nach dem Verlassen fehlgeschlagen:', message);
            }
        });
    }, [invoice.id, onChange]);

    // Ueber eine Ref, damit das Aufraeumen unten nicht bei jedem Wechsel von
    // onChange erneut laeuft und die Speicherung zu frueh ausloest.
    const flushRef = useRef(flushSave);
    useEffect(() => {
        flushRef.current = flushSave;
    }, [flushSave]);

    // Ohne dieses Aufraeumen feuert der Debounce-Timer nach dem Zuruecknavigieren
    // weiter: der Editor springt von selbst wieder auf, und ein Fehlschlag
    // schreibt in eine nicht mehr eingehaengte Komponente. Die noch ausstehende
    // Aenderung wird vorher gespeichert, sonst ist sie verloren.
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            if (saveTimer.current) {
                clearTimeout(saveTimer.current);
                saveTimer.current = null;
            }
            flushRef.current();
        };
    }, []);

    /** Autosave nur fuer Entwuerfe — ab der Ausstellung gibt es nichts zu speichern. */
    const patch = useCallback(
        (updates: Partial<Invoice>) => {
            if (!editable) return;
            setDraft((prev) => ({ ...prev, ...updates }));
            // Verschmelzen statt ersetzen: sonst ginge bei einer zweiten Eingabe
            // innerhalb der Debounce-Zeit das zuerst geaenderte Feld verloren.
            pendingUpdates.current = { ...(pendingUpdates.current ?? {}), ...updates };
            if (saveTimer.current) clearTimeout(saveTimer.current);
            saveTimer.current = setTimeout(() => {
                saveTimer.current = null;
                flushSave();
            }, 1200);
        },
        [editable, flushSave],
    );

    /**
     * Eine einzelne Belegzeile aendern.
     *
     * Wirkt ausschliesslich im Beleg — das Projekt-LV bleibt, wie es ist. Die
     * Rueckschreibung beim Ausstellen laeuft ueber serviceId und Menge, nicht
     * ueber Nummer oder Text.
     */
    const patchLine = useCallback(
        (id: string, updates: Partial<InvoiceLine>) => {
            patch({ lines: draft.lines.map((l) => (l.id === id ? { ...l, ...updates } : l)) });
        },
        [patch, draft.lines],
    );

    // ── Abschnitte ────────────────────────────────────────────────────────
    // Frei benannte Gliederung DIESES Belegs ("Haus 1", "Bauabschnitt Nord").
    // Gespeichert sind nur die Abschnitte (details.sections) und die Zuordnung
    // je Zeile (line.sectionId); die Ueberschriftszeilen im Beleg leitet
    // applyInvoiceSections daraus ab. Deshalb laeuft JEDE Aenderung hier durch
    // dieselbe Funktion — sonst haette der Beleg zwei Gliederungen.
    const invoiceSections: InvoiceSection[] = draft.details?.sections ?? [];

    /** Abschnitte setzen und die Zeilen danach ordnen. */
    const setSections = (next: InvoiceSection[]) => {
        patch({
            // Leere Liste heisst "keine Gliederung" und wird nicht als leeres
            // Feld mitgeschleppt.
            details: { ...draft.details, sections: next.length ? next : undefined },
            lines: applyInvoiceSections(draft.lines, next),
        });
    };

    const addSection = () =>
        setSections([
            ...invoiceSections,
            {
                id: crypto.randomUUID(),
                // Ein leerer Name waere eine leere Ueberschrift auf dem Beleg.
                title: `Abschnitt ${invoiceSections.length + 1}`,
            },
        ]);

    const renameSection = (id: string, title: string) =>
        setSections(invoiceSections.map((sec) => (sec.id === id ? { ...sec, title } : sec)));

    /**
     * Abschnitt loeschen — seine Positionen werden "ohne Zuordnung" und
     * bleiben auf dem Beleg. Das Raeumen der Zuordnung erledigt
     * applyInvoiceSections: der Abschnitt ist dann keiner mehr, den es gibt.
     */
    const deleteSection = (id: string) =>
        setSections(invoiceSections.filter((sec) => sec.id !== id));

    const moveSection = (id: string, dir: -1 | 1) => {
        const index = invoiceSections.findIndex((sec) => sec.id === id);
        const to = index + dir;
        if (index < 0 || to < 0 || to >= invoiceSections.length) return;
        const next = [...invoiceSections];
        [next[index], next[to]] = [next[to], next[index]];
        setSections(next);
    };

    /** Position einem Abschnitt zuordnen (leer = ohne Zuordnung). */
    const assignLineSection = (lineId: string, sectionId?: string) => {
        patch({
            lines: applyInvoiceSections(
                draft.lines.map((l) => (l.id === lineId ? { ...l, sectionId } : l)),
                invoiceSections,
            ),
        });
    };

    /** Wie viele Positionen in einem Abschnitt liegen — die Maske zeigt auch die leeren. */
    const sectionLineCount = (id: string) =>
        draft.lines.filter((l) => l.kind === 'SERVICE' && l.sectionId === id).length;

    /**
     * Positionen nach ihrer Nummer ordnen, sobald die Eingabe verlassen wird.
     *
     * Das Speichern ordnet ohnehin (nextInvoicePosNumbers in updateInvoice) —
     * aber erst nach der Debounce-Zeit und nur, wenn gerade nichts anderes
     * wartet. Waehrend des Tippens umzusortieren risse die Zeile unter dem
     * Cursor weg: aus der "1" von "10" wuerde sofort eine erste Position.
     */
    const sortLinesByPosNumber = useCallback(() => {
        const sorted = nextInvoicePosNumbers(draft.lines);
        const changed = sorted.some(
            (l, i) => l.id !== draft.lines[i].id || l.posNumber !== draft.lines[i].posNumber,
        );
        if (changed) patch({ lines: sorted });
    }, [patch, draft.lines]);

    /**
     * Den Konfigurationsstandard in Entwuerfe nachziehen, die ihn nicht kennen.
     *
     * Ein Entwurf haelt seinen eigenen Satz fest — richtig so, sonst rechnete
     * eine spaetere Aenderung der Einstellungen alte Belege um. Entwuerfe aus
     * der Zeit VOR der Einstellung haben aber gar keinen, und ohne dieses
     * Nachziehen bekommen sie den Zuschlag nie: weder beim Speichern noch beim
     * Neuaufbau, weil updateInvoice ausschliesslich den Satz des Belegs liest.
     * Genau das ist der Grund, warum die Nebenkosten "nicht immer" erscheinen.
     *
     * Nur bei Entwuerfen, und nur wenn am Beleg nichts steht — eine
     * ausdrueckliche Null bleibt unangetastet.
     */
    useEffect(() => {
        if (!editable) return;
        if (draft.details?.ancillaryPercent !== undefined) return;
        if (!invoiceConfig.defaultAncillaryPercent) return;
        patch({
            details: {
                ...draft.details,
                ancillaryPercent: invoiceConfig.defaultAncillaryPercent,
            },
            // Zeilen mitschicken: nur dann bildet updateInvoice den Zuschlag.
            lines: draft.lines,
        });
        // Einmal je Beleg. An draft zu haengen hiesse, nach jedem Speichern
        // erneut zu pruefen — und der Beleg traegt den Satz danach ohnehin.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoice.id, editable, invoiceConfig.defaultAncillaryPercent]);

    /**
     * Den Sicherheitseinbehalt des PROJEKTS in neue Entwuerfe nachziehen.
     *
     * Derselbe Gedanke wie beim Nebenkostensatz darueber, nur kommt die
     * Vorbelegung hier aus dem Projekt und nicht aus den Einstellungen: der
     * Einbehalt ist eine Bedingung DIESES Bauvertrags. Und wie dort gilt sie
     * ausschliesslich fuer Entwuerfe, die noch gar nichts sagen — eine
     * ausdrueckliche Null am Beleg heisst "dieser Beleg behaelt nichts ein" und
     * darf nicht von der Projektangabe ueberschrieben werden.
     *
     * NICHT auf der Schlussrechnung: sie zahlt die Einbehalte aus, statt neue
     * zu bilden (Bauplan §5.2). Ein Gewaehrleistungseinbehalt nach Abnahme
     * bleibt moeglich, aber nur von Hand. Und nicht auf Storno/Gutschrift:
     * dort gibt es nichts einzubehalten, der Beleg spiegelt das Original.
     */
    useEffect(() => {
        if (!editable) return;
        if (draft.details?.retentionPercent !== undefined) return;
        if (!projectRetentionPercent) return;
        if (draft.documentType === 'FINAL_INVOICE') return;
        if (NEGATIVE_DOCUMENT_TYPES.has(draft.documentType)) return;
        patch({
            details: {
                ...draft.details,
                retentionPercent: projectRetentionPercent,
            },
            // Zeilen mitschicken: nur dann rechnet updateInvoice die Summen neu.
            lines: draft.lines,
        });
        // Einmal je Beleg, sobald der Projektstand da ist — wie oben bewusst
        // ohne draft in den Abhaengigkeiten.
        //
        // Dass er erst nach dem Laden des Projekts anspringt, ist auch der
        // Grund, warum er dem Nebenkosten-Effekt darueber nicht ins Gehege
        // kommt: beide bauen ihr details-Objekt aus dem gerade gerenderten
        // draft, und liefen sie im selben Durchlauf, ueberschriebe der zweite
        // das Feld des ersten. Der Projektstand kommt aber immer aus einer
        // spaeteren Antwort, also aus einem spaeteren Durchlauf.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoice.id, editable, projectRetentionPercent, draft.documentType]);

    /**
     * Wartet, bis alles Getippte wirklich in der Datenbank steht.
     *
     * Jede Aktion liest den Beleg frisch vom Server: der Neuaufbau holt sich
     * den Nebenkostensatz, das Ausstellen den ganzen Beleg. Laeuft daneben noch
     * ein Autosave — oder wartet einer im Debounce —, arbeitet die Aktion auf
     * dem Stand von vor der letzten Eingabe. Beim Ausstellen wird der dann
     * festgeschrieben.
     */
    const settleSaves = useCallback(async () => {
        if (saveTimer.current) {
            clearTimeout(saveTimer.current);
            saveTimer.current = null;
        }
        flushSave();
        await saveChain.current;
    }, [flushSave]);

    /**
     * Bezugspunkt fuer die Frage "wurden die LV-Positionen von Hand angefasst?".
     *
     * Der Aufbau ersetzt sie vollstaendig. Solange sie so dastehen, wie der
     * letzte Aufbau sie hinterlassen hat, kostet das nichts. Hat jemand eine
     * Menge oder einen Preis geaendert, waere derselbe Aufbau ein stiller
     * Datenverlust — dann laeuft er nicht von selbst, sondern nur auf Klick.
     */
    const rebuildBaseline = useRef(lvFingerprint(invoice.lines));
    const [positionsEdited, setPositionsEdited] = useState(false);

    /**
     * Kumulativ heisst: die Positionen nennen den Leistungsstand bis zum
     * Stichtag. Der Beleg hat dann keinen waehlbaren Beginn — er ergibt sich
     * aus den Leistungen, die er abrechnet.
     */
    const isCumulative = draft.details?.billingMode === 'CUMULATIVE';

    /**
     * Abschlagsabzug in der Maske.
     *
     * Im Modus INDIVIDUAL nennt der Andruck jeden Abschlag einzeln und darunter
     * die Bruttosumme. Die Maske bildet beides nach: stuende hier nur die Summe,
     * saehe der Anwender erst im PDF, was der Kunde liest.
     *
     * Gezaehlt werden Belege, nicht Zeilen — ein Abschlag ueber zwei Steuersaetze
     * steht mit zwei Eintraegen in der Abzugsliste.
     */
    const advanceDeductions = draft.advanceDeductions ?? [];
    const listAdvanceDeductions =
        draft.advanceDeductionMode === 'INDIVIDUAL' && advanceDeductions.length > 0;
    // Nummerierung EINMAL rechnen, wie im Andruck: Ueberschrift, Einzelzeilen
    // und Summenzeile lesen von hier, damit die Maske zeigt, was gedruckt wird.
    const advanceNumbering = draft.details?.advanceNumbering;
    const advancePlan = advanceNumberingPlan(advanceDeductions, advanceNumbering);
    // Dieselbe Beschriftungsquelle wie der Andruck — keine Zweitfassung. Sie
    // liefert null, solange die Summenzeile nicht angezeigt gehoert.
    const advanceRangeLabel = listAdvanceDeductions
        ? advanceDeductionRangeLabel(advanceDeductions, { numbering: advanceNumbering })
        : null;
    /**
     * Summenzeile der Abschlaege MIT Einbehalt: "abzüglich erhaltener
     * Abschlagszahlungen (Abschläge 1–6)". Sie nennt die Zahlungen, nicht die
     * Forderungen (Bauplan §16), und traegt die Spanne wie die Zeile ohne
     * Einbehalt.
     */
    const advanceSpan = advanceDeductionSpanLabel(advanceDeductions, advanceNumbering);
    const paidPriorLabel = `abzüglich erhaltener Abschlagszahlungen${advanceSpan ? ` (${advanceSpan})` : ''}`;
    /**
     * Die fette Ueberschrift des Abzugsblocks, so wie der Andruck sie setzt.
     *
     * Ohne eigene Eingabe ist das der gerechnete Standardtext — er steht in der
     * Maske als Vorgabe des Eingabefeldes, damit sichtbar ist, wogegen
     * korrigiert wird.
     */
    const advanceHeadingDefault = advanceDeductionHeadingLabel(advanceDeductions, {
        mode: draft.advanceDeductionMode,
        numbering: advanceNumbering,
    });
    /**
     * Restforderung, aufgeteilt in Entgelt und Umsatzsteuer.
     *
     * Dieselbe Rechnung und dieselben Texte wie im Andruck (generatorInvoice) —
     * die Maske soll zeigen, was auf dem Beleg steht, und keine Zweitfassung
     * derselben Aussage fuehren.
     */
    const payableSplit = payableBreakdown(draft.totals, advanceDeductions);
    const payableSplitOnly = payableSplit.groups.length === 1 ? payableSplit.groups[0] : null;
    const payableSplitLines =
        payableSplitOnly && payableSplitOnly.taxRateBp !== 0
            ? [
                  `davon netto ${formatCents(payableSplitOnly.netCents)} · USt ${formatTaxRateBp(payableSplitOnly.taxRateBp)} ${formatCents(payableSplitOnly.taxCents)}`,
              ]
            : payableSplit.groups.map((g) =>
                  g.taxRateBp === 0
                      ? `davon ohne USt: netto ${formatCents(g.netCents)}`
                      : `davon zu ${formatTaxRateBp(g.taxRateBp)} USt: netto ${formatCents(g.netCents)} · USt ${formatCents(g.taxCents)}`,
              );

    // ── Sicherheitseinbehalt ────────────────────────────────────────────────
    //
    // Die Betraege kommen aus den gespeicherten Summen und nicht aus einer
    // eigenen Rechnung in der Maske: gerechnet wird beim Speichern
    // (updateInvoice), und zwei Rechenwege fuer dieselbe Zahl laufen frueher
    // oder spaeter auseinander. Zwischen Tastendruck und Antwort zeigt die
    // Maske deshalb kurz den vorigen Stand — genau wie beim Nebenkostensatz.
    //
    // Kumulatives Modell (Bauplan §16): cumulative = Satz x Leistungsstand,
    // prior = auf den abgezogenen Vorbelegen bereits einbehalten, retention =
    // mit diesem Beleg neu, release = mit diesem Beleg frei, paidPrior =
    // tatsaechlich erhaltene Abschlagszahlungen (prepaid - prior). Altbestand
    // ohne die Felder rechnet 0 bzw. paidPrior = prepaid.
    const retentionPercent = draft.details?.retentionPercent;
    /** Der Satz, mit dem der Beleg wirklich rechnet — Schlussrechnung mit Freigabe: 0. */
    const retentionEffectivePercent = effectiveRetentionPercent(draft.documentType, draft.details);
    const cumulativeRetentionCents = draft.totals.cumulativeRetentionCents ?? 0;
    const priorRetentionCents = draft.totals.priorRetentionCents ?? 0;
    const retentionCents = draft.totals.retentionCents ?? 0;
    const retentionReleaseCents = draft.totals.retentionReleaseCents ?? 0;
    const paidPriorCents = draft.totals.paidPriorCents ?? draft.totals.prepaidCents;
    /** Bruttoforderung DIESES Belegs (gross - prepaid), Bezugsgroesse der Steueraufteilung. */
    const retentionBasis = invoiceAmountCents(
        draft.totals.grossTotalCents,
        draft.totals.prepaidCents,
    );
    /**
     * Sobald der Einbehalt irgendwo wirkt, bekommt der Summenblock das
     * Buchhaltungsmuster — auch dann, wenn cumulative gleich prior ist und
     * dieser Beleg selbst nichts einbehaelt: der Kunde soll trotzdem sehen,
     * dass auf dem Leistungsstand ein Einbehalt liegt.
     */
    const showsRetention =
        cumulativeRetentionCents !== 0 ||
        priorRetentionCents !== 0 ||
        retentionCents !== 0 ||
        retentionReleaseCents !== 0;
    /** Satz 0 bei offenem Einbehalt: die Einbehaltzeile wird zur Freigabezeile. */
    const showsRelease = cumulativeRetentionCents === 0 && retentionReleaseCents > 0;
    /**
     * Storno und kaufmaennische Gutschrift behalten nichts ein: sie spiegeln
     * den Einbehalt des Originals bzw. tragen 0 (creditNoteLogic). Ein
     * Eingabefeld dort verspraeche eine Wirkung, die es nicht hat.
     */
    const retentionLocked = NEGATIVE_DOCUMENT_TYPES.has(draft.documentType);
    /** Ueber 10 % ist ungewoehnlich — § 17 VOB/B kennt 5 %, vereinbart werden 5–10 %. */
    const retentionUnusual = (retentionPercent ?? 0) > 10;
    /** Einbehalte der abgezogenen Vorbelege; auf der Schlussrechnung die Freigabeliste. */
    const priorRetentions = draft.priorRetentions ?? [];
    const priorRetentionListed = priorRetentionTotal(priorRetentions);
    const releasesRetention = wantsRetentionRelease(draft.documentType, draft.details);
    /**
     * Nachholfall: Vorrechnungen abgezogen, aber auf keiner davon ein Einbehalt
     * hinterlegt — dieser Beleg holt den vollen Satz auf den Leistungsstand
     * nach. Richtig, wenn der Kunde bisher voll gezahlt hat; falsch, wenn er
     * die Vorrechnungen bereits selbst gekuerzt hat (Altbestand vor dem
     * Feature). Dann gehoeren die Betraege als Alt-Einbehalte ins Projekt.
     */
    const retentionCatchUp =
        priorRetentionCents === 0 && draft.totals.prepaidCents > 0 && retentionCents > 0;
    /** Maske "Alt-Einbehalte erfassen" aus dem Editor heraus — braucht das Projekt. */
    const [showRetentionBackfill, setShowRetentionBackfill] = useState(false);
    /**
     * Projektvorgabe des Einbehalts — gepflegt HIER und nicht im Projekt-Hub:
     * sie ist eine Abrechnungsbedingung, keine Baustellenangabe, und den
     * Aussendienst geht sie nichts an. null = Anzeige, sonst der getippte
     * Wert; geschrieben wird erst mit "Übernehmen".
     */
    const [projectRetentionDraft, setProjectRetentionDraft] = useState<string | null>(null);
    const canEditProjectRetention =
        darf.bearbeiten.erlaubt && Boolean(draft.primaryProjectId);

    /** Nummerierungsvorgabe aendern — leeres Feld heisst "nichts vorgegeben". */
    const patchAdvanceNumbering = (updates: Partial<NonNullable<typeof advanceNumbering>>) => {
        const next = { ...(advanceNumbering ?? {}), ...updates };
        const cleaned = Object.fromEntries(
            Object.entries(next).filter(([, v]) => v !== undefined && v !== false),
        );
        patch({
            details: {
                ...draft.details,
                advanceNumbering: Object.keys(cleaned).length ? cleaned : undefined,
            },
        });
    };

    /**
     * Leistungen, die dieser Beleg bewusst NICHT abrechnet.
     *
     * Steht am Beleg und nicht im Zustand der Maske: der Positionsaufbau
     * ersetzt die LV-Zeilen vollstaendig und holte eine bloss geloeschte Zeile
     * beim naechsten Zeitraumwechsel zurueck.
     */
    const excludedServiceIds = draft.details?.excludedServiceIds ?? [];
    const excludedKey = excludedServiceIds.join('|');

    /**
     * Wofuer zuletzt gebaut wurde: Zeitraum und die Behandlung undatierter
     * Mengen (Begruendung an neuaufbauSchluessel). Die Wahl ist ohne Angabe
     * die des Hakens; beim Belegwechsel wird sie ausdruecklich mitgegeben,
     * weil der Haken dann erst im naechsten Rendern die neue Wahl zeigt.
     */
    const keyOf = (from?: string, to?: string, ohneDatum: boolean = rebuildUndated) =>
        neuaufbauSchluessel(isCumulative, from, to, ohneDatum);
    const rebuildKey = keyOf(draft.deliveryDateFrom, draft.deliveryDateTo);
    const rebuiltFor = useRef(rebuildKey);

    // Beim Wechsel des Belegs beide Bezugspunkte neu setzen: die Komponente
    // bleibt dabei stehen, und ein Fingerabdruck aus einer anderen Rechnung
    // wuerde jeden folgenden Aufbau als "von Hand geaendert" abweisen.
    //
    // Hier gehoert ALLES hin, was zu genau einem Beleg gehoert und nicht aus
    // draft abgeleitet ist. Dass der Editor heute bei jedem Belegwechsel neu
    // gemountet wird, macht den Reset praktisch folgenlos — aber die Invariante
    // haengt dann an einer Entscheidung der aufrufenden Ansicht. Wer den Editor
    // spaeter stehen laesst (Vor/Zurueck in der Liste), erbte sonst Ueberschrift
    // und Titel des vorigen Belegs, ohne dass hier etwas danach aussieht.
    useEffect(() => {
        rebuildBaseline.current = lvFingerprint(invoice.lines);
        // Die Wahl „ohne Datum mitnehmen" gehoert zum Beleg: der naechste
        // oeffnet mit SEINER. Bezugspunkt und Haken mit derselben Wahl — sonst
        // loeste schon der Wechsel einen Neuaufbau aus.
        const wahl = gespeicherteWahlOhneDatum(invoice.details);
        setRebuildUndated(wahl);
        rebuiltFor.current = keyOf(invoice.deliveryDateFrom, invoice.deliveryDateTo, wahl);
        setPositionsEdited(false);
        // Die Vorhersage gehoert zu genau einem Beleg. Bliebe sie stehen, zeigte
        // der naechste den Zeitraum des vorigen.
        setPredictedBillingPeriod(undefined);
        // Der Angebotsbetreff wird je Beleg nachgeladen; bis der Abruf zurueck
        // ist, boete die Schaltflaeche sonst die Ueberschrift des vorigen an.
        setQuoteHeadline('');
        // Titel herausgenommener Leistungen sind ein Merkzettel zum Beleg —
        // fremde Titel an fremden serviceIds waeren schlicht falsch.
        setExcludedTitles({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoice.id]);

    /**
     * Die Vorhersage schon beim OEFFNEN besorgen.
     *
     * Bisher entstand sie ausschliesslich im Neuaufbau. Wer einen kumulativen
     * Entwurf nur aufschlaegt, loest keinen aus — der Beleg traegt noch keine
     * Zuordnung, der kumulative Rueckfall auf den Leistungszeitraum ist
     * verboten, und die Zeile "Abrechnungszeitraum" fehlte deshalb in Maske und
     * Andruck. Genau die Angabe, nach der der Kunde beim zweiten Beleg desselben
     * Projekts als erstes fragt.
     *
     * Rein lesend: es wird NICHTS gespeichert und keine Zeile uebernommen. Der
     * Aufbau liefert hier nur den vorhergesagten Zeitraum; seine Positionen
     * anzufassen waere ein zweiter, unbeobachteter Schreibweg neben dem
     * Neuaufbau — ein Oeffnen darf einen Beleg nicht veraendern.
     *
     * Kein Konflikt mit dem Neuaufbau: der setzt denselben State kurz darauf mit
     * demselben Ergebnis, weil dieselben puren Bausteine ueber denselben Stand
     * laufen. Ein doppelter Lauf ist deshalb unschaedlich. Die Abhaengigkeiten
     * nennen bewusst nur Beleg, Projekt und Stichtag — weder die Vorhersage
     * selbst noch draft.lines, sonst triebe der Neuaufbau (er ersetzt die
     * Zeilen) diesen Effekt und der Effekt sich selbst im Kreis.
     *
     * Traegt der Beleg bereits eine Zuordnung, ist nichts vorherzusagen: dann
     * ist der Zeitraum festgestellt, und billedPeriodForDisplay hat ohnehin
     * Vorrang.
     */
    useEffect(() => {
        if (!statusOffen || !isCumulative || !draft.primaryProjectId) return;
        if (billingPeriodOf(draft.lines)) return;
        // Dieselben zwei Bremsen wie beim Positionsaufbau weiter unten, und aus
        // demselben Grund: der Stichtag kommt aus einem Datumsfeld. Ohne sie
        // liefe hier je Tastendruck ein voller Netzlauf, jeder fuer eine
        // Zwischenstufe wie "0002-05-01". Ein leerer Stichtag bleibt erlaubt —
        // ohne ihn rechnet der Beleg das ganze Protokoll ab.
        if (!datumVollstaendig(draft.deliveryDateTo)) return;
        // Vor dem Timer festhalten: im Rueckruf ist die Pruefung oben fuer den
        // Typpruefer verloren, und ein "!" verspraeche etwas, das hier ohnehin
        // schon feststeht.
        const projectId = draft.primaryProjectId;
        let cancelled = false;
        const timer = setTimeout(() => {
            buildProjectServiceLines(projectId, {
                excludeInvoiceId: draft.id,
                // Ohne Stichtag rechnet der Beleg das ganze Protokoll ab — der
                // Aufbau kommt damit zurecht und braucht keinen Ersatzwert.
                periodTo: draft.deliveryDateTo,
                cumulative: true,
                // Ohne den Ausschluss nennte die Vorhersage einen Zeitraum, den
                // der Beleg gar nicht abrechnet — die herausgenommene Leistung
                // spannte ihn mit auf.
                excludedServiceIds: draft.details?.excludedServiceIds,
                taxRateBp: invoiceConfig.defaultTaxRateBp,
            })
                .then((built) => {
                    if (cancelled) return;
                    setPredictedBillingPeriod(built.cumulativeBillingPeriod);
                })
                // Eine Anzeige, kein Torwaechter: scheitert der Lauf, entfaellt
                // die Angabe still. Ein Banner behauptete einen Fehler, den der
                // Bearbeiter an diesem Beleg nicht beheben kann.
                .catch((e) => console.warn('Abrechnungszeitraum nicht vorhersagbar', e));
        }, DATE_DEBOUNCE_MS);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
        // draft.lines steht bewusst NICHT in der Liste — siehe Kommentar oben.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        draft.id,
        statusOffen,
        isCumulative,
        draft.primaryProjectId,
        draft.deliveryDateTo,
        // Als Zeichenkette und nicht als Feld: der Beleg kommt nach jedem
        // Speichern mit einem neuen Feld gleichen Inhalts zurueck, und der
        // Effekt liefe dann bei jedem Tastendruck erneut ueber das Netz.
        excludedKey,
        invoiceConfig.defaultTaxRateBp,
    ]);

    /**
     * Kumulativer Entwurf aus der Zeit VOR den beiden Feldern: er traegt keinen
     * ausgewiesenen Zeitraum und faellt deshalb auf deliveryDateFrom zurueck —
     * also auf die erste Leistung des Projekts, den gemeldeten Fehler.
     *
     * Die richtige Angabe steht daneben bereits auf dem Schirm: die Zeile
     * "Leistungszeitraum" nennt, was der Beleg tatsaechlich abrechnet.
     * Uebernommen wird sie aber nur auf Knopfdruck.
     *
     * Bewusst KEIN stilles Nachziehen beim Oeffnen: das schriebe in die
     * Datenbank, ohne dass jemand etwas getan hat, machte aus einer blossen
     * VORHERSAGE (predictedBillingPeriod) eine festgehaltene Aussage des Belegs
     * und traege bewusst geleerte Felder wieder ein. Ein Angebot, das der
     * Bearbeiter annehmen kann, ist hier die ehrlichere Form.
     *
     * Nur Entwuerfe: ein ausgestellter Beleg ist eingefroren, seine Bytes
     * liegen im Archiv.
     */
    const offerServicePeriod =
        editable &&
        isCumulative &&
        !draft.details?.servicePeriodFrom &&
        !draft.details?.servicePeriodTo &&
        !!billingPeriod;

    const applyBillingPeriodAsServicePeriod = () => {
        if (!billingPeriod) return;
        patch({
            details: { ...draft.details, ...servicePeriodFromSelection(billingPeriod) },
        });
    };

    const run = async (label: string, fn: () => Promise<void>) => {
        setBusy(label);
        setError(null);
        setTorSperre(null);
        setNotice(null);
        try {
            await settleSaves();
            await fn();
        } catch (e: any) {
            setTorSperre(torSperrAnzeige(e));
            if (e instanceof InvoiceValidationError) {
                setError(
                    `Die Rechnung ist unvollständig: ${e.issues
                        .filter((i) => i.level === 'error')
                        .map((i) => i.message)
                        .join(' ')}`,
                );
            } else {
                setError(rechtFehlerOder(e, () => e?.message || 'Die Aktion ist fehlgeschlagen.'));
            }
        } finally {
            setBusy(null);
        }
    };

    /**
     * Positionen aus dem Projekt-LV neu aufbauen — mit dem aktuell gewaehlten
     * Leistungszeitraum.
     *
     * Die Zeilen entstehen beim Anlegen des Entwurfs. Wer den Zeitraum danach
     * aendert, aendert ohne diesen Aufbau nur die Kopfangabe des Belegs, waehrend
     * die Positionen weiter den gesamten offenen Bestand zeigen — die Rechnung
     * behauptet dann einen Monat und rechnet alles ab. Deshalb laeuft er nach
     * einer Datumsaenderung von selbst; siehe den Effekt weiter unten.
     */
    const rebuildPositions = (
        /**
         * Die kuenftige Ausschlussliste, falls dieser Aufbau sie gerade aendert
         * (Wiederaufnahme einer herausgenommenen Leistung). Ohne Angabe gilt
         * die am Beleg vermerkte.
         */
        excludedOverride?: string[],
        /**
         * Der kuenftige Stichtag, falls dieser Aufbau ihn gerade setzt.
         *
         * Gebraucht vom Knopf "Abrechnung bis ... begrenzen": er aendert das
         * Datum und baut in EINEM Schritt neu auf. Ohne die Uebergabe liefe der
         * Aufbau auf dem Stand VOR der Aenderung — setDraft wirkt erst zum
         * naechsten Rendern — und die Positionen blieben genau die, die der
         * Knopf beseitigen soll.
         */
        periodToOverride?: string,
    ) =>
        run('rebuild', async () => {
            // Was jetzt gebaut wird, gilt als gebaut — auch wenn es scheitert.
            // Sonst versuchte der automatische Aufbau es endlos erneut.
            rebuiltFor.current = rebuildKey;
            if (!draft.primaryProjectId) {
                throw new Error(
                    'Dem Beleg ist kein Projekt zugeordnet — es gibt kein Leistungsverzeichnis, aus dem Positionen entstehen könnten.',
                );
            }
            // Das Abrechnungsmodell des Belegs mitfuehren. Ohne das baut der
            // Knopf einen kumulativen Beleg still in einen fortschreibenden um:
            // die Positionen zeigten danach nur noch die offenen Mengen,
            // waehrend der Abzug der Vorrechnungen im Summenblock stehen bleibt
            // — der Beleg forderte dann viel zu wenig.
            // Der Ausschluss muss JEDEN Neuaufbau ueberleben — sonst holte
            // schon die naechste Zeitraumaenderung die herausgenommene Leistung
            // zurueck, und niemand saehe, dass die Entscheidung verfallen ist.
            const excludedServiceIds = excludedOverride ?? draft.details?.excludedServiceIds;
            const periodTo = periodToOverride ?? draft.deliveryDateTo;
            const built = await buildProjectServiceLines(draft.primaryProjectId, {
                excludeInvoiceId: draft.id,
                periodFrom: draft.deliveryDateFrom,
                periodTo,
                includeUndated: rebuildUndated,
                cumulative: isCumulative,
                excludedServiceIds,
                taxRateBp: invoiceConfig.defaultTaxRateBp,
            });

            // Nur die aus dem LV stammenden Zeilen werden ersetzt. Von Hand
            // ergaenzte Text-, Rabatt- und Abschlagszeilen bleiben stehen — sie
            // haben keine Entsprechung im Projekt und waeren sonst weg.
            const kept = draft.lines.filter((l) => !(l.kind === 'SERVICE' && l.projectId));

            // Der wartende Autosave ist bereits durch run() geflossen — sein
            // Inhalt steht in der Datenbank und steckt zugleich in diesem
            // Aufruf. Was hier trotzdem mitmuss, ist der Nebenkostensatz:
            // updateInvoice liest ihn sonst allein aus dem Beleg, und ein
            // Entwurf aus der Zeit vor der Einstellung hat dort keinen. Der
            // Neuaufbau lieferte dann Positionen ohne Zuschlag zurueck.
            //
            // Kumulativ wird der Leistungsbeginn hier mitgezogen: die Positionen
            // reichen bis zur ersten Leistung zurueck, der ausgewiesene Zeitraum
            // muss das abdecken. Der leere Wert ist ausdruecklich zugelassen —
            // gibt es keinen datierten Eintrag, weist der Beleg allein den
            // Stichtag aus.
            //
            // Es sei denn, der Bearbeiter hat den Beginn von Hand gesetzt
            // (details.cumulativeFromManual): dann gilt seine Angabe — beim
            // ausgewiesenen Beginn UND beim eingefrorenen Leistungszeitraum.
            // Ohne diese Regel holte jeder Aufbau den ermittelten Wert zurueck.
            const manualStart = manualCumulativeStart(draft);
            const deliveryDateFrom = isCumulative
                ? (manualStart ?? built.cumulativeFrom ?? '')
                : draft.deliveryDateFrom;
            // Die alte Spanne raus, bevor die neue hineinkommt: ermittelt der
            // Aufbau keine (fortschreibend nichts Datiertes im Zeitraum), bliebe
            // sonst der Wert eines frueheren Aufbaus stehen.
            const { performedFrom: _alteSpanneVon, performedTo: _alteSpanneBis, ...detailsOhneSpanne } =
                draft.details ?? {};
            void _alteSpanneVon;
            void _alteSpanneBis;
            const saved = await updateInvoice(draft.id, {
                // Handgesetzte Positionsnummern und Texte ueberleben den
                // Aufbau: er holt sonst Nummer und Bezeichnung aus dem Projekt
                // zurueck und verwuerfe die Aenderung lautlos — dieser Aufbau
                // laeuft auch von selbst, etwa nach einer Zeitraumaenderung.
                lines: [...keepManualLineEdits(built.lines, draft.lines), ...kept],
                deliveryDateFrom,
                // Derselbe Stichtag, mit dem gebaut wurde. Beleg und Positionen
                // duerfen nicht auseinanderlaufen — genau daraus entstand die
                // Rechnung, die bis zum 31.07. abrechnen sollte und die
                // Augustbegehung mitnahm.
                deliveryDateTo: periodTo,
                details: {
                    ...detailsOhneSpanne,
                    // Der Aufbau liest das Protokoll frisch — der ausgewiesene
                    // Leistungszeitraum wird dabei mitgezogen.
                    ...performedDetails(withManualStart(built.performed, isCumulative ? manualStart : undefined)),
                    ancillaryPercent: effectiveAncillaryPercent,
                    // Leer heisst "kein Ausschluss" und wird nicht als leeres
                    // Feld mitgeschleppt.
                    excludedServiceIds: excludedServiceIds?.length ? excludedServiceIds : undefined,
                    // Die Wahl „ohne Datum mitnehmen" dieses Aufbaus — sie
                    // ersetzt die des vorigen, denn die Zeilen sind jetzt
                    // neu. Aus dem Ergebnis, nicht aus dem Haken: am Beleg
                    // steht, was wirklich in den Aufbau ging (Schatten am Tor).
                    includeUndated: built.includeUndated,
                },
            });
            setDraft(saved);
            onChange(saved);
            // Neuer Bezugspunkt: ab hier gilt dieser Stand als "unberuehrt".
            rebuildBaseline.current = lvFingerprint(saved.lines);
            setPositionsEdited(false);
            setPredictedBillingPeriod(built.cumulativeBillingPeriod);

            // Kumulativ nennt der Beleg keinen gewaehlten Zeitraum, sondern
            // einen Stichtag — die Meldung darf keinen behaupten.
            const period = isCumulative
                ? saved.deliveryDateTo
                    ? ` zum Leistungsstand ${de(saved.deliveryDateTo)}`
                    : ''
                : draft.deliveryDateFrom || draft.deliveryDateTo
                  ? ` im Zeitraum ${de(draft.deliveryDateFrom)} bis ${de(draft.deliveryDateTo)}`
                  : '';
            // Undatierte Mengen ausdruecklich benennen, auch wenn sie nicht
            // uebernommen wurden: eine fehlende Zeile sieht aus wie eine, die
            // es nicht gibt.
            const undatedHint =
                !rebuildUndated && built.undated.count > 0
                    ? ` ${built.undated.count} Position(en) (${formatCents(built.undated.netCents)}) haben keinen Protokolleintrag und gehören damit zu keinem Zeitraum.`
                    : '';
            setNotice(
                built.lines.length === 0
                    ? `Für dieses Projekt ist${period} nichts protokolliert — es wurde keine Position übernommen.${undatedHint}`
                    : `${built.lines.length} Position(en)${period} übernommen.` +
                      (built.outsidePeriodCount > 0
                          ? ` ${built.outsidePeriodCount} weitere offene Leistung(en) sind außerhalb des Zeitraums protokolliert und bleiben stehen.`
                          : '') +
                      undatedHint,
            );
            void runCheck();
        });

    // Ueber eine Ref, damit der Effekt unten nicht bei jedem Tastendruck neu
    // aufgesetzt wird und trotzdem immer den aktuellen Zeitraum sieht.
    const rebuildRef = useRef(rebuildPositions);
    useEffect(() => {
        rebuildRef.current = rebuildPositions;
    });

    /**
     * Wie eine herausgenommene Leistung heisst.
     *
     * Am Beleg steht nur die serviceId — der Titel gehoert ins
     * Leistungsverzeichnis und wuerde als Kopie am Beleg veralten. Gemerkt wird
     * er deshalb hier: beim Herausnehmen aus der Zeile, die gerade verschwindet,
     * und beim Oeffnen aus dem Projekt-LV, weil die Zeile dann nicht mehr da
     * ist. Bleibt beides erfolglos, nennt die Leiste die Leistung ohne Titel —
     * wieder aufnehmen laesst sie sich trotzdem.
     */
    const [excludedTitles, setExcludedTitles] = useState<Record<string, string>>({});

    const lineLabel = (line: InvoiceLine): string =>
        [line.lvPosition, line.title].filter(Boolean).join(' ').trim();

    useEffect(() => {
        if (!excludedKey || !draft.primaryProjectId) return;
        let cancelled = false;
        loadProjectById(draft.primaryProjectId)
            .then((project) => {
                if (cancelled || !project) return;
                const fromLv: Record<string, string> = {};
                for (const s of getNormalizedServices(project)) {
                    const label = [s.lvPosition, s.title].filter(Boolean).join(' ').trim();
                    if (label) fromLv[s.id] = label;
                }
                // Das Leistungsverzeichnis gewinnt: die gemerkte Zeile ist eine
                // Momentaufnahme, das LV die Quelle.
                setExcludedTitles((prev) => ({ ...prev, ...fromLv }));
            })
            // Nur die Beschriftung einer Liste — ein Fehlschlag darf die Maske
            // nicht mit einem Banner behelligen.
            .catch((e) => console.warn('Leistungsverzeichnis nicht ladbar', e));
        return () => {
            cancelled = true;
        };
    }, [excludedKey, draft.primaryProjectId]);

    /**
     * Leistungen, die eine Alt-Rechnung bereits gefordert hat (ALT-Buchungen im
     * Leistungsprotokoll des Projekts).
     *
     * Sie tragen quantityBilled 0 und saehen an der Zeile deshalb aus wie "noch
     * nie gefordert" — herausnehmen liesse sich eine solche Position dann,
     * obwohl ihr Alt-Abschlag im Summenblock weiter abgezogen wird. Genau das
     * ist der Basisverlust, den advanceInvoiceLogic spaeter als
     * advance_exceeds_rate_basis meldet.
     *
     * Nur kumulativ geladen: fortschreibend darf jede Zeile heraus, der Riegel
     * waere dort ohne Wirkung.
     *
     * Bis die Liste da ist, bleibt das Herausnehmen GESPERRT (altBookedLoaded):
     * eine leere Menge hiesse sonst "keine Alt-Buchung", und genau in diesem
     * Fenster liesse sich die Position herausnehmen, die der Riegel schuetzen
     * soll. Scheitert der Abruf, bleibt gesperrt — der Zeilenbau
     * (effectiveExcludedServiceIds in invoiceService) haelt die Regel ohnehin
     * ein zweites Mal und bindend.
     */
    const [altBookedServiceIds, setAltBookedServiceIds] = useState<ReadonlySet<string>>(new Set());
    const [altBookedLoaded, setAltBookedLoaded] = useState(false);
    useEffect(() => {
        if (!isCumulative || !draft.primaryProjectId) {
            setAltBookedServiceIds(new Set());
            // Fortschreibend gibt es nichts zu laden und nichts zu sperren.
            setAltBookedLoaded(true);
            return;
        }
        let cancelled = false;
        setAltBookedLoaded(false);
        loadProjectById(draft.primaryProjectId)
            .then((project) => {
                if (cancelled) return;
                // Kein Projekt heisst NICHT "keine Alt-Buchungen": gesperrt
                // bleiben wie im Fehlerfall, sonst verspraeche die Schaltflaeche
                // eine Pruefung, die nie stattgefunden hat.
                if (!project) return;
                const log = getServiceLog((project as any)?.data);
                setAltBookedServiceIds(altBookedServiceIdsOf(log));
                setAltBookedLoaded(true);
            })
            .catch((e) => console.warn('Alt-Buchungen nicht ladbar', e));
        return () => {
            cancelled = true;
        };
    }, [isCumulative, draft.primaryProjectId]);

    /** Herausnehmen ist erst entscheidbar, wenn die Alt-Buchungen bekannt sind. */
    const mayExcludeLine = (line: InvoiceLine): boolean =>
        altBookedLoaded && canExcludeLine(line, isCumulative, altBookedServiceIds);

    /**
     * Position aus dem Beleg herausnehmen — die Leistung bleibt offen.
     *
     * Beides in EINEM patch: der Vermerk am Beleg und die verschwundene Zeile
     * gehoeren zusammen. Zwei getrennte Aufrufe koennten als zwei Speicherungen
     * abgehen, und dazwischen stuende ein Beleg, der die Leistung ausschliesst
     * und trotzdem berechnet.
     */
    const excludeLine = (line: InvoiceLine) => {
        const serviceId = line.serviceId;
        if (!serviceId || !mayExcludeLine(line)) return;
        if (excludedServiceIds.includes(serviceId)) return;
        const label = lineLabel(line);
        if (label) setExcludedTitles((prev) => ({ ...prev, [serviceId]: label }));
        const nextLines = draft.lines.filter((l) => l.id !== line.id);
        patch({
            details: {
                ...draft.details,
                excludedServiceIds: [...excludedServiceIds, serviceId],
            },
            // Zeilen mitschicken: nur dann rechnet updateInvoice Summen und
            // Nebenkostenzuschlag neu.
            lines: nextLines,
        });
        // Kein Handeingriff: der naechste Aufbau erzeugt genau diesen Stand,
        // weil er den Ausschluss kennt. Ohne das Nachziehen hielte der Editor
        // die Positionen fuer von Hand geaendert und baute nach einer
        // Zeitraumaenderung nicht mehr von selbst auf.
        rebuildBaseline.current = lvFingerprint(nextLines);
    };

    /**
     * Herausgenommene Leistung wieder aufnehmen.
     *
     * Ueber den Positionsaufbau und nicht durch Wiedereinsetzen der alten
     * Zeile: die Menge kann sich seither geaendert haben, und eine zweite
     * Bildungsregel neben dem Aufbau saegte frueher oder spaeter etwas anderes
     * als er.
     */
    const includeLine = (serviceId: string) =>
        rebuildPositions(excludedServiceIds.filter((id) => id !== serviceId));

    /**
     * Positionen dem Zeitraum nachfuehren, ohne dass jemand daran denken muss.
     *
     * Ein Beleg, dessen Kopf einen Monat nennt und dessen Positionen alles
     * abrechnen, ist die gefaehrlichste Art von Fehler: er sieht richtig aus.
     *
     * Drei Bremsen:
     *  - Ein Datumsfeld meldet jede Zwischenstufe. "0002-05-01" ist keine
     *    Eingabe, sondern der Weg dorthin — unvollstaendige Daten werden
     *    uebergangen, der Rest wartet eine Sekunde.
     *  - Wurden die Positionen von Hand geaendert, laeuft nichts von selbst:
     *    der Aufbau ersetzt sie und wuerde die Aenderung verwerfen. Stattdessen
     *    erscheint der Hinweis samt Schaltflaeche.
     *  - Gebaut wird nur, wofuer noch nicht gebaut wurde.
     */
    useEffect(() => {
        if (!editable || !draft.primaryProjectId) return;
        if (rebuildKey === rebuiltFor.current) return;

        if (!datumVollstaendig(draft.deliveryDateFrom) || !datumVollstaendig(draft.deliveryDateTo))
            return;

        if (lvFingerprint(draft.lines) !== rebuildBaseline.current) {
            setPositionsEdited(true);
            return;
        }

        const t = setTimeout(() => void rebuildRef.current(), DATE_DEBOUNCE_MS);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rebuildKey, editable, draft.primaryProjectId, draft.lines]);

    /**
     * Projekt als eines ohne Berichtsfuehrung kennzeichnen und sofort erneut
     * pruefen, damit der Befund vor den Augen des Nutzers verschwindet und er
     * nicht raten muss, ob die Aktion gewirkt hat.
     */
    const exemptFromReports = (exempt: boolean) =>
        run('exempt', async () => {
            if (!draft.primaryProjectId) {
                throw new Error('Dem Beleg ist kein Projekt zugeordnet.');
            }
            await setProjectReportExemption(draft.primaryProjectId, exempt);
            setNotice(
                exempt
                    ? 'Das Projekt ist dauerhaft als Projekt ohne Berichtsführung gekennzeichnet. Die berichtsbezogenen Prüfungen entfallen dort ab sofort — alle übrigen bleiben.'
                    : 'Die Markierung ist aufgehoben. Berichtsarchiv und Begehungszähler werden für dieses Projekt wieder geprüft.',
            );
            await runCheck();
        });

    /**
     * Projektvorgabe des Sicherheitseinbehalts schreiben. Leer entfernt den
     * Eintrag (setProjectRetentionPercent erklaert, warum keine 0). Belege mit
     * eigenem Satz bleiben unberuehrt; ein offener Entwurf, der noch keinen
     * Satz traegt, uebernimmt die neue Vorgabe ueber den Vorbelegungs-Effekt.
     */
    const saveProjectRetention = (raw: string) =>
        run('projectRetention', async () => {
            if (!draft.primaryProjectId) {
                throw new Error('Dem Beleg ist kein Projekt zugeordnet.');
            }
            const trimmed = raw.trim().replace(',', '.');
            const value = Number(trimmed);
            const nextPercent =
                trimmed === '' || !Number.isFinite(value) || value <= 0
                    ? undefined
                    : Math.min(100, value);
            await saveProjectRetentionPercent(draft.primaryProjectId, nextPercent);
            setProjectRetentionPercent(nextPercent);
            setProjectRetentionDraft(null);
            setNotice(
                nextPercent === undefined
                    ? 'Das Projekt hat keine Vorgabe für den Sicherheitseinbehalt mehr. Neue Belege starten ohne Einbehalt; Belege mit eigenem Satz bleiben unverändert.'
                    : `Neue Belege dieses Projekts werden mit ${nextPercent} % Sicherheitseinbehalt vorbelegt. Belege mit eigenem Satz bleiben unverändert.`,
            );
        });

    /** Zum Projekt springen. Der Rueckweg fuehrt ueber die Rechnungsliste. */
    const openProject = () =>
        run('project', async () => {
            if (!draft.primaryProjectId) {
                throw new Error('Dem Beleg ist kein Projekt zugeordnet.');
            }
            if (!onOpenProject) return;
            // Ausstehende Eingaben vorher wegschreiben: der Wechsel unmountet
            // den Editor, und der Debounce-Timer feuert dann ins Leere.
            flushSave();
            const project = await loadProjectById(draft.primaryProjectId);
            if (!project) throw new Error('Das Projekt konnte nicht geladen werden.');
            onOpenProject(project);
        });

    /**
     * Leistungsnachweis erzeugen und herunterladen.
     *
     * Getrennt vom Anhaengen: manche Auftraggeber wollen ihn als eigene Datei,
     * andere hinter der Rechnung. Beide Wege erzeugen dasselbe Dokument fuer
     * denselben Zeitraum.
     */
    const downloadProof = () =>
        run('proof', async () => {
            const bytes = await renderPerformanceProofForInvoice(draft, userProfile);
            if (!bytes) {
                throw new Error(
                    'Dieser Beleg hat keinen Projektbezug — es gibt keine Leistungen nachzuweisen.',
                );
            }
            const blob = new Blob([bytes], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Leistungsnachweis_${previewFileName}.pdf`;
            // (Bei hochgeladenem eigenen Nachweis liefert der Render-Pfad
            // dessen Bytes — Download und Anlage bleiben identisch.)
            // Firefox und Safari brechen ab, wenn das Element nicht im Dokument
            // haengt oder die Object-URL im selben Tick freigegeben wird.
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 10_000);
        });

    // ─── Eigener Leistungsnachweis (Upload) ────────────────────────────────
    // Ersetzt die generierte Fassung — z. B. die unterschriebene, die der
    // Auftraggeber zurueckschickt. Datei liegt im Beleg-Bucket, der Beleg
    // traegt nur den Pfad; der Render-Pfad (renderPerformanceProofForInvoice)
    // bevorzugt sie automatisch in Vorschau, Download und Archiv.
    const proofFileInputRef = useRef<HTMLInputElement>(null);

    // ─── Zuschnitt des Nachweises (Zeitraum + Positionen) ──────────────────
    // Die Liste der Positionen wird nachgeladen, sobald die Anlage ueberhaupt
    // angehakt ist: sie braucht das Projekt aus der Cloud, und der Beleg
    // haelt nur dessen Kennung. Fehlt sie, bleibt die Abwahl aus — lieber
    // keine Liste als eine geratene.
    const [proofServices, setProofServices] = useState<PerformanceProofServiceRef[] | null>(null);
    const [proofServicesBusy, setProofServicesBusy] = useState(false);
    const proofRange = proofFilterRange(draft);
    // Als Einzelwerte fuer die Abhaengigkeitsliste des Nachladens unten:
    // proofRange ist bei jedem Rendern ein neues Objekt.
    const proofProjectId = draft.primaryProjectId;
    const proofRangeStart = proofRange.start;
    const proofRangeEnd = proofRange.end;
    const excludedProofIds = proofExcludedServiceIds(draft);
    // Ein hochgeladener Nachweis schlaegt die generierte Fassung — dann greift
    // der Zuschnitt an nichts mehr, und die Maske sagt das, statt Bedienelemente
    // ohne Wirkung anzubieten.
    const proofTrimActive =
        !!draft.details?.attachPerformanceProof &&
        !!draft.primaryProjectId &&
        !draft.details?.customProofPath?.trim();

    useEffect(() => {
        if (!proofTrimActive) {
            setProofServices(null);
            return;
        }
        let active = true;
        setProofServicesBusy(true);
        void (async () => {
            try {
                // Nur die aufgeloesten Werte hereingeben, nicht den ganzen
                // draft: der aendert sich bei jedem Tastendruck im Beleg und
                // zoege dieses Nachladen samt Projektabruf jedes Mal mit.
                const list = await listPerformanceProofServices({
                    primaryProjectId: proofProjectId,
                    deliveryDateFrom: proofRangeStart ?? undefined,
                    deliveryDateTo: proofRangeEnd ?? undefined,
                });
                if (active) setProofServices(list);
            } catch (e) {
                console.warn('Positionen des Leistungsnachweises konnten nicht geladen werden', e);
                if (active) setProofServices(null);
            } finally {
                if (active) setProofServicesBusy(false);
            }
        })();
        return () => {
            active = false;
        };
        // Der Zeitraum steht bewusst mit drin: er bestimmt, wie viele Eintraege
        // je Position gezaehlt werden, und die Zahl steht in der Liste.
    }, [proofTrimActive, proofProjectId, proofRangeStart, proofRangeEnd]);

    /** Eine Position im Nachweis zeigen oder auslassen. */
    const toggleProofService = (serviceId: string) => {
        const next = excludedProofIds.includes(serviceId)
            ? excludedProofIds.filter((id) => id !== serviceId)
            : [...excludedProofIds, serviceId];
        patch({
            details: {
                ...draft.details,
                // Leere Liste NICHT speichern: `undefined` ist der
                // unveraenderte Beleg, `[]` waere eine getroffene Entscheidung,
                // die genauso aussieht und jeden spaeteren Vergleich stoert.
                proofExcludedServiceIds: next.length > 0 ? next : undefined,
            },
        });
    };

    const handleProofUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (!(file.type === 'application/pdf' || /\.pdf$/i.test(file.name))) {
            setError('Nur PDF-Dateien: der Nachweis wird hinten an das Rechnungs-PDF angehängt.');
            return;
        }
        void run('proofUpload', async () => {
            const { path, fileName } = await uploadCustomPerformanceProof(draft.id, file, file.name);
            // Gecachte generierte Anlage verwerfen — ab jetzt gilt der Upload.
            proofAnnexCacheRef.current = null;
            patch({
                details: {
                    ...draft.details,
                    customProofPath: path,
                    customProofFileName: fileName,
                    // Wer eine Datei hochlaedt, will sie anhaengen.
                    attachPerformanceProof: true,
                },
            });
        });
    };
    const removeCustomProof = () => {
        // Nur den Verweis am Beleg entfernen; die Datei bleibt im Bucket
        // liegen (uploadCustomPerformanceProof erklaert, warum).
        proofAnnexCacheRef.current = null;
        patch({
            details: {
                ...draft.details,
                customProofPath: undefined,
                customProofFileName: undefined,
            },
        });
    };

    const issueCtx = (): IssueContext => ({
        actorId: userProfile?.id ?? '',
        userName: pdfUserName,
        config: invoiceConfig,
        // E-Mail und Telefon MIT einfrieren: sie stehen ueber die
        // Spalten-Fusszeile (composeInvoiceFooter) auf dem Papier, kamen im
        // companySnapshot aber nie an. Der XML-Snapshot fiel damit immer auf
        // die LEBENDE invoiceConfig zurueck — eine spaetere Nummernaenderung
        // haette alte Belege im XML veraendert, waehrend das archivierte PDF
        // die alte trug. Gleiche Quelle wie der Druck: invoiceConfig.
        company: {
            name: company.name ?? '',
            address: company.address ?? '',
            email: invoiceConfig.email,
            phone: invoiceConfig.phone,
        },
        renderPdf: async (inv, cust) => {
            // Die Anlage muss VOR dem Hashen dabei sein: archiviert wird genau
            // das Dokument, das der Kunde bekommt. Ein spaeter angeklebter
            // Nachweis liesse die Datei ihrem eigenen sha256 widersprechen.
            const annex = inv.details?.attachPerformanceProof
                ? await renderPerformanceProofForInvoice(inv, userProfile)
                : null;
            return renderInvoicePdfBytes(
                {
                    invoice: inv,
                    customer: cust,
                    company: companyForPdf,
                    config: invoiceConfig,
                    userName: pdfUserName,
                },
                annex,
            );
        },
        // Der Anhang aendert das archivierte Dokument — eine eigene Fassung,
        // damit spaeter nachvollziehbar bleibt, wie es entstanden ist.
        generatorVersion: 'invoice-2',
        // Frischer Lauf beim Ausstellen. Das Ergebnis im Panel stammt vom
        // Oeffnen des Belegs und kann Stunden alt sein — es taugt zur Anzeige,
        // nicht als Freigabe.
        runProjectCheck: async (inv) =>
            mergeCheckResults(await checkInvoiceProjects(inv, invoiceConfig.projectCheck, today)),
        projectCheckAcknowledgement: acknowledgement.trim() || undefined,
    });

    const handleIssue = () =>
        run('issue', async () => {
            if (!customer) throw new Error('Der Kunde konnte nicht geladen werden.');
            const issued = await issueInvoice(draft.id, customer, issueCtx());
            setIssueOpen(false);
            setDraft(issued);
            onChange(issued);
            setNotice(
                `Rechnung ${issued.invoiceNumber} wurde ausgestellt und ist ab jetzt festgeschrieben.`,
            );
            // Nur ein Hinweis — ein Fehler hier darf die gelungene Ausstellung
            // nicht als fehlgeschlagen erscheinen lassen.
            setCompetingDrafts(await fetchCompetingDrafts(issued).catch(() => []));
        });

    /**
     * Loescht die konkurrierenden Entwuerfe — nur die im Status DRAFT, denn
     * nur dort laesst der Datenbank-Trigger das Loeschen zu. Belege in Pruefung
     * oder Freigabe bleiben stehen und werden im Hinweis benannt.
     */
    const handleCleanupDrafts = () =>
        run('cleanupDrafts', async () => {
            const deletable = competingDrafts.filter((c) => c.status === 'DRAFT');
            let deleted = 0;
            let firstError: unknown;
            for (const c of deletable) {
                try {
                    await deleteInvoice(c.id);
                } catch (e) {
                    firstError = firstError ?? e;
                    continue;
                }
                deleted += 1;
                // Sofort aus dem Zustand nehmen, nicht erst nach der Schleife:
                // bricht ein spaeterer Loeschversuch ab, zeigte die Liste sonst
                // Belege an, die es nicht mehr gibt. Funktional, damit kein
                // veralteter Schleifen-Stand zurueckgeschrieben wird.
                setCompetingDrafts((prev) => prev.filter((p) => p.id !== c.id));
            }
            const failed = deletable.length - deleted;
            if (failed > 0) {
                const reason = firstError instanceof Error ? ` ${firstError.message}` : '';
                throw new Error(
                    `${deleted} von ${deletable.length} Entwürfen gelöscht — ${failed} nicht.${reason}`,
                );
            }
            setNotice(
                deleted === 1
                    ? 'Der konkurrierende Entwurf wurde gelöscht.'
                    : `${deleted} konkurrierende Entwürfe wurden gelöscht.`,
            );
        });

    const handleCancel = () =>
        run('cancel', async () => {
            if (!customer) throw new Error('Der Kunde konnte nicht geladen werden.');
            if (!cancelReason.trim()) throw new Error('Bitte einen Stornogrund angeben.');
            const storno = await cancelInvoice(
                draft,
                cancelReason.trim(),
                customer,
                issueCtx(),
                today,
            );
            setCancelOpen(false);
            onBack(
                `Stornorechnung ${storno.invoiceNumber} wurde erstellt. Die ursprüngliche Rechnung bleibt als Beleg erhalten.`,
            );
        });

    const handleVerify = () =>
        run('verify', async () => {
            if (!draft.snapshotId) throw new Error('Zu dieser Rechnung gibt es kein Archiv.');
            const res = await verifySnapshotHash(draft.snapshotId);
            setNotice(
                res.ok
                    ? 'Die archivierte PDF ist unverändert — der Hash stimmt.'
                    : `ACHTUNG: Die archivierte PDF weicht vom gespeicherten Hash ab. Erwartet ${res.expected}, gefunden ${res.actual}.`,
            );
        });

    const openPdf = () =>
        run('open', async () => {
            if (!draft.pdfUrl) throw new Error('Zu dieser Rechnung ist keine PDF archiviert.');
            const url = await getInvoicePdfUrl(draft.pdfUrl);
            window.open(url, '_blank', 'noopener');
        });

    /**
     * XRechnung als UBL 2.1 herunterladen.
     *
     * Erzeugt aus dem FESTGESCHRIEBENEN Beleg: Kunden- und Firmenangaben
     * stammen aus den Snapshots, damit das XML dieselben Daten traegt wie das
     * archivierte PDF. Ein spaeter geaenderter Kundenname darf das XML einer
     * alten Rechnung nicht veraendern.
     */
    const downloadXml = () =>
        run('xml', async () => {
            if (!draft.invoiceNumber) {
                throw new Error('Eine E-Rechnung entsteht erst mit der Ausstellung.');
            }
            const violations = validateEn16931(
                draft,
                // Dieselbe Quelle, die der Kopf zeigt und der Bearbeiten-Dialog
                // zusagt — nicht eine zweite, gleichlautende Formel daneben.
                customerForDocument ?? customer,
                draft.companySnapshot,
                invoiceConfig,
            );
            const blockers = violations.filter((v) => v.severity === 'error');

            const xml = buildUblInvoice(
                draft,
                customerForDocument ?? customer,
                draft.companySnapshot,
                invoiceConfig,
            );
            const blob = new Blob([xml], { type: 'application/xml;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${draft.invoiceNumber}.xml`;
            // Firefox und Safari brechen den Download ab, wenn das Element nicht im
            // Dokument haengt oder die Object-URL im selben Tick freigegeben wird —
            // die Erfolgsmeldung erschiene trotzdem.
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 10_000);

            // Die Datei entsteht auch bei Regelverstoessen — sie ist dann immer
            // noch nuetzlich. Verschwiegen wird der Befund aber nicht: ein
            // Empfaenger mit strenger Pruefung lehnt sie ab.
            setNotice(
                blockers.length
                    ? `XML erzeugt, ABER ${blockers.length} EN-16931-Regel(n) verletzt: ${blockers
                          .slice(0, 3)
                          .map((v) => `${v.code} ${v.message}`)
                          .join(' · ')}${blockers.length > 3 ? ' …' : ''}`
                    : 'XRechnung erzeugt — alle geprüften EN-16931-Regeln erfüllt.',
            );
        });

    const handleCorrection = (correctedLines: InvoiceLine[], reason: string) =>
        run('correct', async () => {
            if (!customer) throw new Error('Der Kunde konnte nicht geladen werden.');
            const corrected = await correctInvoice(
                draft,
                correctedLines,
                reason,
                customer,
                issueCtx(),
                today,
            );
            setCorrectionOpen(false);
            onBack(
                `Korrekturrechnung ${corrected.invoiceNumber} wurde ausgestellt. Sie enthält die Differenz; die ursprüngliche Rechnung bleibt als Beleg bestehen.`,
            );
        });

    const handleCredit = (creditLines: InvoiceLine[], reason: string) =>
        run('credit', async () => {
            if (!customer) throw new Error('Der Kunde konnte nicht geladen werden.');
            const credit = await createCommercialCredit(
                draft,
                creditLines,
                reason,
                customer,
                issueCtx(),
                today,
            );
            setCorrectionOpen(false);
            onBack(`Gutschrift ${credit.invoiceNumber} wurde ausgestellt.`);
        });

    /** Warum das Stornieren gesperrt ist — oder null, wenn es erlaubt ist. */
    const cancelBlockedReason = useMemo(() => {
        const blockers = validateCancellation(draft).filter((m) => m.level === 'error');
        return blockers.length ? blockers[0].message : null;
    }, [draft]);

    /**
     * Projekte des Belegs mit Nummer und Name.
     *
     * Dieselbe Ableitung wie in Belegliste und PDF — wer hier eine Rechnung
     * schreibt, muss sehen, was auf dem Papier landen wird, und nicht eine
     * zweite, freundlichere Fassung davon.
     *
     * Die Abhaengigkeiten nennen die tatsaechlich gelesenen Felder statt draft
     * als Ganzes: der Entwurf bekommt bei JEDEM Tastendruck in irgendeinem Feld
     * ein neues Objekt, und die Ableitung liefe dann samt Projektsuche erneut,
     * obwohl der Projektbezug sich nur an diesen drei Stellen aendern kann.
     */
    const projectRefs = useMemo(
        () =>
            invoiceProjectRefs(draft, (id) => {
                const p = projects?.find((row) => row.id === id);
                return p ? { id, number: p.project_number, name: p.name } : undefined;
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [draft.primaryProjectId, draft.lines, draft.details?.projects, projects],
    );

    /**
     * Der Name, den die Projektzeile des Belegs ohne eigenen Anzeigenamen
     * druckt — Platzhalter des Feldes darunter.
     *
     * Leer bei keinem oder mehr als einem Projekt: dann druckt der Beleg gar
     * keine Projektzeile (invoiceHeading), und ein Feld dafuer waere ein
     * Versprechen, das der Andruck nicht einloest.
     */
    const printedProjectName =
        projectRefs.length === 1 ? formatProjectRefNameOnly(projectRefs[0]) : '';

    const serviceLines = draft.lines.filter((l) => l.kind === 'SERVICE');

    return (
        <div className="flex h-full flex-col">
            <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 bg-white px-4 py-3">
                {/* Ohne Wrapper ginge das Klick-Event als Erfolgsmeldung an die Liste. */}
                <button
                    onClick={() => onBack()}
                    className="text-slate-500 hover:text-slate-800"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="min-w-0">
                    <h1 className="truncate font-mono font-semibold text-slate-800">
                        {draft.invoiceNumber || 'Rechnungsentwurf'}
                    </h1>
                    {/* Der Name kommt aus derselben Quelle wie das PDF darunter
                        (Snapshot beim festgeschriebenen Beleg, sonst der lebende
                        Kunde). Der am Beleg mitgelesene Join-Name greift nur,
                        wenn der Kunde gar nicht geladen ist — er stand hier
                        bisher zuerst und blieb nach einer Umbenennung stehen,
                        waehrend das Dokument schon den neuen Namen druckte. */}
                    <div className="flex items-center gap-1.5">
                        <p className="truncate text-xs text-slate-500">
                            {documentCustomerName(
                                customerWithOverride,
                                draft.customerSnapshot,
                                customerFrozen,
                                draft.customerName,
                            )}
                        </p>
                        {/* Eine Abweichung, die man nicht sieht, ist eine Falle:
                            der Beleg druckt einen anderen Empfaenger als der
                            Kundenstamm, und ohne Kennzeichnung sucht der
                            Bearbeiter den Fehler spaeter im Stamm. */}
                        {overrideActive && (
                            <span
                                title={`Abweichende Kundenangaben nur auf diesem Beleg: ${customerOverrideSummary(customerOverride)}. Der Kundenstamm bleibt unverändert.`}
                                className="shrink-0 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700"
                            >
                                Abweichend
                            </span>
                        )}
                        {canEditCustomer && (
                            <button
                                type="button"
                                onClick={() => setCustomerEditOpen(true)}
                                title="Kundendaten bearbeiten (Anschrift, Name, Kontakt) — wahlweise im Kundenstamm oder nur für diesen Beleg"
                                className="shrink-0 rounded p-0.5 text-slate-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
                            >
                                <PencilLine className="h-3.5 w-3.5" />
                            </button>
                        )}
                    </div>
                </div>
                {/* Das Projekt gehoert in den Kopf und nicht in ein Feld weiter
                    unten: wer eine Rechnung schreibt, muss ohne Suchen sehen,
                    WELCHE Baustelle er gerade abrechnet — und zwar mit Nummer
                    UND Name, weil die Nummer der Schluessel im Betrieb ist und
                    der Name das Einzige, woran der Auftraggeber sie erkennt.
                    Dieselbe Angabe druckt der Beleg. */}
                {projectRefs.length > 0 && (
                    <div className="min-w-0 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5">
                        <div className="text-xs font-medium uppercase tracking-wider text-slate-500">
                            {projectRefs.length === 1
                                ? 'Projekt'
                                : `${projectRefs.length} Projekte`}
                        </div>
                        {/* Bei einer Sammelrechnung ALLE nennen statt einer
                            Zahl: hier wird gearbeitet, und die Frage ist welche
                            Projekte drinstecken, nicht wie viele. */}
                        <div className="flex flex-wrap items-center gap-x-3 text-sm font-semibold text-slate-800">
                            {projectRefs.map((ref, i) => (
                                <span key={ref.id ?? i} className="truncate">
                                    {formatProjectRef(ref) || '—'}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
                <InvoiceStatusBadge invoice={draft} today={today} />
                {/* Wie DIESER Beleg gerechnet ist. Im Kopf neben Status und
                    Festschreibung, weil es eine Belegeigenschaft ist und keine
                    Einstellung: aendern laesst sie sich hier nicht mehr — sie
                    entsteht beim Anlegen und bestimmt danach Positionsaufbau
                    und Abzug.

                    Anders als in der Liste steht sie hier IMMER, auch als
                    „entfällt": im Kopf eines einzelnen Belegs ist eine fehlende
                    Angabe nicht von einer vergessenen zu unterscheiden, und
                    beim Abschlag oder der Gutschrift ist genau das die
                    Auskunft — sie rechnen weder kumulativ noch fortschreibend. */}
                <BillingModeChip invoice={draft} withLabel showWhenUnstated />
                {!statusOffen && (
                    <span className="inline-flex items-center gap-1 rounded border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-600">
                        <Lock className="w-3 h-3" /> festgeschrieben
                    </span>
                )}
                {statusOffen && !editable && (
                    <span
                        title={darf.bearbeiten.hinweis}
                        className="inline-flex items-center gap-1 rounded border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-600"
                    >
                        <Lock className="w-3 h-3" /> nur lesen
                    </span>
                )}

                <div className="ml-auto flex flex-wrap items-center gap-2">
                    {draft.status === 'DRAFT' && (
                        <Button
                            variant="secondary"
                            onClick={() =>
                                run('review', async () => {
                                    await submitForReview(draft);
                                    const next = { ...draft, status: 'READY_FOR_REVIEW' as const };
                                    setDraft(next);
                                    onChange(next);
                                })
                            }
                            disabled={busy != null || !darf.zurPruefung.erlaubt}
                            title={darf.zurPruefung.hinweis}
                        >
                            Zur Prüfung
                        </Button>
                    )}
                    {(draft.status === 'DRAFT' || draft.status === 'READY_FOR_REVIEW') && (
                        <Button
                            variant="secondary"
                            onClick={() =>
                                run('approve', async () => {
                                    await approveInvoice(draft, userProfile?.id ?? '');
                                    const next = { ...draft, status: 'APPROVED' as const };
                                    setDraft(next);
                                    onChange(next);
                                })
                            }
                            disabled={busy != null || !darf.freigeben.erlaubt}
                            title={darf.freigeben.hinweis}
                        >
                            Freigeben
                        </Button>
                    )}
                    {/* Der Rueckweg stand im Zustandsmodell (APPROVED -> DRAFT),
                        hatte aber keinen Knopf: wer einen Beleg pruefte und
                        etwas fand, konnte ihn nur freigeben oder liegen lassen. */}
                    {(draft.status === 'READY_FOR_REVIEW' || draft.status === 'APPROVED') &&
                        (rejectOpen ? (
                            <div className="flex items-center gap-1">
                                <input
                                    autoFocus
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                    placeholder="Grund der Zurückweisung"
                                    className="w-56 rounded-md border border-slate-300 px-2 py-1 text-sm"
                                />
                                <Button
                                    variant="secondary"
                                    disabled={busy != null || !rejectReason.trim()}
                                    onClick={() =>
                                        run('reject', async () => {
                                            await rejectInvoice(
                                                draft,
                                                userProfile?.id ?? '',
                                                rejectReason,
                                            );
                                            setRejectOpen(false);
                                            setRejectReason('');
                                            // Frisch nachladen: Status, Freigabe
                                            // und details kommen aus der
                                            // Datenbank, nicht aus dem Browser.
                                            await reloadInvoice();
                                            setNotice('Der Beleg ist zurück in der Bearbeitung.');
                                        })
                                    }
                                >
                                    {busy === 'reject' && (
                                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                                    )}
                                    Zurückweisen
                                </Button>
                                <button
                                    type="button"
                                    onClick={() => setRejectOpen(false)}
                                    className="px-1 text-xs text-slate-500 hover:underline"
                                >
                                    Abbrechen
                                </button>
                            </div>
                        ) : (
                            <Button
                                variant="secondary"
                                onClick={() => setRejectOpen(true)}
                                disabled={busy != null || !darf.zurueckweisen.erlaubt}
                                title={darf.zurueckweisen.hinweis}
                            >
                                Zurückweisen
                            </Button>
                        ))}
                    {draft.status === 'APPROVED' && (
                        <Button
                            onClick={() => {
                                // Der Dialog zeigt Fehler beim Ausstellen — einer
                                // aus einer frueheren Aktion gehoert nicht hinein.
                                setError(null);
                                setTorSperre(null);
                                setIssueOpen(true);
                            }}
                            disabled={busy != null || blocked || !darf.ausstellen.erlaubt}
                            title={darf.ausstellen.hinweis}
                        >
                            {busy === 'issue' ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <CheckCircle2 className="w-4 h-4" />
                            )}
                            Ausstellen
                        </Button>
                    )}
                    {draft.pdfUrl && (
                        <Button variant="secondary" onClick={openPdf} disabled={busy != null}>
                            <ExternalLink className="w-4 h-4" /> PDF
                        </Button>
                    )}
                    {draft.snapshotId && (
                        <Button variant="secondary" onClick={handleVerify} disabled={busy != null}>
                            <ShieldCheck className="w-4 h-4" /> Integrität prüfen
                        </Button>
                    )}
                    {draft.invoiceNumber && (
                        <Button
                            variant="secondary"
                            onClick={downloadXml}
                            disabled={busy != null}
                            title="XRechnung (UBL 2.1) für den elektronischen Rechnungsversand"
                        >
                            <FileCode className="w-4 h-4" /> E-Rechnung
                        </Button>
                    )}
                    {draft.invoiceNumber && draft.status !== 'CANCELLED' && (
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setError(null);
                                setTorSperre(null);
                                setCorrectionOpen(true);
                            }}
                            disabled={busy != null || !darf.korrigieren.erlaubt}
                            title={darf.korrigieren.hinweis ?? 'Korrekturrechnung oder Gutschrift zu diesem Beleg'}
                        >
                            <PencilLine className="w-4 h-4" /> Korrigieren
                        </Button>
                    )}
                    {draft.invoiceNumber && (
                        <Button
                            variant="danger"
                            onClick={() => {
                                setError(null);
                                setTorSperre(null);
                                setCancelOpen(true);
                            }}
                            // Der Grund steht im Tooltip, statt den Knopf
                            // wortlos verschwinden zu lassen — wer stornieren
                            // will und nichts findet, sucht sonst lange.
                            disabled={busy != null || cancelBlockedReason != null || !darf.stornieren.erlaubt}
                            title={darf.stornieren.hinweis ?? cancelBlockedReason ?? 'Beleg stornieren'}
                        >
                            <Ban className="w-4 h-4" /> Stornieren
                        </Button>
                    )}
                    {draft.status === 'DRAFT' && (
                        <Button
                            variant="danger"
                            onClick={() =>
                                run('delete', async () => {
                                    await deleteInvoice(draft.id);
                                    onBack();
                                })
                            }
                            disabled={busy != null || !darf.bearbeiten.erlaubt}
                            title={darf.bearbeiten.hinweis}
                        >
                            Entwurf löschen
                        </Button>
                    )}
                </div>
            </div>

            {(error || notice) && (
                <div className="space-y-2 px-4 pt-3">
                    {error && (
                        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                            {/* pre-line: die Sperre am Tor bringt ihre Positionen
                                als eigene Zeilen mit. */}
                            <span className="flex-1 whitespace-pre-line">{error}</span>
                            <button onClick={() => setError(null)}>✕</button>
                        </div>
                    )}
                    {notice && (
                        <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                            <span className="flex-1">{notice}</span>
                            <button onClick={() => setNotice(null)}>✕</button>
                        </div>
                    )}
                </div>
            )}

            {competingDrafts.length > 0 && (
                <div className="px-4 pt-3">
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                        <div className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                            <div className="flex-1 space-y-1">
                                <p className="font-semibold">
                                    {competingDrafts.length === 1
                                        ? 'Ein weiterer offener Beleg beansprucht dieselben Positionen.'
                                        : `${competingDrafts.length} weitere offene Belege beanspruchen dieselben Positionen.`}
                                </p>
                                <p>
                                    Diese Belege lassen sich nicht mehr ausstellen — die Mengen
                                    sind mit dieser Rechnung abgerechnet.
                                </p>
                                <ul className="list-disc pl-4">
                                    {competingDrafts.map((c) => (
                                        <li key={c.id}>
                                            {DOCUMENT_TYPE_LABEL[c.documentType]} vom{' '}
                                            {de(c.invoiceDate)} über{' '}
                                            <span className="tabular-nums">{formatCents(c.totals.grossTotalCents)}</span>
                                            {c.status !== 'DRAFT' &&
                                                ' — in Prüfung oder freigegeben, zum Löschen zuerst zurückweisen'}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <button onClick={() => setCompetingDrafts([])}>✕</button>
                        </div>
                        {competingDrafts.some((c) => c.status === 'DRAFT') && (
                            <div className="mt-2 pl-6">
                                <Button
                                    variant="danger"
                                    onClick={handleCleanupDrafts}
                                    disabled={busy != null || !darf.bearbeiten.erlaubt}
                                    title={darf.bearbeiten.hinweis}
                                >
                                    {competingDrafts.filter((c) => c.status === 'DRAFT').length ===
                                    1
                                        ? 'Entwurf löschen'
                                        : `${competingDrafts.filter((c) => c.status === 'DRAFT').length} Entwürfe löschen`}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="grid flex-1 grid-cols-1 gap-4 overflow-auto p-4 lg:grid-cols-2">
                <div className="space-y-4">
                    {shownIssues.length > 0 && (
                        <div className="rounded-lg border border-slate-200 bg-white p-3">
                            <h2 className="mb-2 text-sm font-semibold text-slate-700">
                                Vor der Ausstellung zu klären
                            </h2>
                            <ul className="space-y-1.5 text-sm">
                                {shownIssues.map((m, i) => (
                                    <li key={`${m.code}-${i}`} className="flex items-start gap-2">
                                        <span
                                            className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${
                                                m.level === 'error' ? 'bg-red-500' : 'bg-amber-500'
                                            }`}
                                        />
                                        <span
                                            className={
                                                m.level === 'error'
                                                    ? 'text-red-700'
                                                    : 'text-amber-800'
                                            }
                                        >
                                            {m.message}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            {blocked && (
                                <p className="mt-2 text-xs text-red-600">
                                    Solange rote Punkte offen sind, lässt sich der Beleg nicht
                                    ausstellen.
                                </p>
                            )}
                        </div>
                    )}

                    <div className="rounded-lg border border-slate-200 bg-white p-3">
                        <h2 className="mb-2 text-sm font-semibold text-slate-700">Belegdaten</h2>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            {/* Eigene Ueberschrift statt der Vorgabe. Ueber beide
                                Spalten, weil eine Betreffzeile in eine halbe Spalte
                                nicht lesbar hineinpasst. Leer heisst Vorgabe —
                                nicht "keine Ueberschrift". */}
                            {/* Bewusst OHNE Field-Wrapper — derselbe Grund wie
                                bei der Kostenstelle: der ist ein <label>, und
                                ein Klick auf den Vorlagen-Knopf darin landete
                                im Eingabefeld statt im Menue. */}
                            <div className="col-span-2">
                                <span className="mb-0.5 block text-xs font-medium text-slate-600">
                                    Überschrift
                                </span>
                                <div className="flex items-start gap-2">
                                    <input
                                        type="text"
                                        value={draft.details?.headerText ?? ''}
                                        disabled={!editable}
                                        placeholder={headingFallback}
                                        onChange={(e) =>
                                            patch({
                                                details: {
                                                    ...draft.details,
                                                    headerText: e.target.value || undefined,
                                                },
                                            })
                                        }
                                        className="w-full rounded-md border border-slate-300 px-2 py-1 disabled:bg-slate-50"
                                    />
                                    <HeaderTextPicker
                                        options={headerOptions}
                                        currentText={draft.details?.headerText ?? ''}
                                        disabled={!editable}
                                        busy={headerTemplateBusy}
                                        onPick={(text) =>
                                            patch({
                                                details: {
                                                    ...draft.details,
                                                    headerText: text || undefined,
                                                },
                                            })
                                        }
                                        onSaveCurrent={() => {
                                            const next = addHeaderTemplate(
                                                headerTemplates,
                                                draft.details?.headerText ?? '',
                                            );
                                            // null heisst "steht schon da oder
                                            // ist leer" — dann gibt es nichts
                                            // zu schreiben.
                                            if (next) void persistHeaderTemplates(next);
                                        }}
                                        onDelete={(text) =>
                                            void persistHeaderTemplates(
                                                removeHeaderTemplate(headerTemplates, text),
                                            )
                                        }
                                    />
                                </div>
                                <div className="mt-1 flex flex-wrap items-center gap-2">
                                    <span className="text-xs text-slate-500">
                                        Leer lassen für „{headingFallback}“
                                        {invoiceConfig.defaultHeaderText?.trim()
                                            ? ' (Standard-Überschrift aus den Einstellungen)'
                                            : ''}
                                        . Über „Vorlagen“ auswählen oder eigenen Text
                                        eintragen und speichern. Der Beleg trägt genau EINE
                                        Titelzeile — Belegart und Nummer stehen im rechten
                                        Kopfblock. Solange die Rechnung Entwurf ist, hängt
                                        „(Entwurf)“ am Titel.
                                    </span>
                                    {editable && quoteHeadline && (
                                        <button
                                            type="button"
                                            onClick={() =>
                                                patch({
                                                    details: {
                                                        ...draft.details,
                                                        headerText: quoteHeadline,
                                                    },
                                                })
                                            }
                                            className="rounded-md border border-slate-300 bg-white px-2 py-0.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                                        >
                                            Aus Angebot übernehmen
                                        </button>
                                    )}
                                </div>
                                {/* Schlussblock je Beleg. Die Grussformel in den
                                    Einstellungen gilt fuer Angebot, Rechnung UND
                                    Gutschrift gemeinsam, und der Name liess sich dort
                                    ueberhaupt nicht abschalten — fuer eine einzelne
                                    Rechnung ohne persoenliche Zeichnung musste man die
                                    Einstellung fuer alles andere mit aendern.

                                    Angehakt ist der Regelfall, damit der Bestand
                                    unveraendert druckt (details.hideClosing fehlt =
                                    drucken). */}
                                <label className="mt-2 flex items-start gap-2 text-xs text-slate-600">
                                    <input
                                        type="checkbox"
                                        disabled={!editable}
                                        checked={draft.details?.hideClosing !== true}
                                        onChange={(e) =>
                                            patch({
                                                details: {
                                                    ...draft.details,
                                                    // Nur der abweichende Fall wird
                                                    // gespeichert: `undefined` statt
                                                    // `false` haelt die details frei von
                                                    // einem Feld, das nichts aussagt.
                                                    hideClosing: e.target.checked
                                                        ? undefined
                                                        : true,
                                                },
                                            })
                                        }
                                        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-indigo-600 disabled:opacity-50"
                                    />
                                    <span>
                                        Grußformel, Unterschrift und Name drucken
                                        <span className="mt-0.5 block text-[11px] text-slate-400">
                                            Ohne Haken bleibt der ganze Schlussblock weg —
                                            die Zeile „{invoiceConfig.closingText?.trim() ||
                                                'Mit freundlichem Gruß'}“, die Unterschrift,
                                            der Name des Bearbeiters und die Firmenzeile
                                            darunter. Die Fußzeile mit Anschrift, Steuernummer
                                            und Bankverbindung bleibt — sie ist Pflichtangabe.
                                            Gilt nur für diese Rechnung.
                                        </span>
                                    </span>
                                </label>
                            </div>
                            {/* Zusatzzeilen im ANSCHRIFTFELD — direkt unter der
                                Empfaengeradresse, nicht im Betreff-Block. Fuer
                                Angaben, die der Auftraggeber dort verlangt
                                (Vergabestelle, Referenz, Abteilung). */}
                            <div className="col-span-2">
                                <span className="mb-0.5 block text-xs font-medium text-slate-600">
                                    Adress-Zusatz (optional)
                                </span>
                                <div className="grid grid-cols-3 gap-2">
                                    {(['addressExtra1', 'addressExtra2', 'addressExtra3'] as const).map(
                                        (key, i) => (
                                            <input
                                                key={key}
                                                type="text"
                                                value={draft.details?.[key] ?? ''}
                                                disabled={!editable}
                                                placeholder={`Zusatz ${i + 1}`}
                                                onChange={(e) =>
                                                    patch({
                                                        details: {
                                                            ...draft.details,
                                                            [key]: e.target.value || undefined,
                                                        },
                                                    })
                                                }
                                                className="w-full rounded-md border border-slate-300 px-2 py-1 disabled:bg-slate-50"
                                            />
                                        ),
                                    )}
                                </div>
                                <span className="mt-1 block text-xs text-slate-500">
                                    Steht direkt unter der Empfängeradresse im Anschriftfeld — je
                                    Feld eine Zeile. Der nächste Entwurf zu diesem Projekt
                                    übernimmt die Angaben.
                                </span>
                                {/* Die Abweichung gehoert genau hierhin: sie
                                    betrifft dasselbe Anschriftfeld, das die
                                    Zusatzzeilen tragen — nur eben die Zeilen
                                    darueber. */}
                                {overrideActive && (
                                    <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                                        <span className="flex-1">
                                            Dieser Beleg führt abweichende Kundenangaben (
                                            {customerOverrideSummary(customerOverride)}) — der
                                            Kundenstamm bleibt unberührt.
                                        </span>
                                        {editable && canEditCustomer && (
                                            <button
                                                type="button"
                                                onClick={() => setCustomerEditOpen(true)}
                                                className="shrink-0 rounded-lg border border-amber-300 bg-white px-2 py-1 font-bold text-amber-800 transition-all hover:bg-amber-100"
                                            >
                                                Ansehen / ändern
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                            {/* Projektname NUR fuer diesen Beleg. Steht hier
                                zwischen Ueberschrift und Kostenstelle, weil der
                                Beleg die drei Angaben in genau dieser
                                Reihenfolge druckt.

                                Nur bei genau einem Projekt: eine Sammelrechnung
                                druckt gar keine Projektzeile, und ein Feld ohne
                                Wirkung ist schlimmer als keines. */}
                            {printedProjectName && (
                                <div className="col-span-2">
                                    <Field label="Projektname auf dem Beleg (optional)">
                                        <input
                                            type="text"
                                            value={draft.details?.projectNameOverride ?? ''}
                                            disabled={!editable}
                                            placeholder={printedProjectName}
                                            onChange={(e) =>
                                                patch({
                                                    details: {
                                                        ...draft.details,
                                                        projectNameOverride:
                                                            e.target.value || undefined,
                                                    },
                                                })
                                            }
                                            className="w-full rounded-md border border-slate-300 px-2 py-1 disabled:bg-slate-50"
                                        />
                                    </Field>
                                    <span className="mt-1 block text-xs text-slate-500">
                                        Steht im Beleg als „Projekt: …“. Leer lassen für „
                                        {printedProjectName}“. Wirkt NUR auf diese Rechnung — das
                                        Projekt wird nicht umbenannt, und „Unser Zeichen“
                                        (Projektnummer) bleibt unverändert. Der nächste Entwurf zu
                                        diesem Projekt übernimmt den Namen.
                                    </span>
                                </div>
                            )}
                            {/* Kostenstelle des Auftraggebers. Sie steht im
                                Beleg direkt unter der Projektbezeichnung und
                                gehoert deshalb auch hier in den Kopfteil, dicht
                                bei der Ueberschrift.

                                Bewusst OHNE Field-Wrapper: der ist ein <label>,
                                und ein Klick auf den Vorlagen-Knopf darin
                                landete im Eingabefeld statt im Menue. */}
                            <div className="col-span-2">
                                <span className="mb-0.5 block text-xs font-medium text-slate-600">
                                    Kostenstelle (optional)
                                </span>
                                <div className="grid grid-cols-2 gap-2">
                                    {/* Links die BEZEICHNUNG mit den Vorlagen —
                                        der Text, der auf dem Beleg vor dem
                                        Doppelpunkt steht. Rechts der WERT: der
                                        ist je Beleg verschieden und deshalb
                                        freier Text ohne Vorlagenliste. */}
                                    <div className="flex items-start gap-2">
                                        <input
                                            type="text"
                                            value={draft.details?.costCenterLabel ?? ''}
                                            disabled={!editable}
                                            placeholder="Bezeichnung, z. B. PSP-Element"
                                            onChange={(e) =>
                                                patch({
                                                    details: {
                                                        ...draft.details,
                                                        costCenterLabel:
                                                            e.target.value || undefined,
                                                    },
                                                })
                                            }
                                            className="w-full rounded-md border border-slate-300 px-2 py-1 disabled:bg-slate-50"
                                        />
                                        <CostCenterPicker
                                            templates={costCenters}
                                            unavailable={costCentersUnavailable}
                                            currentLabel={draft.details?.costCenterLabel ?? ''}
                                            disabled={!editable}
                                            onPick={(label) =>
                                                patch({
                                                    details: {
                                                        ...draft.details,
                                                        costCenterLabel: label || undefined,
                                                    },
                                                })
                                            }
                                            onCreated={(t) =>
                                                setCostCenters((prev) =>
                                                    [...prev, t].sort((a, b) =>
                                                        a.name.localeCompare(b.name, 'de'),
                                                    ),
                                                )
                                            }
                                            onDeleted={(id) =>
                                                setCostCenters((prev) =>
                                                    prev.filter((t) => t.id !== id),
                                                )
                                            }
                                        />
                                    </div>
                                    <input
                                        type="text"
                                        value={draft.details?.costCenter ?? ''}
                                        disabled={!editable}
                                        placeholder="Wert, z. B. 4711-02"
                                        onChange={(e) =>
                                            patch({
                                                details: {
                                                    ...draft.details,
                                                    costCenter: e.target.value || undefined,
                                                },
                                            })
                                        }
                                        className="w-full rounded-md border border-slate-300 px-2 py-1 font-mono disabled:bg-slate-50"
                                    />
                                </div>
                                <span className="mt-1 block text-xs text-slate-500">
                                    Erscheint im Beleg unter der Projektbezeichnung als
                                    „Bezeichnung: Wert“ — ohne Bezeichnung als „Kostenstelle:
                                    Wert“. Ohne Wert gibt es keine Zeile. Vorbelegt wird die
                                    zuletzt in DIESEM Projekt verwendete Kostenstelle — gibt es
                                    dort noch keine, bleibt das Feld leer.
                                </span>
                            </div>
                            {/* Freitext unter der Kostenstelle — fuer Angaben,
                                die der Auftraggeber ueber der Anrede sehen will
                                und fuer die es kein eigenes Feld gibt. Nicht zu
                                verwechseln mit der Einleitung weiter unten: die
                                steht UNTER der Anrede. */}
                            <div className="col-span-2">
                                <Field label="Zusatztext (optional)">
                                    <textarea
                                        rows={2}
                                        value={draft.details?.subjectNote ?? ''}
                                        disabled={!editable}
                                        onChange={(e) =>
                                            patch({
                                                details: {
                                                    ...draft.details,
                                                    subjectNote: e.target.value || undefined,
                                                },
                                            })
                                        }
                                        className="w-full resize-none rounded-md border border-slate-300 px-2 py-1 disabled:bg-slate-50"
                                    />
                                </Field>
                                <span className="mt-1 block text-xs text-slate-500">
                                    Steht im Beleg unter der Kostenstelle, über der Anrede. Mehrere
                                    Zeilen sind möglich.
                                </span>
                            </div>
                            <Field label="Rechnungsdatum">
                                <input
                                    type="date"
                                    value={draft.invoiceDate}
                                    disabled={!editable}
                                    onChange={(e) =>
                                        patch({
                                            invoiceDate: e.target.value,
                                            paymentTermsDays: draft.paymentTermsDays,
                                        })
                                    }
                                    className="w-full rounded-md border border-slate-300 px-2 py-1 disabled:bg-slate-50"
                                />
                            </Field>
                            <Field label="Zahlungsziel (Tage)">
                                <input
                                    type="number"
                                    min={0}
                                    value={draft.paymentTermsDays}
                                    disabled={!editable}
                                    onChange={(e) =>
                                        patch({
                                            paymentTermsDays: Number(e.target.value) || 0,
                                            invoiceDate: draft.invoiceDate,
                                        })
                                    }
                                    className="w-full rounded-md border border-slate-300 px-2 py-1 disabled:bg-slate-50"
                                />
                            </Field>
                            {/* Skonto besteht aus Prozentsatz und Frist. Geloescht
                                wird der Skonto nur, wenn beide Werte auf 0 stehen —
                                waehrend des Tippens ist eine halb gefuellte Angabe
                                normal und bleibt erhalten (die Pruefpunkte melden
                                sie als unvollstaendig). */}
                            <Field label="Skonto (%)">
                                <input
                                    type="number"
                                    min={0}
                                    step={0.1}
                                    value={draft.details?.skonto?.percent ?? ''}
                                    disabled={!editable}
                                    onChange={(e) => {
                                        const percent = Number(e.target.value) || 0;
                                        const days = draft.details?.skonto?.days ?? 0;
                                        patch({
                                            details: {
                                                ...draft.details,
                                                skonto:
                                                    percent <= 0 && days <= 0
                                                        ? undefined
                                                        : { percent, days },
                                            },
                                        });
                                    }}
                                    className="w-full rounded-md border border-slate-300 px-2 py-1 disabled:bg-slate-50"
                                />
                            </Field>
                            <Field label="Skonto-Frist (Tage)">
                                <input
                                    type="number"
                                    min={0}
                                    value={draft.details?.skonto?.days ?? ''}
                                    disabled={!editable}
                                    onChange={(e) => {
                                        const days = Number(e.target.value) || 0;
                                        const percent = draft.details?.skonto?.percent ?? 0;
                                        patch({
                                            details: {
                                                ...draft.details,
                                                skonto:
                                                    percent <= 0 && days <= 0
                                                        ? undefined
                                                        : { percent, days },
                                            },
                                        });
                                    }}
                                    className="w-full rounded-md border border-slate-300 px-2 py-1 disabled:bg-slate-50"
                                />
                            </Field>
                            <div className="col-span-2">
                                <Field label="Zahlungsbedingungen">
                                    <textarea
                                        rows={2}
                                        value={draft.details?.paymentTerms ?? ''}
                                        disabled={!editable}
                                        onChange={(e) =>
                                            patch({
                                                details: {
                                                    ...draft.details,
                                                    paymentTerms: e.target.value || undefined,
                                                },
                                            })
                                        }
                                        className="w-full resize-none rounded-md border border-slate-300 px-2 py-1 disabled:bg-slate-50"
                                    />
                                </Field>
                                <div className="mt-1 flex flex-wrap items-center gap-2">
                                    <span className="text-xs text-slate-500">
                                        Leer lassen für den Standardsatz mit Fälligkeitsdatum.
                                    </span>
                                    {/* Der Text wird bewusst nur auf Knopfdruck erzeugt:
                                        ein spaeter geaenderter Termin oder eine geaenderte
                                        Frist schreibt einen von Hand angepassten Text nicht
                                        stillschweigend um. */}
                                    {editable && skontoText && (
                                        <button
                                            type="button"
                                            onClick={() =>
                                                patch({
                                                    details: {
                                                        ...draft.details,
                                                        paymentTerms: skontoText,
                                                    },
                                                })
                                            }
                                            className="rounded-md border border-slate-300 bg-white px-2 py-0.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                                        >
                                            Skonto-Text einsetzen
                                        </button>
                                    )}
                                </div>
                            </div>
                            {/* Kumulativ gibt es kein Von und kein Bis, sondern
                                einen Stichtag: der Beleg weist den gesamten
                                Leistungsstand bis zu diesem Tag aus. Ein
                                eingebbares "Leistung von" waere dort eine
                                Angabe, die der Beleg nicht einhaelt — der
                                Beginn ergibt sich aus den abgerechneten
                                Leistungen und wird beim Aufbau nachgezogen. */}
                            {isCumulative ? (
                                <Field label="Leistungsstand zum">
                                    <input
                                        type="date"
                                        value={draft.deliveryDateTo || ''}
                                        disabled={!editable}
                                        title={SELECTION_FIELD_HINT}
                                        onChange={(e) => patch({ deliveryDateTo: e.target.value })}
                                        className="w-full rounded-md border border-slate-300 px-2 py-1 disabled:bg-slate-50"
                                    />
                                    <span className="mt-1 block text-xs text-slate-500">
                                        {SELECTION_FIELD_HINT}
                                    </span>
                                    {/* Der Leistungsbeginn wird ermittelt (erste
                                        Leistung bis zum Stichtag) — und laesst sich
                                        hier von Hand ersetzen. Die Angabe steht in
                                        details.cumulativeFromManual, damit der
                                        naechste automatische Aufbau sie nicht
                                        stillschweigend zuruecknimmt. Gesetzt wird sie
                                        sofort auch in deliveryDateFrom und
                                        performedFrom: der Aufbau laeuft bei von Hand
                                        geaenderten Positionen NICHT von selbst, und
                                        der Beleg soll die Aenderung trotzdem zeigen. */}
                                    <span className="mt-1 block text-xs text-slate-500">
                                        Leistungsbeginn{' '}
                                        {draft.details?.cumulativeFromManual
                                            ? 'von Hand gesetzt:'
                                            : 'wird automatisch ermittelt:'}{' '}
                                        {de(draft.deliveryDateFrom || undefined)}
                                    </span>
                                    {editable && (
                                        <div className="mt-1 flex items-center gap-2">
                                            <input
                                                type="date"
                                                value={draft.details?.cumulativeFromManual ?? ''}
                                                max={draft.deliveryDateTo || undefined}
                                                title="Leistungsbeginn von Hand setzen — ersetzt den ermittelten Wert auf dem Beleg und im Leistungszeitraum"
                                                onChange={(e) => {
                                                    const wert = e.target.value;
                                                    // Unfertig getippt (kein volles Datum): nur
                                                    // merken, nichts am Beleg umstellen.
                                                    const tag = /^\d{4}-\d{2}-\d{2}$/.test(wert) ? wert : undefined;
                                                    patch({
                                                        ...(tag ? { deliveryDateFrom: tag } : {}),
                                                        details: {
                                                            ...draft.details,
                                                            cumulativeFromManual: wert || undefined,
                                                            ...(tag ? { performedFrom: tag } : {}),
                                                        },
                                                    });
                                                }}
                                                className="rounded-md border border-slate-300 px-2 py-1 text-xs"
                                            />
                                            {draft.details?.cumulativeFromManual && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        // Zurueck auf automatisch: der Aufbau
                                                        // ermittelt den Beginn neu. deliveryDateFrom
                                                        // wird geleert, damit der Aufbau anspringt;
                                                        // bei von Hand geaenderten Positionen sagt
                                                        // die Aufbau-Schaltflaeche, was zu tun ist.
                                                        const { cumulativeFromManual: _weg, performedFrom: _alt, ...rest } =
                                                            draft.details ?? {};
                                                        void _weg;
                                                        void _alt;
                                                        patch({ deliveryDateFrom: '', details: rest });
                                                    }}
                                                    className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                                                >
                                                    automatisch
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    {performedPeriod.from && performedPeriod.to && (
                                        <span className="mt-1 block text-xs text-slate-500">
                                            Leistungszeitraum:{' '}
                                            {performedPeriod.from === performedPeriod.to
                                                ? de(performedPeriod.to)
                                                : `${de(performedPeriod.from)} – ${de(performedPeriod.to)}`}
                                        </span>
                                    )}
                                </Field>
                            ) : (
                                <Field label="Leistung von">
                                    <input
                                        type="date"
                                        value={draft.deliveryDateFrom || ''}
                                        disabled={!editable}
                                        title={SELECTION_FIELD_HINT}
                                        onChange={(e) => patch({ deliveryDateFrom: e.target.value })}
                                        className="w-full rounded-md border border-slate-300 px-2 py-1 disabled:bg-slate-50"
                                    />
                                </Field>
                            )}
                            {/* Der Satz steht im Vertrag und unterscheidet sich
                                je Auftraggeber — deshalb am Beleg und nicht nur
                                in den Einstellungen. */}
                            <Field label="Nebenkosten (%)">
                                <input
                                    type="number"
                                    min={0}
                                    max={100}
                                    step={0.1}
                                    value={effectiveAncillaryPercent ?? ''}
                                    disabled={!editable}
                                    onChange={(e) =>
                                        patch({
                                            details: {
                                                ...draft.details,
                                                // Geleert heisst NULL, nicht
                                                // "nichts gesagt": sonst zoege
                                                // der Standard aus den
                                                // Einstellungen den Satz beim
                                                // naechsten Oeffnen wieder ein.
                                                ancillaryPercent:
                                                    e.target.value === ''
                                                        ? 0
                                                        : Number(e.target.value),
                                            },
                                            // Zeilen mitschicken: nur dann bildet
                                            // updateInvoice den Zuschlag neu.
                                            lines: draft.lines,
                                        })
                                    }
                                    className="w-full rounded-md border border-slate-300 px-2 py-1 disabled:bg-slate-50"
                                />
                            </Field>
                            {/* Nachlass auf den ganzen Beleg. Er entsteht als
                                eigene Position je Steuersatz — deshalb wandern
                                die Zeilen mit, und der Nebenkostenzuschlag
                                rechnet danach auf die geminderte Summe. */}
                            <Field label="Nachlass auf den Beleg">
                                <DiscountInput
                                    percent={draft.details?.discountPercent}
                                    amountCents={draft.details?.discountAmountCents}
                                    disabled={!editable}
                                    ariaLabel="Nachlass auf den Beleg"
                                    onChange={(next) =>
                                        patch({
                                            details: {
                                                ...draft.details,
                                                // Beide Werte ausdruecklich,
                                                // auch die Null: ein fehlendes
                                                // Feld liesse den gespeicherten
                                                // Stand stehen (updateInvoice).
                                                discountPercent: next.percent,
                                                discountAmountCents: next.amountCents,
                                            },
                                            // Zeilen mitschicken: nur dann bildet
                                            // updateInvoice die Nachlasszeilen neu.
                                            lines: draft.lines,
                                        })
                                    }
                                />
                                <span className="mt-1 block text-xs text-slate-500">
                                    Erscheint als eigene Position je Steuersatz. Die Nebenkosten
                                    rechnen auf die geminderte Summe.
                                </span>
                            </Field>
                            {/* Sicherheitseinbehalt (§ 17 VOB/B oder freie
                                Vereinbarung). Bewusst KEIN Nachlass: er bildet
                                keine Position und mindert die
                                Bemessungsgrundlage nicht — er haelt einen Teil
                                des Zahlbetrags zurueck, der mit der
                                Schlussrechnung faellig wird. Deshalb auch das
                                eigene Feld und nicht der DiscountInput.
                                Ueber die ganze Breite: ohne den Hinweistext
                                sieht das Feld aus wie ein weiterer Abzug. */}
                            <div className="col-span-2">
                                <Field label="Sicherheitseinbehalt (%)">
                                    <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        step={0.5}
                                        value={retentionPercent ?? ''}
                                        disabled={!editable || retentionLocked}
                                        onChange={(e) =>
                                            patch({
                                                details: {
                                                    ...draft.details,
                                                    // Geleert schickt die 0 und
                                                    // nicht undefined: undefined
                                                    // hiesse "nie etwas gesagt",
                                                    // und der Vorbelegungs-Effekt
                                                    // zoege den Projektsatz beim
                                                    // naechsten Oeffnen wieder
                                                    // herein. Wer das Feld leert,
                                                    // meint aber "dieser Beleg
                                                    // behaelt nichts ein" —
                                                    // dieselbe Entscheidung wie
                                                    // beim Nebenkostensatz.
                                                    retentionPercent:
                                                        e.target.value === ''
                                                            ? 0
                                                            : Math.min(
                                                                  100,
                                                                  Math.max(
                                                                      0,
                                                                      Number(e.target.value),
                                                                  ),
                                                              ),
                                                },
                                                // Zeilen mitschicken: nur dann
                                                // rechnet updateInvoice die
                                                // Summen neu.
                                                lines: draft.lines,
                                            })
                                        }
                                        className="w-full rounded-md border border-slate-300 px-2 py-1 disabled:bg-slate-50"
                                    />
                                    <span className="mt-1 block text-xs text-slate-500">
                                        Kumuliert auf die Gesamtleistung; bereits einbehaltene
                                        Beträge der Vorrechnungen werden angerechnet. 0 % gibt
                                        bisher Einbehaltenes frei.
                                    </span>
                                    {retentionLocked && (
                                        <span className="mt-1 block text-xs text-slate-500">
                                            Bei Storno und Gutschrift ohne Wirkung — der Beleg
                                            spiegelt den Einbehalt des Originals.
                                        </span>
                                    )}
                                    {/* Die Rechnung in einer Zeile: kumuliert auf den
                                        Leistungsstand, davon schon einbehalten, davon
                                        mit diesem Beleg — dieselben Zahlen wie im
                                        Summenblock, nur naeher am Feld. */}
                                    {cumulativeRetentionCents > 0 && (
                                        <span className="mt-1 block text-xs text-slate-500 tabular-nums">
                                            Kumuliert {formatCents(cumulativeRetentionCents)} auf{' '}
                                            {formatCents(draft.totals.grossTotalCents)}
                                            {priorRetentionCents !== 0 &&
                                                `, davon bereits einbehalten ${formatCents(priorRetentionCents)}`}
                                            {retentionCents > 0 &&
                                                `, mit diesem Beleg ${formatCents(retentionCents)}`}
                                            {retentionReleaseCents > 0 &&
                                                `, mit diesem Beleg freigegeben ${formatCents(retentionReleaseCents)}`}
                                        </span>
                                    )}
                                    {retentionUnusual && (
                                        <span className="mt-1 block rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800">
                                            Über 10 % ist ungewöhnlich — üblich sind 5 %. Bitte
                                            gegen den Vertrag prüfen.
                                        </span>
                                    )}
                                    {/* Nachholfall (Bauplan §16): ohne Alt-Einbehalte
                                        zahlt der Kunde den Einbehalt der Vorrechnungen
                                        ein zweites Mal, wenn er sie damals selbst
                                        gekuerzt hat. Der Knopf oeffnet die Maske
                                        direkt — der Umweg ueber den Projekt-Hub
                                        kostete den halb fertigen Beleg aus dem Blick. */}
                                    {retentionCatchUp && (
                                        <span className="mt-1 block rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800">
                                            Auf den Vorrechnungen ist kein Einbehalt hinterlegt —
                                            dieser Beleg holt den vollen Betrag nach. Hat der
                                            Kunde bereits einbehalten, Alt-Einbehalte
                                            erfassen.
                                            {canEditProjectRetention && (
                                                <button
                                                    type="button"
                                                    onClick={() => setShowRetentionBackfill(true)}
                                                    className="ml-2 font-semibold underline decoration-amber-400 underline-offset-2 hover:text-amber-900"
                                                >
                                                    Alt-Einbehalte erfassen
                                                </button>
                                            )}
                                        </span>
                                    )}
                                    {/* Projektvorgabe: gepflegt hier und nicht im
                                        Projekt-Hub — der Einbehalt ist eine
                                        Abrechnungsbedingung, keine Baustellenangabe.
                                        Der Knopf zu den Alt-Einbehalten steht im
                                        Nachholfall schon im Hinweis darueber. */}
                                    {draft.primaryProjectId && (
                                        <span className="mt-1 block text-xs text-slate-500">
                                            {projectRetentionDraft === null ? (
                                                <>
                                                    Projektvorgabe:{' '}
                                                    {projectRetentionPercent !== undefined
                                                        ? `${projectRetentionPercent} %`
                                                        : 'keine'}{' '}
                                                    — belegt neue Belege dieses Projekts vor.
                                                    {canEditProjectRetention && (
                                                        <button
                                                            type="button"
                                                            disabled={busy !== null}
                                                            onClick={() =>
                                                                setProjectRetentionDraft(
                                                                    projectRetentionPercent !== undefined
                                                                        ? String(projectRetentionPercent)
                                                                        : '',
                                                                )
                                                            }
                                                            className="ml-2 font-semibold text-indigo-600 underline underline-offset-2 hover:text-indigo-800 disabled:opacity-50"
                                                        >
                                                            Vorgabe ändern
                                                        </button>
                                                    )}
                                                    {canEditProjectRetention && !retentionCatchUp && (
                                                        <button
                                                            type="button"
                                                            disabled={busy !== null}
                                                            onClick={() => setShowRetentionBackfill(true)}
                                                            className="ml-2 font-semibold text-indigo-600 underline underline-offset-2 hover:text-indigo-800 disabled:opacity-50"
                                                        >
                                                            Alt-Einbehalte erfassen
                                                        </button>
                                                    )}
                                                </>
                                            ) : (
                                                <span className="inline-flex flex-wrap items-center gap-2">
                                                    <span>Projektvorgabe (%)</span>
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        max={100}
                                                        step={0.5}
                                                        value={projectRetentionDraft}
                                                        placeholder="keine"
                                                        autoFocus
                                                        onChange={(e) => setProjectRetentionDraft(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                void saveProjectRetention(projectRetentionDraft);
                                                            }
                                                            if (e.key === 'Escape') setProjectRetentionDraft(null);
                                                        }}
                                                        className="w-20 rounded-md border border-slate-300 px-2 py-0.5"
                                                    />
                                                    <button
                                                        type="button"
                                                        disabled={busy !== null}
                                                        onClick={() => void saveProjectRetention(projectRetentionDraft)}
                                                        className="font-semibold text-indigo-600 underline underline-offset-2 hover:text-indigo-800 disabled:opacity-50"
                                                    >
                                                        Übernehmen
                                                    </button>
                                                    <button
                                                        type="button"
                                                        disabled={busy !== null}
                                                        onClick={() => setProjectRetentionDraft(null)}
                                                        className="underline underline-offset-2 hover:text-slate-700 disabled:opacity-50"
                                                    >
                                                        Abbrechen
                                                    </button>
                                                </span>
                                            )}
                                        </span>
                                    )}
                                </Field>
                            </div>
                            {!isCumulative && (
                                <Field label="Leistung bis">
                                    <input
                                        type="date"
                                        value={draft.deliveryDateTo || ''}
                                        disabled={!editable}
                                        title={SELECTION_FIELD_HINT}
                                        onChange={(e) => patch({ deliveryDateTo: e.target.value })}
                                        className="w-full rounded-md border border-slate-300 px-2 py-1 disabled:bg-slate-50"
                                    />
                                    {/* Einmal ausgeschrieben fuer beide Felder
                                        des Paares: sie steuern die AUSWAHL. Wer
                                        nur den Andruck aendern will, ist im
                                        Kasten darunter richtig. */}
                                    <span className="mt-1 block text-xs text-slate-500">
                                        {SELECTION_FIELD_HINT}
                                    </span>
                                    {/* Auch fortschreibend, wo die Angabe sich
                                        mit dem Leistungsdatum deckt: sie
                                        steht auf jedem Beleg, und der Editor
                                        zeigt vorher, was der Andruck zeigt. */}
                                    {performedPeriod.from && performedPeriod.to && (
                                        <span className="mt-1 block text-xs text-slate-500">
                                            Leistungszeitraum:{' '}
                                            {performedPeriod.from === performedPeriod.to
                                                ? de(performedPeriod.to)
                                                : `${de(performedPeriod.from)} – ${de(performedPeriod.to)}`}
                                        </span>
                                    )}
                                </Field>
                            )}
                            {/* Der ABRECHNUNGSZEITRAUM — worueber abgerechnet
                                wird. Getrennt von den Feldern darueber, die die
                                Auswahl der Positionen steuern, und getrennt vom
                                Leistungszeitraum, der sagt, WANN geleistet
                                wurde. Kumulativ fallen die beiden zwingend
                                auseinander: der Beleg schreibt den Gesamtstand
                                fort und rechnet Leistungen ab dem ersten
                                Projekttag mit ab, abgerechnet wird aber ueber
                                den gewaehlten Monat. Eine Aenderung hier baut
                                die Positionen NICHT neu auf: sie aendert nur,
                                was auf dem Papier und in der E-Rechnung steht. */}
                            <div className="col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-2">
                                <p className="mb-1 text-xs font-medium text-slate-600">
                                    Abrechnungszeitraum auf dem Beleg (nur Andruck — ändert keine
                                    Beträge)
                                </p>
                                {/* Gebunden an die EIGENE Angabe, nicht an den
                                    abgeleiteten Wert: printedPeriod verwirft
                                    unfertige Datumsangaben (plausibleDay), und
                                    ein <input type="date"> meldet beim Tippen
                                    des Jahres genau solche Zwischenstaende
                                    ("0002-05-01"). Gebunden an das Abgeleitete
                                    sprang das Feld dabei auf das Leistungsdatum
                                    zurueck und kaempfte gegen den Tippenden.
                                    Vorbelegt bleibt es trotzdem: ohne eigene
                                    Angabe steht der abgeleitete Wert darin.
                                    Andruck und XML schuetzt plausibleDay
                                    weiterhin. */}
                                <div className="grid grid-cols-2 gap-2">
                                    <Field label="von">
                                        <input
                                            type="date"
                                            value={
                                                draft.details?.servicePeriodFrom ??
                                                printedPeriod.from ??
                                                ''
                                            }
                                            disabled={!editable}
                                            onChange={(e) =>
                                                patch({
                                                    details: {
                                                        ...draft.details,
                                                        servicePeriodFrom:
                                                            e.target.value || undefined,
                                                    },
                                                })
                                            }
                                            className="w-full rounded-md border border-slate-300 px-2 py-1 disabled:bg-slate-50"
                                        />
                                    </Field>
                                    <Field label="bis">
                                        <input
                                            type="date"
                                            value={
                                                draft.details?.servicePeriodTo ??
                                                printedPeriod.to ??
                                                ''
                                            }
                                            disabled={!editable}
                                            onChange={(e) =>
                                                patch({
                                                    details: {
                                                        ...draft.details,
                                                        servicePeriodTo: e.target.value || undefined,
                                                    },
                                                })
                                            }
                                            className="w-full rounded-md border border-slate-300 px-2 py-1 disabled:bg-slate-50"
                                        />
                                    </Field>
                                </div>
                                {servicePeriodError ? (
                                    <p className="mt-1 flex items-start gap-1 text-xs text-red-700">
                                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                        <span>{servicePeriodError}</span>
                                    </p>
                                ) : servicePeriodDrift ? (
                                    <div className="mt-1">
                                        <p className="flex items-start gap-1 text-xs text-amber-800">
                                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                            <span>{servicePeriodDrift}</span>
                                        </p>
                                        {/* Der Ausweg mit einem Klick: das obere
                                            Feld auf das hier eingetragene Ende
                                            ziehen. Derselbe Weg wie die
                                            Handeingabe — der Positionsaufbau
                                            laeuft danach von selbst, und Mengen,
                                            Leistungszeitraum und dieser Hinweis
                                            stimmen wieder ueberein. */}
                                        {editable && servicePeriodLimit && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    patch({ deliveryDateTo: servicePeriodLimit });
                                                    void rebuildPositions(
                                                        undefined,
                                                        servicePeriodLimit,
                                                    );
                                                }}
                                                className="mt-1 rounded-md border border-amber-400 bg-white px-2 py-0.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
                                            >
                                                Abrechnung bis {de(servicePeriodLimit)} begrenzen (
                                                {isCumulative ? 'Leistungsstand' : 'Stichtag'}{' '}
                                                anpassen)
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <p className="mt-1 text-xs text-slate-500">
                                        Steht so auf dem Andruck, in der E-Rechnung und im
                                        angehängten Leistungsnachweis. Vorbelegt aus dem im
                                        Anlagedialog gewählten Zeitraum; geleert gilt wieder das
                                        Leistungsdatum des Belegs. Der <em>Leistungszeitraum</em> —
                                        wann tatsächlich geleistet wurde — wird davon nicht berührt.
                                    </p>
                                )}
                                {/* Angebot statt stillem Nachziehen: der Beleg
                                    trägt keinen eigenen Abrechnungszeitraum, die
                                    Zeile entfällt deshalb auf dem Andruck.
                                    Angeboten wird der Zeitraum, den die
                                    Zuordnung hergibt — das ist der tatsächliche
                                    Leistungszeitraum, und die Beschriftung sagt
                                    genau das. Übernommen wird auf Knopfdruck. */}
                                {offerServicePeriod && billingPeriod && (
                                    <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2">
                                        <p className="flex items-start gap-1 text-xs text-amber-900">
                                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                            <span>
                                                Dieser Beleg nennt keinen eigenen
                                                Abrechnungszeitraum. Tatsächlich geleistet wurde{' '}
                                                {billingPeriod.from === billingPeriod.to
                                                    ? de(billingPeriod.to)
                                                    : `${de(billingPeriod.from)} – ${de(billingPeriod.to)}`}
                                                .
                                            </span>
                                        </p>
                                        <button
                                            type="button"
                                            onClick={applyBillingPeriodAsServicePeriod}
                                            className="mt-1 rounded-md border border-amber-400 bg-white px-2 py-0.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
                                        >
                                            Tatsächlichen Leistungszeitraum übernehmen
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                        {editable && draft.primaryProjectId && (
                            <div className="mt-3 flex items-start gap-2 border-t border-slate-200 pt-3">
                                <Button
                                    variant="secondary"
                                    onClick={() => void rebuildPositions()}
                                    disabled={busy !== null}
                                    className="shrink-0"
                                >
                                    {busy === 'rebuild' ? (
                                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                                    ) : (
                                        <RefreshCw className="mr-1 h-4 w-4" />
                                    )}
                                    Positionen neu aufbauen
                                </Button>
                                <div className="text-xs text-slate-500">
                                    {/* Der Regelfall braucht diese Schaltflaeche
                                        nicht mehr — sie bleibt fuer den Fall,
                                        dass die Positionen von Hand geaendert
                                        wurden und der Aufbau deshalb nicht von
                                        selbst laeuft. */}
                                    {positionsEdited ? (
                                        <p className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 leading-snug text-amber-900">
                                            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                                            <span>
                                                <strong>
                                                    Der Zeitraum passt nicht mehr zu den Positionen.
                                                </strong>{' '}
                                                Automatisch aufgebaut wird hier nicht: die
                                                Positionen wurden von Hand geändert, und der Aufbau
                                                würde diese Änderung verwerfen. Mit der
                                                Schaltfläche links baust du sie bewusst neu auf.
                                            </span>
                                        </p>
                                    ) : (
                                        <p>
                                            Läuft nach einer Änderung des{' '}
                                            {isCumulative ? 'Stichtags' : 'Leistungsdatums'} von
                                            selbst. Ersetzt dabei die Positionen aus dem
                                            Leistungsverzeichnis durch den aktuellen Stand; von Hand
                                            ergänzte Zeilen bleiben stehen.
                                        </p>
                                    )}
                                    {/* Was uebernommen wird, haengt am
                                        Abrechnungsmodell — und zwar
                                        grundlegend. Ein Satz fuer beide Faelle
                                        waere fuer einen davon falsch: kumulativ
                                        gibt es nur einen Stichtag, und der
                                        Leistungsbeginn ist dessen Folge, keine
                                        Eingabe. */}
                                    <p className="mt-1">
                                        {isCumulative ? (
                                            <>
                                                <strong>Kumulativ:</strong> übernommen wird der{' '}
                                                <em>gesamte</em> Leistungsstand bis{' '}
                                                {draft.deliveryDateTo
                                                    ? de(draft.deliveryDateTo)
                                                    : 'heute'}{' '}
                                                — auch das, was frühere Rechnungen bereits gefordert
                                                haben. Abgezogen wird es unten im Summenblock. Der
                                                ausgewiesene Leistungsbeginn wird dabei mitgeführt:
                                                er muss alles abdecken, was der Beleg abrechnet.
                                            </>
                                        ) : draft.deliveryDateFrom || draft.deliveryDateTo ? (
                                            <>
                                                <strong>
                                                    Zeitraum {de(draft.deliveryDateFrom)} bis{' '}
                                                    {de(draft.deliveryDateTo)}:
                                                </strong>{' '}
                                                übernommen wird nur, was in diesen Tagen im
                                                Leistungsprotokoll steht.
                                            </>
                                        ) : (
                                            <>
                                                <strong>Ohne Zeitraum:</strong> übernommen wird
                                                alles, was erbracht und noch nicht abgerechnet ist.
                                            </>
                                        )}
                                    </p>
                                    <label className="mt-1 flex items-start gap-2">
                                        <input
                                            type="checkbox"
                                            className="mt-0.5"
                                            checked={rebuildUndated}
                                            onChange={(e) => setRebuildUndated(e.target.checked)}
                                        />
                                        <span>
                                            Mengen ohne Datum mitnehmen. Wer den Leistungsstand von
                                            Hand korrigiert, erzeugt keinen Eintrag im
                                            Leistungsprotokoll — solche Mengen gehören zu keinem Tag
                                            und tauchen deshalb in keinem Zeitraum auf.
                                        </span>
                                    </label>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-white p-3">
                        <h2 className="mb-2 text-sm font-semibold text-slate-700">
                            Positionen ({serviceLines.length})
                        </h2>

                        {/* Gliederung des Belegs. Nur im Entwurf zu aendern —
                            ab der Ausstellung ist der Beleg eingefroren, die
                            Abschnitte werden dann nur noch angezeigt. Ein
                            Abschnitt ohne Positionen steht hier, aber nicht auf
                            dem Beleg: eine Ueberschrift ueber nichts liest sich
                            wie ein Fehler. */}
                        {(editable || invoiceSections.length > 0) && (
                            <div className="mb-3 rounded-md border border-slate-200 bg-slate-50 p-2">
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-xs font-semibold text-slate-600">
                                        Abschnitte
                                    </span>
                                    {editable && (
                                        <button
                                            type="button"
                                            onClick={addSection}
                                            className="rounded-md border border-slate-300 bg-white px-2 py-0.5 text-xs text-slate-700 hover:bg-slate-100"
                                        >
                                            + Abschnitt
                                        </button>
                                    )}
                                </div>
                                {invoiceSections.length === 0 ? (
                                    <p className="mt-1 text-[11px] leading-4 text-slate-500">
                                        Ohne Abschnitte druckt der Beleg eine durchgehende Liste.
                                        Mit Abschnitten bekommt jede Gruppe eine Überschrift und —
                                        ab zwei Abschnitten — eine Zwischensumme.
                                    </p>
                                ) : (
                                    <ul className="mt-1 space-y-1">
                                        {invoiceSections.map((sec, i) => (
                                            <li key={sec.id} className="flex items-center gap-1">
                                                <input
                                                    value={sec.title}
                                                    onChange={(e) =>
                                                        renameSection(sec.id, e.target.value)
                                                    }
                                                    disabled={!editable}
                                                    aria-label="Name des Abschnitts"
                                                    title="Steht als Überschrift auf dem Beleg."
                                                    className="min-w-0 flex-1 rounded-md border border-slate-300 px-2 py-0.5 text-xs disabled:bg-slate-100"
                                                />
                                                <span className="w-16 shrink-0 text-right text-[11px] tabular-nums text-slate-500">
                                                    {sectionLineCount(sec.id)} Pos.
                                                </span>
                                                {editable && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            onClick={() => moveSection(sec.id, -1)}
                                                            disabled={i === 0}
                                                            aria-label="Abschnitt nach oben"
                                                            title="Nach oben"
                                                            className="rounded-md px-1 text-slate-500 hover:bg-slate-200 disabled:text-slate-300 disabled:hover:bg-transparent"
                                                        >
                                                            ↑
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => moveSection(sec.id, 1)}
                                                            disabled={
                                                                i === invoiceSections.length - 1
                                                            }
                                                            aria-label="Abschnitt nach unten"
                                                            title="Nach unten"
                                                            className="rounded-md px-1 text-slate-500 hover:bg-slate-200 disabled:text-slate-300 disabled:hover:bg-transparent"
                                                        >
                                                            ↓
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => deleteSection(sec.id)}
                                                            aria-label="Abschnitt löschen"
                                                            title="Abschnitt löschen — seine Positionen bleiben auf dem Beleg und stehen danach ohne Zuordnung."
                                                            className="rounded-md p-1 text-slate-400 hover:bg-slate-200 hover:text-red-600"
                                                        >
                                                            <MinusCircle className="h-3.5 w-3.5" />
                                                        </button>
                                                    </>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}

                        {/* ANDRUCK GEDECKTER POSITIONEN (Monatspauschale).
                            Nur sichtbar, wenn der Beleg solche Zeilen traegt.
                            Reine Darstellung: die Zeilen unten bleiben, wie sie
                            sind — PDF und E-Rechnung fassen sie bei "Nur
                            Monatspauschale" je Steuersatz zu einer Summenzeile
                            zusammen. Weglassen ginge nicht, siehe den Hinweis
                            am Knopf "Nicht abrechnen". */}
                        {draft.lines.some((l) => l.flatCovered) && (
                            <div className="mb-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                                <div className="text-xs text-slate-600">
                                    <span className="font-medium text-slate-700">
                                        Andruck bei Monatspauschale
                                    </span>
                                    <span className="block text-slate-500">
                                        {(draft.details?.flatRateDisplay ?? 'ALL') === 'FLAT_ONLY'
                                            ? `Gedeckte Positionen erscheinen auf dem Beleg als eine Summenzeile „${FLAT_SUMMARY_TITLE}“. Beträge, Abzug und Zahlbetrag bleiben gleich.`
                                            : 'Jede gedeckte Position erscheint einzeln mit ihrem eingefrorenen Stand.'}
                                    </span>
                                </div>
                                <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 text-xs">
                                    {(
                                        [
                                            ['ALL', 'Alle Positionen', 'Jede Position einzeln, wie sie unten steht'],
                                            ['FLAT_ONLY', 'Nur Monatspauschale', 'Gedeckte Positionen zu einer Summenzeile zusammengefasst — der Kunde sieht die Pauschale'],
                                        ] as const
                                    ).map(([mode, label, title]) => (
                                        <button
                                            key={mode}
                                            type="button"
                                            title={title}
                                            disabled={!editable}
                                            onClick={() =>
                                                patch({
                                                    details: {
                                                        ...draft.details,
                                                        flatRateDisplay: mode,
                                                    },
                                                })
                                            }
                                            className={`rounded-md px-2 py-1 transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                                                (draft.details?.flatRateDisplay ?? 'ALL') === mode
                                                    ? 'bg-blue-600 font-medium text-white'
                                                    : 'text-slate-600 hover:bg-slate-100'
                                            }`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                                    <tr className="border-b border-slate-200">
                                        <th className="py-1 font-medium">Pos.</th>
                                        <th className="py-1 font-medium">Leistung</th>
                                        {/* Die Spalte erscheint erst mit dem
                                            ersten Abschnitt: ohne Gliederung
                                            waere sie eine Spalte ohne Inhalt. */}
                                        {invoiceSections.length > 0 && (
                                            <th className="py-1 font-medium">Abschnitt</th>
                                        )}
                                        <th className="py-1 text-right font-medium">Menge</th>
                                        <th className="py-1 text-right font-medium">EP</th>
                                        {/* Nur im Entwurf: ab der Ausstellung
                                            steht der Nachlass im Betrag der
                                            Zeile und ist nicht mehr zu aendern.
                                            Die Spalte zeigt ihn dann als
                                            abgezogenen Betrag. */}
                                        <th className="py-1 text-right font-medium">Nachlass</th>
                                        <th className="py-1 text-right font-medium">USt</th>
                                        <th className="py-1 text-right font-medium">Netto</th>
                                        {/* Eine leere Ueberschrift ist fuer den
                                            Screenreader keine Spalte: er nennt
                                            nichts, waehrend darunter der Knopf
                                            "Nicht abrechnen" steht. */}
                                        {editable && (
                                            <th className="py-1">
                                                <span className="sr-only">Aktionen</span>
                                            </th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200">
                                    {draft.lines.map((l) => (
                                        <tr key={l.id} className="hover:bg-slate-50">
                                            {/* Nummer und Text gelten nur fuer
                                                diesen Beleg — das Projekt-LV
                                                bleibt unveraendert. */}
                                            <td className="py-1 align-top">
                                                {editable && l.kind === 'SERVICE' ? (
                                                    <input
                                                        value={l.posNumber ?? ''}
                                                        onChange={(e) => {
                                                            const v = e.target.value;
                                                            // Leeres Feld heisst "wieder
                                                            // durchnummerieren" — sonst
                                                            // gaebe es keinen Weg zurueck
                                                            // zur fortlaufenden Nummer.
                                                            patchLine(
                                                                l.id,
                                                                v.trim()
                                                                    ? {
                                                                          posNumber: v,
                                                                          posNumberManual: true,
                                                                      }
                                                                    : {
                                                                          posNumber: undefined,
                                                                          posNumberManual: false,
                                                                      },
                                                            );
                                                        }}
                                                        onBlur={sortLinesByPosNumber}
                                                        title="Positionsnummer nur auf diesem Beleg. Der Beleg sortiert sich danach. Leeren stellt die fortlaufende Nummer wieder her."
                                                        aria-label="Positionsnummer"
                                                        className="w-14 rounded-md border border-slate-200 px-1 py-0.5 font-mono tabular-nums focus:border-blue-500 focus:outline-none"
                                                    />
                                                ) : (
                                                    <span className="font-mono">{l.posNumber || ''}</span>
                                                )}
                                            </td>
                                            <td className="py-1">
                                                {editable && l.kind === 'SERVICE' ? (
                                                    <input
                                                        value={l.title}
                                                        onChange={(e) =>
                                                            patchLine(l.id, {
                                                                title: e.target.value,
                                                                titleManual: true,
                                                            })
                                                        }
                                                        title="Bezeichnung nur auf diesem Beleg. Die Leistung im Projekt behält ihren Namen."
                                                        aria-label="Bezeichnung der Position"
                                                        className="w-full min-w-[12rem] rounded-md border border-slate-200 px-1 py-0.5 focus:border-blue-500 focus:outline-none"
                                                    />
                                                ) : (
                                                    <span
                                                        className={
                                                            l.kind === 'SECTION'
                                                                ? 'font-semibold'
                                                                : ''
                                                        }
                                                    >
                                                        {l.title}
                                                    </span>
                                                )}
                                                {l.lvPosition && (
                                                    <span className="ml-1 text-slate-400">
                                                        ({l.lvPosition})
                                                    </span>
                                                )}
                                                {l.flatCovered && (
                                                    <span
                                                        className="ml-1 rounded bg-amber-50 px-1 text-[10px] text-amber-700"
                                                        title={
                                                            draft.details?.flatRateDisplay === 'FLAT_ONLY'
                                                                ? 'Durch die Monatspauschale gedeckt — im Andruck in der Summenzeile zusammengefasst.'
                                                                : 'Durch die Monatspauschale gedeckt — steht einzeln auf dem Beleg.'
                                                        }
                                                    >
                                                        {draft.details?.flatRateDisplay === 'FLAT_ONLY'
                                                            ? 'Pauschale · zusammengefasst'
                                                            : 'Pauschale'}
                                                    </span>
                                                )}
                                                {/* Kumulativ nennt die Zeile den
                                                    Gesamtstand — "7 Stück" können
                                                    eine neue Begehung plus sechs
                                                    alte sein. Erst die Aufteilung
                                                    macht sichtbar, was diese
                                                    Rechnung tatsächlich fordert. */}
                                                {l.previouslyBilledQty != null &&
                                                    l.previouslyBilledQty > 0 && (
                                                        <span className="block text-slate-400">
                                                            davon {l.previouslyBilledQty}{' '}
                                                            {l.unit || ''} bereits abgerechnet
                                                            {(l.quantity ?? 0) -
                                                                l.previouslyBilledQty >
                                                            0.0005 ? (
                                                                <>
                                                                    {' · '}
                                                                    <span className="font-medium text-emerald-700">
                                                                        neu{' '}
                                                                        {(l.quantity ?? 0) -
                                                                            l.previouslyBilledQty}{' '}
                                                                        {l.unit || ''}
                                                                    </span>
                                                                </>
                                                            ) : (
                                                                ' · nichts Neues'
                                                            )}
                                                        </span>
                                                    )}
                                            </td>
                                            {invoiceSections.length > 0 && (
                                                <td className="py-1 pr-2 align-top">
                                                    {l.kind === 'SERVICE' ? (
                                                        <select
                                                            value={l.sectionId ?? ''}
                                                            disabled={!editable}
                                                            onChange={(e) =>
                                                                assignLineSection(
                                                                    l.id,
                                                                    e.target.value || undefined,
                                                                )
                                                            }
                                                            aria-label={`Abschnitt für ${l.title}`}
                                                            title="Abschnitt dieser Position. Ohne Zuordnung steht sie vor dem ersten Abschnitt."
                                                            className="w-full min-w-[7rem] rounded-md border border-slate-200 px-1 py-0.5 disabled:bg-slate-100"
                                                        >
                                                            <option value="">
                                                                ohne Zuordnung
                                                            </option>
                                                            {invoiceSections.map((sec) => (
                                                                <option key={sec.id} value={sec.id}>
                                                                    {sec.title}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    ) : null}
                                                </td>
                                            )}
                                            <td className="py-1 text-right tabular-nums">
                                                {l.quantity ?? ''} {l.unit || ''}
                                            </td>
                                            <td className="py-1 text-right tabular-nums">
                                                {l.unitPriceCents != null
                                                    ? formatCents(l.unitPriceCents)
                                                    : ''}
                                            </td>
                                            {/* Der Einzelpreis bleibt der
                                                vereinbarte Preis; der Nachlass
                                                steht daneben, damit Menge x EP
                                                abzueglich Nachlass = Netto
                                                nachvollziehbar bleibt — genau
                                                so druckt auch der Beleg. */}
                                            <td className="py-1 text-right tabular-nums">
                                                {editable && l.kind === 'SERVICE' ? (
                                                    <DiscountInput
                                                        percent={l.discountPercent}
                                                        amountCents={l.discountAmountCents}
                                                        ariaLabel={`Nachlass auf ${l.title}`}
                                                        onChange={(next) =>
                                                            patchLine(l.id, {
                                                                discountPercent: next.percent,
                                                                discountAmountCents:
                                                                    next.amountCents,
                                                            })
                                                        }
                                                    />
                                                ) : (
                                                    <span className="text-slate-500">
                                                        {lineDiscountCents(l)
                                                            ? formatCents(-lineDiscountCents(l))
                                                            : ''}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="py-1 text-right tabular-nums">
                                                {CONTRIBUTES_TO_NET.has(l.kind)
                                                    ? formatTaxRateBp(l.taxRateBp)
                                                    : ''}
                                            </td>
                                            {/* Auch Nachlass- und Zuschlagszeilen
                                                zeigen ihren Betrag: sie sind der
                                                sichtbare Beweis, dass die Eingabe
                                                oben angekommen ist. */}
                                            <td className="py-1 text-right font-medium tabular-nums">
                                                {CONTRIBUTES_TO_NET.has(l.kind)
                                                    ? formatCents(l.netCents)
                                                    : ''}
                                            </td>
                                            {/* Herausnehmen statt loeschen: die
                                                Leistung bleibt offen und wird
                                                mit einem spaeteren Beleg
                                                gefordert. Ein blosses Loeschen
                                                haette der naechste Aufbau
                                                rueckgaengig gemacht. */}
                                            {editable && (
                                                <td className="py-1 pl-2 text-right">
                                                    {l.kind === 'SERVICE' && l.serviceId && (
                                                        <button
                                                            type="button"
                                                            onClick={() => excludeLine(l)}
                                                            disabled={
                                                                busy !== null ||
                                                                !mayExcludeLine(l)
                                                            }
                                                            title={
                                                                mayExcludeLine(l)
                                                                    ? 'Nicht abrechnen — die Leistung bleibt offen und lässt sich mit einem späteren Beleg fordern.'
                                                                    : !altBookedLoaded
                                                                      ? 'Der Leistungsstand des Projekts wird geladen — bitte einen Moment.'
                                                                      : 'Diese Leistung wurde bereits gefordert (frühere Rechnung oder nachgetragene Alt-Rechnung) und muss im kumulativen Beleg bleiben. Der Abzug rechnet je Vorrechnung deren Gesamtforderung und nicht je Leistung — fehlt die Position im Gesamtstand, zieht der Beleg mehr ab, als er ausweist, und fordert zu wenig.'
                                                            }
                                                            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-red-600 disabled:cursor-not-allowed disabled:text-slate-200 disabled:hover:bg-transparent disabled:hover:text-slate-200"
                                                        >
                                                            <MinusCircle className="h-3.5 w-3.5" />
                                                            <span className="sr-only">
                                                                Nicht abrechnen
                                                            </span>
                                                        </button>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Was fehlt, muss man sehen. Eine herausgenommene
                            Position verschwindet aus der Tabelle — ohne diese
                            Leiste saehe der Beleg aus, als haette es die
                            Leistung nie gegeben. */}
                        {editable && excludedServiceIds.length > 0 && (
                            <div className="mt-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-xs">
                                <p className="font-medium text-slate-700">
                                    Nicht abgerechnet ({excludedServiceIds.length})
                                </p>
                                <p className="mt-0.5 text-slate-500">
                                    Diese Leistungen bleiben offen und lassen sich mit einem
                                    späteren Beleg fordern. Der Positionsaufbau übergeht sie — auch
                                    nach einer Änderung des{' '}
                                    {isCumulative ? 'Stichtags' : 'Leistungsdatums'}.
                                </p>
                                <ul className="mt-1.5 space-y-1">
                                    {excludedServiceIds.map((id) => (
                                        <li key={id} className="flex items-center gap-2">
                                            <span className="min-w-0 flex-1 truncate text-slate-600">
                                                {excludedTitles[id] || 'Leistung ohne Titel'}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => void includeLine(id)}
                                                disabled={busy !== null}
                                                className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-0.5 font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50"
                                            >
                                                <Undo2 className="h-3 w-3" />
                                                wieder aufnehmen
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="mt-3 space-y-1 border-t border-slate-200 pt-3 text-sm">
                            <Row label="Netto" value={formatCents(draft.totals.netTotalCents)} />
                            {draft.totals.taxGroups.map((g) => (
                                <Row
                                    key={g.taxRateBp}
                                    label={`zzgl. ${formatTaxRateBp(g.taxRateBp)} USt auf ${formatCents(g.taxableNetCents)}`}
                                    value={formatCents(g.taxCents)}
                                />
                            ))}
                            <Row
                                label="Brutto"
                                value={formatCents(draft.totals.grossTotalCents)}
                                bold
                            />
                            {/* Sicherheitseinbehalt nach dem Buchhaltungsmuster
                                (Bauplan §16): VOR den Abschlagszahlungen und auf
                                den LEISTUNGSSTAND gerechnet —
                                  Brutto - kumulierter Einbehalt - erhaltene
                                  Zahlungen = Zahlbetrag.
                                Die Unterzeilen teilen den kumulierten Betrag in
                                "schon einbehalten" und "mit dieser Rechnung";
                                nur der zweite Teil mindert diesen Beleg. Ohne
                                Einbehalt bleibt der Block zeichengleich zu
                                vorher. */}
                            {showsRetention &&
                                (showsRelease ? (
                                    <>
                                        <Row
                                            label="Sicherheitseinbehalt: Freigabe"
                                            value={`+ ${formatCents(retentionReleaseCents)}`}
                                        />
                                        {priorRetentions.map((r) => (
                                            <Row
                                                key={r.sourceId}
                                                label={`${r.invoiceNumber || 'ohne Nummer'} vom ${de(r.invoiceDate)}${r.backfilled ? ' · Alt-Einbehalt' : ''}`}
                                                value={formatCents(r.retentionCents)}
                                                sub
                                            />
                                        ))}
                                        {priorRetentions.length > 1 &&
                                            priorRetentionListed !== retentionReleaseCents && (
                                                <Row
                                                    label="Summe der Vorrechnungen"
                                                    value={formatCents(priorRetentionListed)}
                                                    sub
                                                />
                                            )}
                                    </>
                                ) : (
                                    <>
                                        <Row
                                            // Derselbe Text wie im Andruck — eine
                                            // Quelle fuer Maske und Beleg.
                                            label={`${retentionNote(retentionEffectivePercent)} ${retentionBasisNote(draft.totals.grossTotalCents)}`}
                                            value={`- ${formatCents(cumulativeRetentionCents)}`}
                                        />
                                        {priorRetentionCents !== 0 && (
                                            <Row
                                                label="davon bereits mit früheren Rechnungen einbehalten"
                                                value={formatCents(priorRetentionCents)}
                                                sub
                                            />
                                        )}
                                        {retentionCents !== 0 && (
                                            <Row
                                                label="davon mit dieser Rechnung"
                                                value={formatCents(retentionCents)}
                                                sub
                                            />
                                        )}
                                        {retentionReleaseCents !== 0 && (
                                            <Row
                                                // Satz gesenkt, aber nicht auf 0:
                                                // der Ueberhang der Vorbelege wird
                                                // mit diesem Beleg frei.
                                                label="davon mit dieser Rechnung freigegeben"
                                                value={`+ ${formatCents(retentionReleaseCents)}`}
                                                sub
                                            />
                                        )}
                                    </>
                                ))}
                            {draft.totals.prepaidCents > 0 && (
                                <>
                                    {/* Ansicht des Abschlagsabzugs JE BELEG: der
                                        Konfigurationsstandard (AdminGlobalSettings,
                                        "Abschlagsabzug") belegt nur neue Belege vor —
                                        massgeblich ist das Feld am Beleg selbst. Nach
                                        der Ausstellung gesperrt wie jedes Belegfeld. */}
                                    <div className="flex items-center justify-between pt-1">
                                        <span className="text-xs text-slate-500">Abschläge im Beleg</span>
                                        <div className="inline-flex rounded-lg border border-slate-200 p-0.5 text-xs">
                                            {(
                                                [
                                                    ['INDIVIDUAL', 'Einzeln', 'Jeder Abschlag als eigene Zeile mit Nummer und Datum'],
                                                    ['GROUPED', 'Zusammengefasst', 'Nur die Summenzeile „abzüglich Abschlagszahlungen"'],
                                                ] as const
                                            ).map(([mode, label, title]) => (
                                                <button
                                                    key={mode}
                                                    type="button"
                                                    title={title}
                                                    disabled={!editable}
                                                    onClick={() => patch({ advanceDeductionMode: mode })}
                                                    className={`rounded-md px-2 py-1 transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                                                        (draft.advanceDeductionMode ?? 'INDIVIDUAL') === mode
                                                            ? 'bg-blue-600 font-medium text-white'
                                                            : 'text-slate-600 hover:bg-slate-100'
                                                    }`}
                                                >
                                                    {label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    {/* Ueberschrift des Abzugsblocks — die fette
                                        Zeile ueber den Abzugszeilen. Die
                                        gerechnete Spanne zaehlt die Abzuege
                                        dieses Belegs; rechnet der Auftraggeber
                                        eine andere Nummerierung (Alt-Abschlaege,
                                        Abschlaege aus einem Vorprojekt), muss
                                        der Text von Hand richtiggestellt werden
                                        koennen. Leer heisst: Vorgabe. */}
                                    <div className="pt-1">
                                        <span className="mb-0.5 block text-xs text-slate-500">
                                            Überschrift des Abzugs
                                        </span>
                                        <input
                                            type="text"
                                            value={draft.details?.advanceDeductionHeading ?? ''}
                                            disabled={!editable}
                                            placeholder={advanceHeadingDefault}
                                            title="Steht fett über den Abzugszeilen. Leer lassen für die Vorgabe."
                                            onChange={(e) =>
                                                patch({
                                                    details: {
                                                        ...draft.details,
                                                        advanceDeductionHeading:
                                                            e.target.value || undefined,
                                                    },
                                                })
                                            }
                                            className="w-full rounded-md border border-slate-300 px-2 py-1 text-xs disabled:bg-slate-50"
                                        />
                                    </div>
                                    {/* Nummerierung der Abschlaege. Sie steht an
                                        drei Stellen des Belegs — Ueberschrift,
                                        Einzelzeilen, Summenzeile — und wird
                                        hier EINMAL vorgegeben, damit die drei
                                        nicht auseinanderlaufen. Die Endnummer
                                        darf ueber die aufgefuehrten Abzuege
                                        hinausgehen: die Kette des Auftraggebers
                                        ist laenger als der Ausschnitt, den
                                        dieser Beleg abzieht. */}
                                    <div className="flex flex-wrap items-end gap-2 pt-1">
                                        <div>
                                            <span className="mb-0.5 block text-xs text-slate-500">
                                                Abschläge Nr.
                                            </span>
                                            <div className="flex items-center gap-1">
                                                <input
                                                    type="number"
                                                    min={1}
                                                    step={1}
                                                    value={advanceNumbering?.from ?? ''}
                                                    disabled={!editable || advancePlan.hidden}
                                                    placeholder={String(advancePlan.from)}
                                                    title="Nummer des ersten hier abgezogenen Abschlags"
                                                    onChange={(e) =>
                                                        patchAdvanceNumbering({
                                                            from:
                                                                e.target.value === ''
                                                                    ? undefined
                                                                    : Math.max(
                                                                          1,
                                                                          Math.trunc(
                                                                              Number(e.target.value),
                                                                          ),
                                                                      ),
                                                        })
                                                    }
                                                    className="w-16 rounded-md border border-slate-300 px-2 py-1 text-xs disabled:bg-slate-50"
                                                />
                                                <span className="text-xs text-slate-400">bis</span>
                                                <input
                                                    type="number"
                                                    min={1}
                                                    step={1}
                                                    value={advanceNumbering?.to ?? ''}
                                                    disabled={!editable || advancePlan.hidden}
                                                    placeholder={String(advancePlan.to)}
                                                    title="Nummer, mit der die Spanne endet — darf über die hier aufgeführten Abschläge hinausgehen"
                                                    onChange={(e) =>
                                                        patchAdvanceNumbering({
                                                            to:
                                                                e.target.value === ''
                                                                    ? undefined
                                                                    : Math.max(
                                                                          1,
                                                                          Math.trunc(
                                                                              Number(e.target.value),
                                                                          ),
                                                                      ),
                                                        })
                                                    }
                                                    className="w-16 rounded-md border border-slate-300 px-2 py-1 text-xs disabled:bg-slate-50"
                                                />
                                            </div>
                                        </div>
                                        <label className="flex items-center gap-1.5 pb-1 text-xs text-slate-600">
                                            <input
                                                type="checkbox"
                                                checked={advancePlan.hidden}
                                                disabled={!editable}
                                                onChange={(e) =>
                                                    patchAdvanceNumbering({
                                                        hidden: e.target.checked || undefined,
                                                    })
                                                }
                                                className="h-3.5 w-3.5 rounded border-slate-300"
                                            />
                                            ohne Nummern
                                        </label>
                                    </div>
                                    {listAdvanceDeductions &&
                                        advanceDeductions.map((d, i) => (
                                            <Row
                                                // Ein Abschlag kann mehrere Steuersaetze
                                                // haben, die Nummer allein ist also nicht
                                                // eindeutig.
                                                key={`${d.advanceInvoiceId}_${d.taxRateBp}_${i}`}
                                                label={advanceDeductionLineLabel(
                                                    d,
                                                    advancePlan.ordinals[i],
                                                    formatDate(d.invoiceDate),
                                                    { hidden: advancePlan.hidden },
                                                )}
                                                value={`- ${formatCents(d.grossCents)}`}
                                            />
                                        ))}
                                    <Row
                                        // Zusammengefasst (oder einzelner Abschlag) nennt die
                                        // Zeile die Spanne — sonst ist nirgends sichtbar,
                                        // wie viele Belege im Sammelabzug stecken.
                                        //
                                        // Mit Einbehalt steht hier, was der Kunde auf die
                                        // Vorrechnungen tatsaechlich GEZAHLT hat (prepaid -
                                        // prior), nicht was sie gefordert haben: die Zahl,
                                        // die er in seinen Ueberweisungen wiederfindet
                                        // (Befund der Buchhaltung, Bauplan §16).
                                        label={
                                            showsRetention
                                                ? paidPriorLabel
                                                : (advanceRangeLabel ??
                                                  `abzüglich ${
                                                      advanceDeductionSpanLabel(
                                                          advanceDeductions,
                                                          advanceNumbering,
                                                      ) ?? 'Abschlagszahlungen'
                                                  }`)
                                        }
                                        value={`- ${formatCents(
                                            showsRetention
                                                ? paidPriorCents
                                                : draft.totals.prepaidCents,
                                        )}`}
                                        // Abgesetzt statt fett: der Zahlbetrag darunter
                                        // bleibt die kraeftigste Zeile des Blocks.
                                        divider={advanceRangeLabel != null}
                                    />
                                    {/* Die Einzelzeilen darueber nennen die FORDERUNGEN
                                        der Vorrechnungen; die Summe hier die Zahlungen.
                                        Die Bruecke zwischen beiden steht dabei, sonst
                                        addiert der Leser die Zeilen und findet die Summe
                                        nicht. */}
                                    {showsRetention && priorRetentionCents !== 0 && (
                                        <div className="pl-3 text-[11px] leading-4 text-slate-500 tabular-nums">
                                            Forderungen der Vorrechnungen{' '}
                                            {formatCents(draft.totals.prepaidCents)} abzüglich dort
                                            einbehaltener {formatCents(priorRetentionCents)}
                                        </div>
                                    )}
                                    {!showsRetention && (
                                        <>
                                            <Row
                                                // "(brutto)": der Zahlbetrag ist Brutto minus
                                                // Brutto, waehrend die Zeilen darueber von der
                                                // Gesamtleistung sprechen. Dieselbe Beschriftung
                                                // wie auf dem Beleg.
                                                label="Zahlbetrag (brutto)"
                                                value={formatCents(draft.totals.payableCents)}
                                                bold
                                            />
                                            {/* Wie auf dem Beleg: welcher Teil der Restforderung
                                                Entgelt ist und welcher Umsatzsteuer.
                                                Nachrichtlich — der Steuerausweis bleibt der Block
                                                ueber der Gesamtleistung. */}
                                            {payableSplit.consistent && (
                                                <div className="pl-3 text-[11px] leading-4 text-slate-500">
                                                    {payableSplitLines.map((l) => (
                                                        <div key={l}>{l}</div>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                            {/* Mit Einbehalt: der Zahlbetrag steht unter Einbehalt
                                und Zahlungen. Die Steueraufteilung bezieht sich
                                weiter auf die Forderung VOR Einbehalt (gross -
                                prepaid) — der Einbehalt hat keinen Steueranteil
                                (Bauplan §7.5) — und nennt diese Bezugsgroesse,
                                damit die Teile nicht neben einer Summe stehen, die
                                sie nicht ergeben. */}
                            {showsRetention && (
                                <>
                                    <Row
                                        label="Zahlbetrag (brutto)"
                                        value={formatCents(draft.totals.payableCents)}
                                        bold
                                    />
                                    {payableSplit.consistent && (
                                        <div className="pl-3 text-[11px] leading-4 text-slate-500">
                                            <div className="tabular-nums">
                                                Rechnungsbetrag vor Einbehalt{' '}
                                                {formatCents(retentionBasis)}
                                            </div>
                                            {payableSplitLines.map((l) => (
                                                <div key={l}>{l}</div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                            {/* Schlussrechnung: hier werden die Einbehalte der
                                Vorrechnungen faellig. Der Block steht auch dann,
                                wenn nichts einbehalten wurde — sonst bliebe die
                                wichtigste Frage der Schlussrechnung ("kommt das
                                zurueckgehaltene Geld mit?") unbeantwortet, und
                                niemand wuesste, ob die Anwendung sie ueberhaupt
                                gestellt hat. */}
                            {draft.documentType === 'FINAL_INVOICE' && (
                                <div className="mt-3 border-t border-slate-200 pt-3">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <span className="text-xs font-semibold text-slate-700">
                                            Sicherheitseinbehalte auszahlen
                                        </span>
                                        <label className="flex items-center gap-1.5 text-xs text-slate-600">
                                            <input
                                                type="checkbox"
                                                // Fehlendes Feld heisst "auszahlen"
                                                // (wantsRetentionRelease) — die
                                                // Schlussrechnung macht die
                                                // Einbehalte faellig, das Abschalten
                                                // ist die Ausnahme.
                                                checked={releasesRetention}
                                                disabled={!editable}
                                                onChange={(e) =>
                                                    patch({
                                                        details: {
                                                            ...draft.details,
                                                            releaseRetention: e.target.checked,
                                                        },
                                                        // Zeilen mitschicken: nur
                                                        // dann rechnet updateInvoice
                                                        // die Auszahlung neu.
                                                        lines: draft.lines,
                                                    })
                                                }
                                                className="h-3.5 w-3.5 rounded border-slate-300"
                                            />
                                            mit diesem Beleg auszahlen
                                        </label>
                                    </div>
                                    {/* Die Freigabeliste selbst steht oben im
                                        Summenblock (Zeile "Sicherheitseinbehalt:
                                        Freigabe") — hier nur der Schalter und
                                        seine Folge. */}
                                    {!releasesRetention ? (
                                        <p className="mt-1 text-xs text-amber-700">
                                            Der kumulierte Einbehalt bleibt stehen
                                            (Gewährleistung).
                                        </p>
                                    ) : priorRetentions.length === 0 ? (
                                        <p className="mt-1 text-xs text-slate-500">
                                            Keine offenen Einbehalte.
                                        </p>
                                    ) : (
                                        <p className="mt-1 text-xs text-slate-500 tabular-nums">
                                            Freigegeben werden {formatCents(retentionReleaseCents)}{' '}
                                            aus {priorRetentions.length}{' '}
                                            {priorRetentions.length === 1 ? 'Vorrechnung' : 'Vorrechnungen'}
                                            {priorRetentions.some((r) => r.backfilled) &&
                                                ', darunter Alt-Einbehalte'}
                                            .
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Maske "Alt-Einbehalte erfassen" aus dem Nachholfall-Hinweis.
                        Nach dem ersten Speichern wird der Beleg neu gerechnet:
                        updateInvoice bildet die Summen aus Zeilen und
                        Vorbelegen, und erst mit den Alt-Einbehalten stimmt
                        "davon bereits einbehalten". */}
                    {showRetentionBackfill && draft.primaryProjectId && (
                        <RetentionBackfillModal
                            projectId={draft.primaryProjectId}
                            retentionPercent={retentionPercent ?? projectRetentionPercent}
                            onClose={() => setShowRetentionBackfill(false)}
                            onSaved={() => {
                                if (!editable) return;
                                patch({ details: draft.details, lines: draft.lines });
                            }}
                        />
                    )}
                    {/* Projektbezug: hinspringen und den Nachweis erzeugen.
                        Steht direkt beim Projektcheck, weil beides denselben
                        Anlass hat — der Check meldet etwas, das man im Projekt
                        nachsehen oder mit dem Nachweis belegen will. */}
                    {draft.primaryProjectId && (
                        <div className="rounded-lg border border-slate-200 bg-white p-3">
                            <h2 className="mb-2 text-sm font-semibold text-slate-700">
                                Projekt und Nachweis
                            </h2>
                            <div className="flex flex-wrap gap-2">
                                {onOpenProject && (
                                    <Button
                                        variant="secondary"
                                        onClick={() => void openProject()}
                                        disabled={busy != null}
                                    >
                                        {busy === 'project' ? (
                                            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                                        ) : (
                                            <FolderOpen className="mr-1 h-4 w-4" />
                                        )}
                                        Projekt öffnen
                                    </Button>
                                )}
                                <Button
                                    variant="secondary"
                                    onClick={() => void downloadProof()}
                                    disabled={busy != null}
                                >
                                    {busy === 'proof' ? (
                                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                                    ) : (
                                        <FileText className="mr-1 h-4 w-4" />
                                    )}
                                    Leistungsnachweis
                                </Button>
                            </div>
                            <label className="mt-2 flex items-start gap-2 text-xs text-slate-600">
                                <input
                                    type="checkbox"
                                    className="mt-0.5"
                                    disabled={!editable}
                                    checked={!!draft.details?.attachPerformanceProof}
                                    onChange={(e) =>
                                        patch({
                                            details: {
                                                ...draft.details,
                                                attachPerformanceProof: e.target.checked,
                                            },
                                        })
                                    }
                                />
                                <span>
                                    An die Rechnung anhängen. Der Nachweis erscheint sofort in der
                                    Vorschau, wird beim Ausstellen hinten angefügt und ist damit
                                    Teil des archivierten Dokuments — nachträglich lässt er sich
                                    nicht mehr ergänzen.
                                </span>
                            </label>
                            {/* ZUSCHNITT DER ANLAGE — Zeitraum und Positionen.

                                Beides ändert AUSSCHLIESSLICH die Anlage. Was
                                der Beleg fordert, hängt am Auswahlfenster und
                                an den Zeilen; eine ausgeblendete Position ist
                                nicht abgerechnet, sondern nur nicht belegt.
                                Deshalb steht der Zuschnitt hier beim Nachweis
                                und nicht bei den Zeiträumen des Belegs — dort
                                gelesen wäre er eine Mengenänderung. */}
                            {proofTrimActive && (
                                <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-2 text-xs">
                                    <div className="font-medium text-slate-700">
                                        Zuschnitt der Anlage
                                    </div>
                                    <p className="mt-0.5 text-slate-500">
                                        Ohne Angabe zeigt der Nachweis den Zeitraum des Belegs und
                                        alle Positionen. Der Zuschnitt ändert nur die Anlage — nicht,
                                        was die Rechnung fordert.
                                    </p>
                                    <div className="mt-2 flex flex-wrap items-end gap-3">
                                        <label className="flex flex-col gap-0.5">
                                            <span className="text-slate-500">Nachweis von</span>
                                            <input
                                                type="date"
                                                disabled={!editable}
                                                value={draft.details?.proofRangeFrom ?? ''}
                                                onChange={(e) =>
                                                    patch({
                                                        details: {
                                                            ...draft.details,
                                                            proofRangeFrom:
                                                                e.target.value || undefined,
                                                        },
                                                    })
                                                }
                                                className="rounded-md border border-slate-300 px-2 py-1"
                                            />
                                        </label>
                                        <label className="flex flex-col gap-0.5">
                                            <span className="text-slate-500">bis</span>
                                            <input
                                                type="date"
                                                disabled={!editable}
                                                value={draft.details?.proofRangeTo ?? ''}
                                                onChange={(e) =>
                                                    patch({
                                                        details: {
                                                            ...draft.details,
                                                            proofRangeTo: e.target.value || undefined,
                                                        },
                                                    })
                                                }
                                                className="rounded-md border border-slate-300 px-2 py-1"
                                            />
                                        </label>
                                        {editable && hasCustomProofRange(draft) && (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    patch({
                                                        details: {
                                                            ...draft.details,
                                                            proofRangeFrom: undefined,
                                                            proofRangeTo: undefined,
                                                        },
                                                    })
                                                }
                                                className="rounded-md border border-slate-300 bg-white px-2 py-1 font-medium text-slate-700 hover:bg-slate-50"
                                            >
                                                Zeitraum des Belegs
                                            </button>
                                        )}
                                    </div>
                                    {/* Der wirksame Zeitraum ausgeschrieben: die
                                        leeren Felder oben sagen nicht, WORAUF
                                        der Nachweis dann filtert. */}
                                    <p className="mt-1 text-slate-500">
                                        Der Nachweis listet auf:{' '}
                                        {proofRange.start
                                            ? proofRange.start.split('-').reverse().join('.')
                                            : 'ab Projektbeginn'}{' '}
                                        –{' '}
                                        {proofRange.end
                                            ? proofRange.end.split('-').reverse().join('.')
                                            : 'offen'}
                                    </p>
                                    <div className="mt-2 text-slate-500">
                                        {proofServicesBusy
                                            ? 'Positionen werden geladen…'
                                            : proofServices === null
                                              ? 'Die Positionen konnten nicht geladen werden — der Nachweis zeigt dann alle.'
                                              : proofServices.length === 0
                                                ? 'Das Projekt führt keine Positionen — der Nachweis bliebe leer.'
                                                : `${proofServices.length - proofServices.filter((s) => excludedProofIds.includes(s.id)).length} von ${proofServices.length} Positionen im Nachweis`}
                                    </div>
                                    {proofServices && proofServices.length > 0 && (
                                        <ul className="mt-1 max-h-56 divide-y divide-slate-200 overflow-y-auto rounded border border-slate-200 bg-white">
                                            {proofServices.map((s) => {
                                                const off = excludedProofIds.includes(s.id);
                                                return (
                                                    <li
                                                        key={s.id}
                                                        className="flex items-start gap-2 px-2 py-1"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            className="mt-0.5"
                                                            disabled={!editable}
                                                            checked={!off}
                                                            onChange={() => toggleProofService(s.id)}
                                                        />
                                                        <span
                                                            className={
                                                                off
                                                                    ? 'text-slate-400 line-through'
                                                                    : 'text-slate-700'
                                                            }
                                                        >
                                                            {[s.lvPosition, s.title]
                                                                .filter((part) => !!part)
                                                                .join(' ')}
                                                        </span>
                                                        <span className="ml-auto shrink-0 text-slate-400">
                                                            {s.entryCount === 1
                                                                ? '1 Eintrag'
                                                                : `${s.entryCount} Einträge`}
                                                        </span>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </div>
                            )}
                            {/* Eigener Nachweis statt der generierten Fassung —
                                z. B. weil der Auftraggeber die unterschriebene
                                zurueckgeschickt hat. */}
                            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                                <input
                                    ref={proofFileInputRef}
                                    type="file"
                                    accept="application/pdf,.pdf"
                                    className="hidden"
                                    onChange={handleProofUpload}
                                />
                                {draft.details?.customProofPath ? (
                                    <>
                                        <span className="inline-flex max-w-full items-center gap-1 rounded bg-emerald-50 px-2 py-1 font-medium text-emerald-700">
                                            <FileText className="h-3.5 w-3.5 shrink-0" />
                                            <span className="truncate">
                                                Eigener Nachweis:{' '}
                                                {draft.details.customProofFileName || 'hochgeladene Datei'}
                                            </span>
                                        </span>
                                        {editable && (
                                            <>
                                                <button
                                                    type="button"
                                                    disabled={busy != null}
                                                    onClick={() => proofFileInputRef.current?.click()}
                                                    className="rounded-md border border-slate-300 bg-white px-2 py-1 font-medium text-slate-700 hover:bg-slate-50"
                                                >
                                                    Ersetzen
                                                </button>
                                                <button
                                                    type="button"
                                                    disabled={busy != null}
                                                    onClick={removeCustomProof}
                                                    className="rounded-md border border-slate-300 bg-white px-2 py-1 font-medium text-slate-700 hover:bg-slate-50"
                                                >
                                                    Entfernen
                                                </button>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    editable && (
                                        <button
                                            type="button"
                                            disabled={busy != null}
                                            onClick={() => proofFileInputRef.current?.click()}
                                            className="rounded-md border border-slate-300 bg-white px-2 py-1 font-medium text-slate-700 hover:bg-slate-50"
                                        >
                                            {busy === 'proofUpload'
                                                ? 'Lädt hoch…'
                                                : 'Eigenen Nachweis hochladen (PDF)'}
                                        </button>
                                    )
                                )}
                                <span className="text-slate-500">
                                    Ersetzt die generierte Fassung — z. B. die unterschriebene.
                                </span>
                            </div>
                            {onOpenProject && (
                                <p className="mt-1 text-xs text-slate-400">
                                    Zurück aus dem Projekt führt über die Rechnungsliste — dieser
                                    Beleg wird dort wieder geöffnet.
                                </p>
                            )}
                        </div>
                    )}

                    <ProjectCheckPanel
                        result={check}
                        loading={checkBusy}
                        error={checkError}
                        acknowledgement={acknowledgement}
                        onAcknowledgementChange={setAcknowledgement}
                        onRefresh={() => void runCheck()}
                        readOnly={!editable}
                        onExemptProject={
                            draft.primaryProjectId
                                ? (exempt) => void exemptFromReports(exempt)
                                : undefined
                        }
                        exempting={busy === 'exempt'}
                    />

                    {/* Zahlungen erst ab der Ausstellung: auf einen Entwurf kann
                        nichts eingehen, und ISSUING ist ein Zwischenzustand, in
                        dem ausschliesslich issueInvoice schreibt. */}
                    {!statusOffen && draft.status !== 'ISSUING' && (
                        <PaymentsPanel
                            invoice={draft}
                            invoiceConfig={invoiceConfig}
                            today={today}
                            onChanged={reloadInvoice}
                            buchenGesperrt={darf.zahlungen.hinweis}
                            mahnenGesperrt={darf.mahnen.hinweis}
                        />
                    )}
                    {/* Ohne diesen Hinweis fehlt der Zahlungsbereich einfach,
                        und wer ihn sucht, sucht an der falschen Stelle weiter.
                        Ein leerer Platz erklaert sich nicht von selbst. */}
                    {statusOffen && (
                        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-3 text-xs text-slate-500">
                            <span className="font-medium text-slate-600">Zahlungen</span> lassen
                            sich erfassen, sobald die Rechnung <strong>ausgestellt</strong> ist.
                            Vorher kann nichts eingegangen sein — der Beleg ist beim Kunden noch
                            nicht angekommen.
                        </div>
                    )}
                </div>

                <div className="rounded-lg border border-slate-200 bg-slate-100">
                    {/* Eigener Download-Knopf. Der Knopf IM eingebetteten
                        PDF-Betrachter gehoert dem Browser: die Quelle ist eine
                        data- beziehungsweise Objekt-URL ohne Dateinamen, und
                        Chrome legt sie deshalb als "download.pdf" ab. Ueber das
                        download-Attribut hier bestimmen wir den Namen selbst. */}
                    {documentUri && (
                        <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
                            <span className="text-xs text-slate-500">
                                {hasArchivedPdf
                                    ? 'Archivdokument — der ausgestellte Beleg'
                                    : 'Vorschau — nicht das Archivdokument'}
                            </span>
                            <a
                                href={documentUri}
                                download={`${previewFileName}.pdf`}
                                className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                            >
                                <Download className="h-3.5 w-3.5" />
                                {previewFileName}.pdf
                            </a>
                        </div>
                    )}
                    {documentUri && istNativeApp() ? (
                        // Android-App: kein PDF-Betrachter in der WebView —
                        // Seiten per pdf.js. documentUri ist data: (Vorschau)
                        // oder blob: (Archiv); blobFuerUrl kann beides.
                        <div className="relative h-full min-h-[600px] w-full overflow-hidden rounded-lg">
                            <NativePdfSeiten
                                key={documentUri}
                                pdfUrl={documentUri}
                                maxSeiten={3}
                                // Gesperrt, bis der Teilen-Dialog zu ist: ein zweites
                                // Tippen liefe ins „Teilen läuft schon".
                                onTeilen={() => {
                                    setTeilenLaeuft(true);
                                    void blobFuerUrl(documentUri)
                                        .then((pdf) => dateiAusgeben(pdf, `${previewFileName}.pdf`))
                                        .catch(ausgabeFehlerMelden)
                                        .finally(() => setTeilenLaeuft(false));
                                }}
                                teilenLaeuft={teilenLaeuft}
                            />
                        </div>
                    ) : documentUri ? (
                        <iframe
                            title={hasArchivedPdf ? 'Archiviertes Rechnungsdokument' : 'Rechnungsvorschau'}
                            src={documentUri}
                            className="h-full min-h-[600px] w-full rounded-lg"
                        />
                    ) : archiveError ? (
                        <div className="flex h-full min-h-[400px] flex-col items-center justify-center gap-1 px-4 text-center text-sm text-rose-700">
                            <AlertTriangle className="h-5 w-5" />
                            <span>{archiveError}</span>
                            <span className="text-xs text-slate-500">
                                Der Beleg selbst ist unverändert — nur die Anzeige des
                                archivierten PDF ist gerade nicht möglich.
                            </span>
                        </div>
                    ) : (
                        <div className="flex h-full min-h-[400px] items-center justify-center text-sm text-slate-500">
                            {hasArchivedPdf
                                ? 'Archivdokument wird geladen …'
                                : 'Vorschau wird erstellt …'}
                        </div>
                    )}
                </div>
            </div>

            {correctionOpen && (
                <CorrectionDialog
                    invoice={draft}
                    busy={busy === 'correct' || busy === 'credit'}
                    fehler={error}
                    onClose={() => setCorrectionOpen(false)}
                    onSubmitCorrection={handleCorrection}
                    onSubmitCredit={handleCredit}
                />
            )}

            {/* Rueckfrage vor dem Ausstellen: der Klick vergibt endgueltig die
                Rechnungsnummer, schreibt in die Projekte zurueck und archiviert das
                PDF. Zurueck geht das nur ueber einen eigenen Stornobeleg. */}
            {issueOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 animate-fade-in p-4">
                    <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-lg">
                        <h2 className="mb-2 font-semibold text-slate-800">Beleg ausstellen?</h2>
                        <dl className="mb-3 space-y-1 text-sm">
                            <div className="flex justify-between gap-3">
                                <dt className="text-slate-500">Belegart</dt>
                                <dd className="font-medium text-slate-800">
                                    {DOCUMENT_TYPE_LABEL[draft.documentType]}
                                </dd>
                            </div>
                            <div className="flex justify-between gap-3">
                                <dt className="text-slate-500">Kunde</dt>
                                <dd className="truncate font-medium text-slate-800">
                                    {/* Derselbe Name wie im Kopf: die Rueckfrage
                                        muss den Kunden nennen, der gleich
                                        eingefroren wird — nicht den Stand aus
                                        dem Join. */}
                                    {documentCustomerName(
                                        customerWithOverride,
                                        draft.customerSnapshot,
                                        customerFrozen,
                                        draft.customerName,
                                    )}
                                </dd>
                            </div>
                            {/* Bei einem Abzug ist der Bruttobetrag NICHT das,
                                was der Beleg fordert. Ihn allein zu zeigen las
                                sich wie eine Rechnung ohne Abzug — auf einer
                                kumulativen Rechnung ist das der ganze
                                Leistungsstand statt des Restbetrags. Deshalb
                                der Zahlbetrag, und das Brutto daneben, sobald
                                beide auseinandergehen. */}
                            {draft.totals.payableCents !== draft.totals.grossTotalCents && (
                                <>
                                    <div className="flex justify-between gap-3">
                                        <dt className="text-slate-500">Brutto</dt>
                                        <dd className="tabular-nums text-slate-600">
                                            {formatCents(draft.totals.grossTotalCents)}
                                        </dd>
                                    </div>
                                    {/* Einbehalt kumuliert, davon neu, erhaltene
                                        Zahlungen — dieselben Zeilen wie im
                                        Summenblock (Bauplan §16). Frueher stand
                                        hier die Differenz Zahlbetrag minus Brutto
                                        in EINER Zeile "abzüglich bereits
                                        abgerechnet" — mit Einbehalt waere darin
                                        etwas versteckt, das gerade NICHT
                                        abgerechnet ist, sondern zurueckgehalten
                                        wird. Ohne Einbehalt bleibt die Zeile. */}
                                    {showsRetention && showsRelease && (
                                        <div className="flex justify-between gap-3">
                                            <dt className="text-slate-500">
                                                Sicherheitseinbehalt: Freigabe
                                            </dt>
                                            <dd className="tabular-nums text-slate-600">
                                                {formatCents(retentionReleaseCents)}
                                            </dd>
                                        </div>
                                    )}
                                    {showsRetention && !showsRelease && (
                                        <>
                                            <div className="flex justify-between gap-3">
                                                <dt className="text-slate-500">
                                                    {retentionNote(retentionEffectivePercent)},
                                                    kumuliert
                                                </dt>
                                                <dd className="tabular-nums text-slate-600">
                                                    {formatCents(-cumulativeRetentionCents)}
                                                </dd>
                                            </div>
                                            {priorRetentionCents !== 0 && (
                                                <div className="flex justify-between gap-3 pl-3 text-xs">
                                                    <dt className="text-slate-500">
                                                        davon bereits einbehalten
                                                    </dt>
                                                    <dd className="tabular-nums text-slate-500">
                                                        {formatCents(priorRetentionCents)}
                                                    </dd>
                                                </div>
                                            )}
                                            {retentionCents !== 0 && (
                                                <div className="flex justify-between gap-3 pl-3 text-xs">
                                                    <dt className="text-slate-500">
                                                        davon neu mit diesem Beleg
                                                    </dt>
                                                    <dd className="tabular-nums text-slate-500">
                                                        {formatCents(retentionCents)}
                                                    </dd>
                                                </div>
                                            )}
                                            {retentionReleaseCents !== 0 && (
                                                <div className="flex justify-between gap-3 pl-3 text-xs">
                                                    <dt className="text-slate-500">
                                                        davon mit diesem Beleg freigegeben
                                                    </dt>
                                                    <dd className="tabular-nums text-slate-500">
                                                        {formatCents(retentionReleaseCents)}
                                                    </dd>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {draft.totals.prepaidCents > 0 && (
                                        <div className="flex justify-between gap-3">
                                            <dt className="text-slate-500">
                                                {showsRetention
                                                    ? 'abzüglich erhaltener Abschlagszahlungen'
                                                    : 'abzüglich bereits abgerechnet'}
                                            </dt>
                                            <dd className="tabular-nums text-slate-600">
                                                {formatCents(
                                                    -(showsRetention
                                                        ? paidPriorCents
                                                        : draft.totals.prepaidCents),
                                                )}
                                            </dd>
                                        </div>
                                    )}
                                </>
                            )}
                            <div className="flex justify-between gap-3">
                                <dt className="text-slate-500">Zahlbetrag</dt>
                                <dd className="font-semibold tabular-nums text-slate-900">
                                    {formatCents(draft.totals.payableCents)}
                                </dd>
                            </div>
                        </dl>
                        {offenNachStichtag.length > 0 && (
                            <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                                <p className="font-semibold">
                                    Diese Rechnung enthält nicht alle erbrachten Leistungen
                                </p>
                                <ul className="mt-1.5 list-disc space-y-1 pl-4">
                                    {offenNachStichtag.map((m, i) => (
                                        <li key={`${m.code}-${i}`}>{m.message}</li>
                                    ))}
                                </ul>
                                <p className="mt-1.5 text-xs">
                                    Ausstellen geht trotzdem. Die fehlende Menge lässt sich danach
                                    nur noch mit einem eigenen Beleg abrechnen.
                                </p>
                            </div>
                        )}
                        <p className="mb-4 text-sm text-slate-600">
                            Mit dem Ausstellen wird die Rechnungsnummer endgültig vergeben, die
                            abgerechneten Mengen werden in die Projekte zurückgeschrieben und das
                            PDF wird archiviert. Rückgängig machen lässt sich das nur mit einer
                            eigenen Stornorechnung.
                        </p>
                        {/* Ein Fehler beim Ausstellen gehoert IN den Dialog: der
                            Balken der Maske liegt hinter der Abdunklung. Die Sperre
                            am Rechnungstor (S5a Stufe 3b) kommt in ihrer Form —
                            Satz, Positionen, Rat —, alles andere als Klartext. */}
                        {torSperre ? (
                            <div
                                role="alert"
                                className="mb-4 max-h-64 overflow-y-auto rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900"
                            >
                                <p className="font-semibold">{torSperre.titel}</p>
                                {torSperre.punkte.length > 0 && (
                                    <ul className="mt-1.5 list-disc space-y-1 pl-4">
                                        {torSperre.punkte.map((p, i) => (
                                            <li key={i}>{p}</li>
                                        ))}
                                    </ul>
                                )}
                                {torSperre.hinweis && <p className="mt-1.5 text-xs">{torSperre.hinweis}</p>}
                            </div>
                        ) : (
                            error && (
                                <div
                                    role="alert"
                                    className="mb-4 max-h-64 overflow-y-auto whitespace-pre-line rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900"
                                >
                                    {error}
                                </div>
                            )
                        )}
                        <div className="flex justify-end gap-2">
                            <Button
                                variant="secondary"
                                onClick={() => setIssueOpen(false)}
                                disabled={busy != null}
                            >
                                Abbrechen
                            </Button>
                            <Button onClick={handleIssue} disabled={busy != null || blocked}>
                                {busy === 'issue' ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <CheckCircle2 className="w-4 h-4" />
                                )}
                                Jetzt ausstellen
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {cancelOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 animate-fade-in p-4">
                    <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-lg">
                        <h2 className="mb-2 font-semibold text-slate-800">Rechnung stornieren</h2>
                        <p className="mb-3 text-sm text-slate-600">
                            Es entsteht eine eigene Stornorechnung mit eigener Nummer. Die
                            ursprüngliche Rechnung <span className="font-mono">{draft.invoiceNumber}</span> bleibt als Beleg erhalten
                            und wird nicht gelöscht. Die abgerechneten Mengen werden im Projekt
                            wieder freigegeben.
                        </p>
                        <label className="block text-sm font-medium text-slate-700">
                            Stornogrund (Pflichtangabe)
                        </label>
                        <textarea
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            rows={3}
                            className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm"
                            placeholder="z. B. Rechnung versehentlich doppelt erstellt"
                        />
                        {/* Wie im Ausstellen-Dialog: das Storno laeuft durch
                            dasselbe Rechnungstor, sein Fehler gehoert hierher und
                            nicht hinter die Abdunklung. Die Sperre am Tor bringt
                            ihre Positionen als eigene Zeilen in der Meldung mit. */}
                        {error && (
                            <div
                                role="alert"
                                className="mt-3 max-h-64 overflow-y-auto whitespace-pre-line rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900"
                            >
                                {error}
                            </div>
                        )}
                        <div className="mt-4 flex justify-end gap-2">
                            <Button variant="secondary" onClick={() => setCancelOpen(false)}>
                                Abbrechen
                            </Button>
                            <Button
                                variant="danger"
                                onClick={handleCancel}
                                disabled={busy != null || !cancelReason.trim()}
                            >
                                {busy === 'cancel' && <Loader2 className="w-4 h-4 animate-spin" />}
                                Storno erstellen
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Kundendaten ohne Umweg ueber die Kundenverwaltung — mit der
                Wahl, wie weit die Aenderung reicht.

                GLOBAL geht das Ergebnis an die Liste (customers), NICHT an den
                Beleg: der Entwurf liest den Kunden ohnehin live, und ihn
                anzufassen setzte den gerade getippten Stand zurueck.

                BELEGBEZOGEN landet nur der Diff in details.customerOverride —
                ueber patch(), also denselben Autosave wie jedes andere Feld der
                Maske. */}
            {customerEditOpen && draft.customerId && (
                <CustomerQuickEditModal
                    customerId={draft.customerId}
                    fallback={customer}
                    kind="invoice"
                    state={customerEditState}
                    override={customerOverride}
                    projectSticky={overrideProjectSticky}
                    stammAenderbar={darf.kundenstamm.erlaubt}
                    onSaved={(updated) => onCustomerChange?.(updated)}
                    // Im offenen Entwurf ohne Recht „Rechnungen bearbeiten"
                    // keine belegbezogene Wahl: patch schriebe nichts, und der
                    // Dialog meldete eine Abweichung als gespeichert, die nie
                    // ankam. Festgeschriebene Belege wie bisher (der Dialog
                    // erklaert dort selbst, warum die Wahl entfaellt).
                    onSavedForDocument={
                        !statusOffen || editable
                            ? (next) =>
                                  patch({
                                      details: {
                                          ...draft.details,
                                          // Ausdruecklich undefined statt Auslassen: nur so
                                          // verschwindet eine zurueckgenommene Abweichung
                                          // wirklich aus dem Beleg.
                                          customerOverride: next,
                                      },
                                  })
                            : undefined
                    }
                    onClose={() => setCustomerEditOpen(false)}
                />
            )}
        </div>
    );
};

/** Was ein Nachlassfeld an den Beleg zurueckmeldet — beide Formen, immer beide. */
export interface DiscountChange {
    percent: number;
    amountCents: number;
}

/**
 * Nachlass erfassen — wahlweise als Prozentsatz oder als fester Eurobetrag.
 *
 * Ein Feld und ein Umschalter statt zweier Felder: Prozent und Betrag schliessen
 * einander aus (siehe InvoiceLine.discountAmountCents), und zwei nebeneinander
 * gefuellte Felder waeren genau die Eingabe, die das Modell nicht abbilden kann.
 * Deshalb meldet die Komponente immer BEIDE Werte zurueck, einen davon als 0 —
 * die ausdrueckliche Null loescht den jeweils anderen, waehrend ein fehlendes
 * Feld den gespeicherten Stand stehen liesse (updateInvoice).
 *
 * Der Umschalter deutet die eingetippte ZAHL in der neuen Einheit, er rechnet
 * sie nicht um: aus "10 %" wird "10,00 €". Umrechnen hiesse, einen anderen
 * Betrag hinzuschreiben als den vereinbarten; die Zahl stehen zu lassen zeigt
 * dagegen sofort in der Netto-Spalte, was gilt.
 *
 * Eigener Textzustand waehrend der Eingabe: der gespeicherte Wert ist eine Zahl,
 * und wer ihn bei jedem Tastendruck zurueckformatiert, verschluckt das
 * angefangene "2," und die fuehrende "0," (type="number" tut genau das).
 */
const DiscountInput: React.FC<{
    percent?: number;
    amountCents?: number;
    disabled?: boolean;
    /** Fuer Screenreader — die Tabellenspalte beschriftet das Feld nicht. */
    ariaLabel: string;
    className?: string;
    onChange: (next: DiscountChange) => void;
}> = ({ percent, amountCents, disabled, ariaLabel, className, onChange }) => {
    const mode: 'amount' | 'percent' = amountCents ? 'amount' : 'percent';
    const stored =
        mode === 'amount'
            ? formatCentsPlain(amountCents ?? 0)
            : percent
              ? percent.toLocaleString('de-DE', { maximumFractionDigits: 2 })
              : '';
    const [typed, setTyped] = useState<string | null>(null);

    const emit = (raw: string, nextMode: 'amount' | 'percent') => {
        const value = parseDecimalInput(raw);
        onChange(
            nextMode === 'amount'
                ? { percent: 0, amountCents: value == null ? 0 : centsFromEuroInput(raw) ?? 0 }
                : { percent: value ?? 0, amountCents: 0 },
        );
    };

    return (
        <span className={`inline-flex items-center gap-1 ${className ?? ''}`}>
            <input
                value={typed ?? stored}
                disabled={disabled}
                inputMode="decimal"
                placeholder="0"
                aria-label={ariaLabel}
                onChange={(e) => {
                    setTyped(e.target.value);
                    emit(e.target.value, mode);
                }}
                onBlur={() => setTyped(null)}
                className="w-16 rounded-md border border-slate-200 px-1 py-0.5 text-right tabular-nums focus:border-blue-500 focus:outline-none disabled:bg-slate-50"
            />
            <select
                value={mode}
                disabled={disabled}
                aria-label={`${ariaLabel} — Einheit`}
                title="Prozent oder fester Betrag. Der Umschalter deutet die eingetragene Zahl in der neuen Einheit."
                onChange={(e) => {
                    const next = e.target.value as 'amount' | 'percent';
                    emit(typed ?? stored, next);
                }}
                className="rounded-md border border-slate-200 px-0.5 py-0.5 focus:border-blue-500 focus:outline-none disabled:bg-slate-50"
            >
                <option value="percent">%</option>
                <option value="amount">€</option>
            </select>
        </span>
    );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <label className="block">
        <span className="mb-0.5 block text-xs font-medium text-slate-600">{label}</span>
        {children}
    </label>
);

/**
 * Eine Zeile des Summenblocks.
 *
 * `divider` setzt eine Zeile optisch ab, ohne sie fett zu machen — gebraucht
 * fuer die Summe der Einzelabzuege, die zur Aufstellung darueber gehoert, aber
 * dem Zahlbetrag darunter nicht die Aufmerksamkeit nehmen darf.
 */
const Row: React.FC<{
    label: string;
    value: string;
    bold?: boolean;
    divider?: boolean;
    /** Unterzeile ("davon …"): eingerueckt und kleiner, gehoert zur Zeile darueber. */
    sub?: boolean;
}> = ({ label, value, bold, divider, sub }) => (
    <div
        className={`flex justify-between gap-3 ${bold ? 'font-semibold text-slate-900' : sub ? 'pl-3 text-xs text-slate-500' : 'text-slate-600'} ${divider ? 'mt-1 border-t border-slate-200 pt-1' : ''}`}
    >
        <span>{label}</span>
        <span className="tabular-nums">{value}</span>
    </div>
);
